export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[];

export type Database = {
  public: {
    Tables: {
      ad_campaigns: {
        Row: {
          comedian_id: string;
          created_at: string;
          end_date: string;
          id: string;
          location_geo: unknown;
          location_name: string;
          start_date: string;
          type: Database["public"]["Enums"]["ad_campaign_type"];
        };
        Insert: {
          comedian_id: string;
          created_at?: string;
          end_date: string;
          id?: string;
          location_geo: unknown;
          location_name: string;
          start_date: string;
          type?: Database["public"]["Enums"]["ad_campaign_type"];
        };
        Update: {
          comedian_id?: string;
          created_at?: string;
          end_date?: string;
          id?: string;
          location_geo?: unknown;
          location_name?: string;
          start_date?: string;
          type?: Database["public"]["Enums"]["ad_campaign_type"];
        };
        Relationships: [
          {
            foreignKeyName: "ad_campaigns_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      ad_campaigns_feed_shows: {
        Row: {
          campaign_id: string;
          created_at: string;
          id: string;
          show_id: string;
        };
        Insert: {
          campaign_id: string;
          created_at?: string;
          id?: string;
          show_id: string;
        };
        Update: {
          campaign_id?: string;
          created_at?: string;
          id?: string;
          show_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "ad_campaigns_feed_shows_campaign_id_fkey";
            columns: ["campaign_id"];
            isOneToOne: false;
            referencedRelation: "ad_campaigns";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ad_campaigns_feed_shows_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ad_campaigns_feed_shows_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
        ];
      };
      ads_weekly_newsletter: {
        Row: {
          body: string | null;
          button_text: string;
          created_at: string;
          email_date: string;
          header: string | null;
          image_id: string;
          link: string;
        };
        Insert: {
          body?: string | null;
          button_text: string;
          created_at?: string;
          email_date: string;
          header?: string | null;
          image_id: string;
          link: string;
        };
        Update: {
          body?: string | null;
          button_text?: string;
          created_at?: string;
          email_date?: string;
          header?: string | null;
          image_id?: string;
          link?: string;
        };
        Relationships: [];
      };
      agent_tasks: {
        Row: {
          agent_params: Json | null;
          agent_type: string;
          created_at: string;
          error: string | null;
          id: string;
          result: Json | null;
          run_params: Json | null;
          state: Json | null;
          status: string;
          updated_at: string;
        };
        Insert: {
          agent_params?: Json | null;
          agent_type: string;
          created_at?: string;
          error?: string | null;
          id?: string;
          result?: Json | null;
          run_params?: Json | null;
          state?: Json | null;
          status?: string;
          updated_at?: string;
        };
        Update: {
          agent_params?: Json | null;
          agent_type?: string;
          created_at?: string;
          error?: string | null;
          id?: string;
          result?: Json | null;
          run_params?: Json | null;
          state?: Json | null;
          status?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      airbyte_heartbeat: {
        Row: {
          id: number;
          text: string | null;
          timestamp: string;
        };
        Insert: {
          id?: number;
          text?: string | null;
          timestamp?: string;
        };
        Update: {
          id?: number;
          text?: string | null;
          timestamp?: string;
        };
        Relationships: [];
      };
      aliases: {
        Row: {
          alias: string;
          resource_id: string;
          slug: string;
          type: Database["public"]["Enums"]["page_types"];
        };
        Insert: {
          alias: string;
          resource_id: string;
          slug: string;
          type: Database["public"]["Enums"]["page_types"];
        };
        Update: {
          alias?: string;
          resource_id?: string;
          slug?: string;
          type?: Database["public"]["Enums"]["page_types"];
        };
        Relationships: [];
      };
      artist_boost: {
        Row: {
          artist_id: string | null;
          boost_expiration_date: string | null;
          created_at: string;
          id: number;
          is_boosted: boolean | null;
        };
        Insert: {
          artist_id?: string | null;
          boost_expiration_date?: string | null;
          created_at?: string;
          id?: number;
          is_boosted?: boolean | null;
        };
        Update: {
          artist_id?: string | null;
          boost_expiration_date?: string | null;
          created_at?: string;
          id?: number;
          is_boosted?: boolean | null;
        };
        Relationships: [
          {
            foreignKeyName: "artist_boost_artist_id_fkey";
            columns: ["artist_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      artist_credits: {
        Row: {
          artist_id: string;
          automations_balance: number;
          balance: number;
          created_at: string;
          free_balance: number;
          purchased_balance: number;
          wallet_id: string;
        };
        Insert: {
          artist_id: string;
          automations_balance?: number;
          balance?: number;
          created_at?: string;
          free_balance?: number;
          purchased_balance?: number;
          wallet_id?: string;
        };
        Update: {
          artist_id?: string;
          automations_balance?: number;
          balance?: number;
          created_at?: string;
          free_balance?: number;
          purchased_balance?: number;
          wallet_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "artist_credits_artist_id_fkey";
            columns: ["artist_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      artist_hidden_words: {
        Row: {
          comedian_id: string;
          created_at: string | null;
          created_by: string | null;
          filter_type: string | null;
          id: string;
          shared_by: string | null;
          source: string | null;
          word: string;
        };
        Insert: {
          comedian_id: string;
          created_at?: string | null;
          created_by?: string | null;
          filter_type?: string | null;
          id?: string;
          shared_by?: string | null;
          source?: string | null;
          word: string;
        };
        Update: {
          comedian_id?: string;
          created_at?: string | null;
          created_by?: string | null;
          filter_type?: string | null;
          id?: string;
          shared_by?: string | null;
          source?: string | null;
          word?: string;
        };
        Relationships: [
          {
            foreignKeyName: "artist_hidden_words_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "artist_hidden_words_shared_by_fkey";
            columns: ["shared_by"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      artist_invitations: {
        Row: {
          created_at: string;
          created_by: string;
          expires_at: string;
          id: string;
          invited_user_id: string;
          status: string | null;
        };
        Insert: {
          created_at?: string;
          created_by: string;
          expires_at: string;
          id?: string;
          invited_user_id: string;
          status?: string | null;
        };
        Update: {
          created_at?: string;
          created_by?: string;
          expires_at?: string;
          id?: string;
          invited_user_id?: string;
          status?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "artist_invitations_created_by_fkey";
            columns: ["created_by"];
            isOneToOne: false;
            referencedRelation: "super_admins";
            referencedColumns: ["user_id"];
          },
        ];
      };
      artist_metrics: {
        Row: {
          comedian_id: string;
          created_at: string;
          id: number;
          subscriber_count: number | null;
          total_play_count: number | null;
          updated_at: string | null;
        };
        Insert: {
          comedian_id: string;
          created_at?: string;
          id?: number;
          subscriber_count?: number | null;
          total_play_count?: number | null;
          updated_at?: string | null;
        };
        Update: {
          comedian_id?: string;
          created_at?: string;
          id?: number;
          subscriber_count?: number | null;
          total_play_count?: number | null;
          updated_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "artist_metrics_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      artist_moderation_settings: {
        Row: {
          allow_appeals: boolean | null;
          attack_on_author_threshold: number | null;
          attack_on_commenter_threshold: number | null;
          auto_hide_new_accounts: boolean | null;
          auto_hide_no_photo: boolean | null;
          auto_hide_on_threshold: boolean | null;
          blocked_words: string[] | null;
          comedian_id: string;
          created_at: string | null;
          filter_level: string | null;
          flagged_words: string[] | null;
          flirtation_threshold: number | null;
          id: string;
          identity_attack_threshold: number | null;
          inflammatory_threshold: number | null;
          insult_threshold: number | null;
          likely_to_reject_threshold: number | null;
          manual_approval: boolean | null;
          obscene_threshold: number | null;
          profanity_threshold: number | null;
          send_notification_on_hide: boolean | null;
          severe_toxicity_threshold: number | null;
          sexually_explicit_threshold: number | null;
          spam_threshold: number | null;
          threat_threshold: number | null;
          toxicity_threshold: number | null;
          unsubstantial_threshold: number | null;
          updated_at: string | null;
          updated_by: string | null;
        };
        Insert: {
          allow_appeals?: boolean | null;
          attack_on_author_threshold?: number | null;
          attack_on_commenter_threshold?: number | null;
          auto_hide_new_accounts?: boolean | null;
          auto_hide_no_photo?: boolean | null;
          auto_hide_on_threshold?: boolean | null;
          blocked_words?: string[] | null;
          comedian_id: string;
          created_at?: string | null;
          filter_level?: string | null;
          flagged_words?: string[] | null;
          flirtation_threshold?: number | null;
          id?: string;
          identity_attack_threshold?: number | null;
          inflammatory_threshold?: number | null;
          insult_threshold?: number | null;
          likely_to_reject_threshold?: number | null;
          manual_approval?: boolean | null;
          obscene_threshold?: number | null;
          profanity_threshold?: number | null;
          send_notification_on_hide?: boolean | null;
          severe_toxicity_threshold?: number | null;
          sexually_explicit_threshold?: number | null;
          spam_threshold?: number | null;
          threat_threshold?: number | null;
          toxicity_threshold?: number | null;
          unsubstantial_threshold?: number | null;
          updated_at?: string | null;
          updated_by?: string | null;
        };
        Update: {
          allow_appeals?: boolean | null;
          attack_on_author_threshold?: number | null;
          attack_on_commenter_threshold?: number | null;
          auto_hide_new_accounts?: boolean | null;
          auto_hide_no_photo?: boolean | null;
          auto_hide_on_threshold?: boolean | null;
          blocked_words?: string[] | null;
          comedian_id?: string;
          created_at?: string | null;
          filter_level?: string | null;
          flagged_words?: string[] | null;
          flirtation_threshold?: number | null;
          id?: string;
          identity_attack_threshold?: number | null;
          inflammatory_threshold?: number | null;
          insult_threshold?: number | null;
          likely_to_reject_threshold?: number | null;
          manual_approval?: boolean | null;
          obscene_threshold?: number | null;
          profanity_threshold?: number | null;
          send_notification_on_hide?: boolean | null;
          severe_toxicity_threshold?: number | null;
          sexually_explicit_threshold?: number | null;
          spam_threshold?: number | null;
          threat_threshold?: number | null;
          toxicity_threshold?: number | null;
          unsubstantial_threshold?: number | null;
          updated_at?: string | null;
          updated_by?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "artist_moderation_settings_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      artist_preferences: {
        Row: {
          artist_id: string | null;
          created_at: string;
          expand_shows: boolean | null;
          id: number;
        };
        Insert: {
          artist_id?: string | null;
          created_at?: string;
          expand_shows?: boolean | null;
          id?: number;
        };
        Update: {
          artist_id?: string | null;
          created_at?: string;
          expand_shows?: boolean | null;
          id?: number;
        };
        Relationships: [
          {
            foreignKeyName: "artist_preferences_artist_id_fkey";
            columns: ["artist_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      artist_ranking_score: {
        Row: {
          boost_expiration_date: string | null;
          comedian_id: string;
          id: number;
          is_boosted: boolean;
          last_updated_at: string | null;
          score: number;
        };
        Insert: {
          boost_expiration_date?: string | null;
          comedian_id: string;
          id?: number;
          is_boosted?: boolean;
          last_updated_at?: string | null;
          score: number;
        };
        Update: {
          boost_expiration_date?: string | null;
          comedian_id?: string;
          id?: number;
          is_boosted?: boolean;
          last_updated_at?: string | null;
          score?: number;
        };
        Relationships: [
          {
            foreignKeyName: "artist_ranking_score_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      artist_ranking_score_v2: {
        Row: {
          boost_expiration_date: string | null;
          comedian_id: string;
          ds: string;
          is_boosted: boolean | null;
          last_updated_at: string | null;
          score: number;
        };
        Insert: {
          boost_expiration_date?: string | null;
          comedian_id: string;
          ds: string;
          is_boosted?: boolean | null;
          last_updated_at?: string | null;
          score: number;
        };
        Update: {
          boost_expiration_date?: string | null;
          comedian_id?: string;
          ds?: string;
          is_boosted?: boolean | null;
          last_updated_at?: string | null;
          score?: number;
        };
        Relationships: [
          {
            foreignKeyName: "artist_ranking_score_v2_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      artist_value_feedback: {
        Row: {
          comment: string | null;
          context: Json | null;
          created_at: string;
          id: string;
          rating: number;
          surface: string;
          user_id: string | null;
        };
        Insert: {
          comment?: string | null;
          context?: Json | null;
          created_at?: string;
          id?: string;
          rating: number;
          surface: string;
          user_id?: string | null;
        };
        Update: {
          comment?: string | null;
          context?: Json | null;
          created_at?: string;
          id?: string;
          rating?: number;
          surface?: string;
          user_id?: string | null;
        };
        Relationships: [];
      };
      auth_attempts: {
        Row: {
          attempted_at: string;
          country_code: string | null;
          created_at: string;
          email: string | null;
          id: number;
          ip_address: string;
          phone_number: string | null;
          success: boolean;
        };
        Insert: {
          attempted_at: string;
          country_code?: string | null;
          created_at?: string;
          email?: string | null;
          id?: number;
          ip_address: string;
          phone_number?: string | null;
          success?: boolean;
        };
        Update: {
          attempted_at?: string;
          country_code?: string | null;
          created_at?: string;
          email?: string | null;
          id?: number;
          ip_address?: string;
          phone_number?: string | null;
          success?: boolean;
        };
        Relationships: [];
      };
      auth_blocklist: {
        Row: {
          created_at: string;
          expires_at: string;
          id: number;
          identifier: string;
          reason: string;
          type: string;
        };
        Insert: {
          created_at?: string;
          expires_at: string;
          id?: number;
          identifier: string;
          reason: string;
          type: string;
        };
        Update: {
          created_at?: string;
          expires_at?: string;
          id?: number;
          identifier?: string;
          reason?: string;
          type?: string;
        };
        Relationships: [];
      };
      ayrshare_social_posts: {
        Row: {
          ayrshare_post_id: string | null;
          comedian_id: string | null;
          content: string | null;
          created_at: string | null;
          id: string;
          media_urls: string[] | null;
          platforms: string[] | null;
          post_response: Json | null;
          scheduled_for: string | null;
          short_code: string | null;
          status: string | null;
          user_posted_id: string | null;
        };
        Insert: {
          ayrshare_post_id?: string | null;
          comedian_id?: string | null;
          content?: string | null;
          created_at?: string | null;
          id?: string;
          media_urls?: string[] | null;
          platforms?: string[] | null;
          post_response?: Json | null;
          scheduled_for?: string | null;
          short_code?: string | null;
          status?: string | null;
          user_posted_id?: string | null;
        };
        Update: {
          ayrshare_post_id?: string | null;
          comedian_id?: string | null;
          content?: string | null;
          created_at?: string | null;
          id?: string;
          media_urls?: string[] | null;
          platforms?: string[] | null;
          post_response?: Json | null;
          scheduled_for?: string | null;
          short_code?: string | null;
          status?: string | null;
          user_posted_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "ayrshare_social_posts_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      ayrshare_user_profiles: {
        Row: {
          ayrshare_profile_key: string | null;
          ayrshare_profile_title: string | null;
          comedian_id: string;
          created_at: string | null;
          email: string | null;
          full_name: string | null;
          is_social_linked: boolean | null;
          social_accounts: Json | null;
          updated_at: string | null;
        };
        Insert: {
          ayrshare_profile_key?: string | null;
          ayrshare_profile_title?: string | null;
          comedian_id: string;
          created_at?: string | null;
          email?: string | null;
          full_name?: string | null;
          is_social_linked?: boolean | null;
          social_accounts?: Json | null;
          updated_at?: string | null;
        };
        Update: {
          ayrshare_profile_key?: string | null;
          ayrshare_profile_title?: string | null;
          comedian_id?: string;
          created_at?: string | null;
          email?: string | null;
          full_name?: string | null;
          is_social_linked?: boolean | null;
          social_accounts?: Json | null;
          updated_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "ayrshare_user_profiles_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      badges_comedian_shows: {
        Row: {
          badge_type: string;
          id: number;
          last_updated: string | null;
          num_users: number | null;
          show_id: string;
        };
        Insert: {
          badge_type: string;
          id?: number;
          last_updated?: string | null;
          num_users?: number | null;
          show_id?: string;
        };
        Update: {
          badge_type?: string;
          id?: number;
          last_updated?: string | null;
          num_users?: number | null;
          show_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "badges_comedian_shows_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "badges_comedian_shows_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
        ];
      };
      booking_analytics: {
        Row: {
          created_at: string | null;
          entity_id: string;
          entity_type: string;
          event_data: Json | null;
          event_type: string;
          id: string;
          ip_address: unknown;
          session_id: string | null;
          user_agent: string | null;
          user_id: string | null;
        };
        Insert: {
          created_at?: string | null;
          entity_id: string;
          entity_type: string;
          event_data?: Json | null;
          event_type: string;
          id?: string;
          ip_address?: unknown;
          session_id?: string | null;
          user_agent?: string | null;
          user_id?: string | null;
        };
        Update: {
          created_at?: string | null;
          entity_id?: string;
          entity_type?: string;
          event_data?: Json | null;
          event_type?: string;
          id?: string;
          ip_address?: unknown;
          session_id?: string | null;
          user_agent?: string | null;
          user_id?: string | null;
        };
        Relationships: [];
      };
      booking_communications: {
        Row: {
          booking_request_id: string;
          communication_type: string;
          created_at: string | null;
          delivered_at: string | null;
          email_message_id: string | null;
          id: string;
          message_body: string;
          read_at: string | null;
          recipient_type: string;
          sender_type: string;
          sender_user_id: string | null;
          sent_at: string | null;
          sms_message_id: string | null;
          subject: string | null;
        };
        Insert: {
          booking_request_id: string;
          communication_type: string;
          created_at?: string | null;
          delivered_at?: string | null;
          email_message_id?: string | null;
          id?: string;
          message_body: string;
          read_at?: string | null;
          recipient_type: string;
          sender_type: string;
          sender_user_id?: string | null;
          sent_at?: string | null;
          sms_message_id?: string | null;
          subject?: string | null;
        };
        Update: {
          booking_request_id?: string;
          communication_type?: string;
          created_at?: string | null;
          delivered_at?: string | null;
          email_message_id?: string | null;
          id?: string;
          message_body?: string;
          read_at?: string | null;
          recipient_type?: string;
          sender_type?: string;
          sender_user_id?: string | null;
          sent_at?: string | null;
          sms_message_id?: string | null;
          subject?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "booking_communications_booking_request_id_fkey";
            columns: ["booking_request_id"];
            isOneToOne: false;
            referencedRelation: "booking_requests";
            referencedColumns: ["id"];
          },
        ];
      };
      booking_contact_comedians: {
        Row: {
          comedian_id: string;
          contact_id: string;
          created_at: string;
          id: string;
          notes: string | null;
          tags: string[] | null;
          tier: number | null;
        };
        Insert: {
          comedian_id: string;
          contact_id: string;
          created_at?: string;
          id?: string;
          notes?: string | null;
          tags?: string[] | null;
          tier?: number | null;
        };
        Update: {
          comedian_id?: string;
          contact_id?: string;
          created_at?: string;
          id?: string;
          notes?: string | null;
          tags?: string[] | null;
          tier?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "booking_contact_comedians_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "booking_contact_comedians_contact_id_fkey";
            columns: ["contact_id"];
            isOneToOne: false;
            referencedRelation: "booking_contacts";
            referencedColumns: ["id"];
          },
        ];
      };
      booking_contact_invitations: {
        Row: {
          booking_contact_id: string;
          booking_id: string;
          created_at: string;
          email_sent: boolean;
          email_sent_at: string | null;
          expires_at: string;
          id: string;
          invitation_token: string;
          lineup_position: number | null;
          performance_duration: number | null;
          performance_fee: number | null;
          performance_time: string | null;
          show_id: string | null;
          sms_sent: boolean;
          sms_sent_at: string | null;
          updated_at: string;
        };
        Insert: {
          booking_contact_id: string;
          booking_id: string;
          created_at?: string;
          email_sent?: boolean;
          email_sent_at?: string | null;
          expires_at: string;
          id?: string;
          invitation_token: string;
          lineup_position?: number | null;
          performance_duration?: number | null;
          performance_fee?: number | null;
          performance_time?: string | null;
          show_id?: string | null;
          sms_sent?: boolean;
          sms_sent_at?: string | null;
          updated_at?: string;
        };
        Update: {
          booking_contact_id?: string;
          booking_id?: string;
          created_at?: string;
          email_sent?: boolean;
          email_sent_at?: string | null;
          expires_at?: string;
          id?: string;
          invitation_token?: string;
          lineup_position?: number | null;
          performance_duration?: number | null;
          performance_fee?: number | null;
          performance_time?: string | null;
          show_id?: string | null;
          sms_sent?: boolean;
          sms_sent_at?: string | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "booking_contact_invitations_booking_contact_id_fkey";
            columns: ["booking_contact_id"];
            isOneToOne: false;
            referencedRelation: "booking_contacts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "booking_contact_invitations_booking_id_fkey";
            columns: ["booking_id"];
            isOneToOne: false;
            referencedRelation: "bookings";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "booking_contact_invitations_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "booking_contact_invitations_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
        ];
      };
      booking_contact_responses: {
        Row: {
          booking_contact_invitation_id: string;
          booking_date_ids: string[];
          comedian_id: string | null;
          contact_email: string | null;
          contact_name: string | null;
          created_at: string;
          id: string;
          invitation_response_at: string | null;
          invitation_sent_at: string | null;
          invitation_token: string | null;
          lineup_position: number | null;
          message: string | null;
          performance_duration: number | null;
          performance_fee: number | null;
          performance_time: string | null;
          show_id: string | null;
          status: string;
          updated_at: string;
        };
        Insert: {
          booking_contact_invitation_id: string;
          booking_date_ids?: string[];
          comedian_id?: string | null;
          contact_email?: string | null;
          contact_name?: string | null;
          created_at?: string;
          id?: string;
          invitation_response_at?: string | null;
          invitation_sent_at?: string | null;
          invitation_token?: string | null;
          lineup_position?: number | null;
          message?: string | null;
          performance_duration?: number | null;
          performance_fee?: number | null;
          performance_time?: string | null;
          show_id?: string | null;
          status?: string;
          updated_at?: string;
        };
        Update: {
          booking_contact_invitation_id?: string;
          booking_date_ids?: string[];
          comedian_id?: string | null;
          contact_email?: string | null;
          contact_name?: string | null;
          created_at?: string;
          id?: string;
          invitation_response_at?: string | null;
          invitation_sent_at?: string | null;
          invitation_token?: string | null;
          lineup_position?: number | null;
          message?: string | null;
          performance_duration?: number | null;
          performance_fee?: number | null;
          performance_time?: string | null;
          show_id?: string | null;
          status?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "booking_contact_responses_booking_contact_invitation_id_fkey";
            columns: ["booking_contact_invitation_id"];
            isOneToOne: false;
            referencedRelation: "booking_contact_invitations";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "booking_contact_responses_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "booking_contact_responses_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "booking_contact_responses_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
        ];
      };
      booking_contacts: {
        Row: {
          created_at: string;
          description: string | null;
          email: string | null;
          id: string;
          is_active: boolean;
          name: string;
          phone: string | null;
          updated_at: string;
          venue_page_id: string;
        };
        Insert: {
          created_at?: string;
          description?: string | null;
          email?: string | null;
          id?: string;
          is_active?: boolean;
          name: string;
          phone?: string | null;
          updated_at?: string;
          venue_page_id: string;
        };
        Update: {
          created_at?: string;
          description?: string | null;
          email?: string | null;
          id?: string;
          is_active?: boolean;
          name?: string;
          phone?: string | null;
          updated_at?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "booking_contacts_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      booking_dates: {
        Row: {
          booking_id: string;
          created_at: string;
          date: string;
          end_time: string | null;
          id: string;
          is_available: boolean;
          notes: string | null;
          start_time: string | null;
          updated_at: string;
        };
        Insert: {
          booking_id: string;
          created_at?: string;
          date: string;
          end_time?: string | null;
          id?: string;
          is_available?: boolean;
          notes?: string | null;
          start_time?: string | null;
          updated_at?: string;
        };
        Update: {
          booking_id?: string;
          created_at?: string;
          date?: string;
          end_time?: string | null;
          id?: string;
          is_available?: boolean;
          notes?: string | null;
          start_time?: string | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "booking_dates_booking_id_fkey";
            columns: ["booking_id"];
            isOneToOne: false;
            referencedRelation: "bookings";
            referencedColumns: ["id"];
          },
        ];
      };
      booking_requests: {
        Row: {
          audience_size: number | null;
          comedian_id: string;
          compensation_offered: number | null;
          compensation_type: string | null;
          created_at: string | null;
          created_by: string | null;
          event_description: string | null;
          event_title: string | null;
          event_type: string;
          id: string;
          initial_message: string;
          last_communication_at: string | null;
          performance_duration: number | null;
          requested_dates: Json;
          response_deadline: string | null;
          special_requirements: string | null;
          status: string | null;
          updated_at: string | null;
          venue_contact_email: string;
          venue_contact_phone: string | null;
          venue_page_id: string;
        };
        Insert: {
          audience_size?: number | null;
          comedian_id: string;
          compensation_offered?: number | null;
          compensation_type?: string | null;
          created_at?: string | null;
          created_by?: string | null;
          event_description?: string | null;
          event_title?: string | null;
          event_type?: string;
          id?: string;
          initial_message: string;
          last_communication_at?: string | null;
          performance_duration?: number | null;
          requested_dates: Json;
          response_deadline?: string | null;
          special_requirements?: string | null;
          status?: string | null;
          updated_at?: string | null;
          venue_contact_email: string;
          venue_contact_phone?: string | null;
          venue_page_id: string;
        };
        Update: {
          audience_size?: number | null;
          comedian_id?: string;
          compensation_offered?: number | null;
          compensation_type?: string | null;
          created_at?: string | null;
          created_by?: string | null;
          event_description?: string | null;
          event_title?: string | null;
          event_type?: string;
          id?: string;
          initial_message?: string;
          last_communication_at?: string | null;
          performance_duration?: number | null;
          requested_dates?: Json;
          response_deadline?: string | null;
          special_requirements?: string | null;
          status?: string | null;
          updated_at?: string | null;
          venue_contact_email?: string;
          venue_contact_phone?: string | null;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "booking_requests_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "booking_requests_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      booking_responses: {
        Row: {
          booking_request_id: string;
          comedian_id: string;
          counter_offer_compensation: number | null;
          counter_offer_dates: Json | null;
          created_at: string | null;
          id: string;
          responded_by: string | null;
          response_message: string | null;
          response_type: string;
        };
        Insert: {
          booking_request_id: string;
          comedian_id: string;
          counter_offer_compensation?: number | null;
          counter_offer_dates?: Json | null;
          created_at?: string | null;
          id?: string;
          responded_by?: string | null;
          response_message?: string | null;
          response_type: string;
        };
        Update: {
          booking_request_id?: string;
          comedian_id?: string;
          counter_offer_compensation?: number | null;
          counter_offer_dates?: Json | null;
          created_at?: string | null;
          id?: string;
          responded_by?: string | null;
          response_message?: string | null;
          response_type?: string;
        };
        Relationships: [
          {
            foreignKeyName: "booking_responses_booking_request_id_fkey";
            columns: ["booking_request_id"];
            isOneToOne: false;
            referencedRelation: "booking_requests";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "booking_responses_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      bookings: {
        Row: {
          additional_message: string | null;
          created_at: string;
          created_by: string;
          description: string | null;
          id: string;
          status: string;
          title: string;
          updated_at: string;
          venue_page_id: string;
        };
        Insert: {
          additional_message?: string | null;
          created_at?: string;
          created_by: string;
          description?: string | null;
          id?: string;
          status?: string;
          title: string;
          updated_at?: string;
          venue_page_id: string;
        };
        Update: {
          additional_message?: string | null;
          created_at?: string;
          created_by?: string;
          description?: string | null;
          id?: string;
          status?: string;
          title?: string;
          updated_at?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "bookings_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      carousel_content: {
        Row: {
          carousel_type: number | null;
          comedian_id: string | null;
          created_at: string;
          id: string;
          position: number;
          special_id: string | null;
        };
        Insert: {
          carousel_type?: number | null;
          comedian_id?: string | null;
          created_at?: string;
          id?: string;
          position: number;
          special_id?: string | null;
        };
        Update: {
          carousel_type?: number | null;
          comedian_id?: string | null;
          created_at?: string;
          id?: string;
          position?: number;
          special_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "carousel_content_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "carousel_content_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      city_coordinates: {
        Row: {
          city: string | null;
          country: string | null;
          lat: number | null;
          long: number | null;
          region: string | null;
        };
        Insert: {
          city?: string | null;
          country?: string | null;
          lat?: number | null;
          long?: number | null;
          region?: string | null;
        };
        Update: {
          city?: string | null;
          country?: string | null;
          lat?: number | null;
          long?: number | null;
          region?: string | null;
        };
        Relationships: [];
      };
      clipping_jobs: {
        Row: {
          clips: Json;
          comedian_id: string;
          created_at: string;
          error: string | null;
          external_project_id: string;
          id: string;
          parent_special_id: string;
          provider: string;
          requested_by: string | null;
          status: string;
          updated_at: string;
        };
        Insert: {
          clips?: Json;
          comedian_id: string;
          created_at?: string;
          error?: string | null;
          external_project_id: string;
          id?: string;
          parent_special_id: string;
          provider: string;
          requested_by?: string | null;
          status?: string;
          updated_at?: string;
        };
        Update: {
          clips?: Json;
          comedian_id?: string;
          created_at?: string;
          error?: string | null;
          external_project_id?: string;
          id?: string;
          parent_special_id?: string;
          provider?: string;
          requested_by?: string | null;
          status?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "clipping_jobs_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "clipping_jobs_parent_special_id_fkey";
            columns: ["parent_special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      collection_specials: {
        Row: {
          collection_id: string;
          created_at: string;
          is_featured: boolean;
          sort_order: number;
          special_id: string;
        };
        Insert: {
          collection_id: string;
          created_at?: string;
          is_featured?: boolean;
          sort_order?: number;
          special_id: string;
        };
        Update: {
          collection_id?: string;
          created_at?: string;
          is_featured?: boolean;
          sort_order?: number;
          special_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "collection_specials_collection_id_fkey";
            columns: ["collection_id"];
            isOneToOne: false;
            referencedRelation: "collections";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "collection_specials_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      collections: {
        Row: {
          comedian_id: string;
          created_at: string;
          description: string;
          id: string;
          kind: Database["public"]["Enums"]["collection_kind"];
          name: string;
          published_at: string | null;
          required_subscription_tier: number | null;
          requires_purchase: boolean;
          requires_subscription: boolean;
          slug: string;
          subscription_product_id: string | null;
        };
        Insert: {
          comedian_id?: string;
          created_at?: string;
          description: string;
          id?: string;
          kind?: Database["public"]["Enums"]["collection_kind"];
          name: string;
          published_at?: string | null;
          required_subscription_tier?: number | null;
          requires_purchase: boolean;
          requires_subscription: boolean;
          slug: string;
          subscription_product_id?: string | null;
        };
        Update: {
          comedian_id?: string;
          created_at?: string;
          description?: string;
          id?: string;
          kind?: Database["public"]["Enums"]["collection_kind"];
          name?: string;
          published_at?: string | null;
          required_subscription_tier?: number | null;
          requires_purchase?: boolean;
          requires_subscription?: boolean;
          slug?: string;
          subscription_product_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "collections_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "collections_subscription_product_id_fkey";
            columns: ["subscription_product_id"];
            isOneToOne: false;
            referencedRelation: "products";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_analytics_tags: {
        Row: {
          comedian_id: string;
          config: Json;
          created_at: string;
          enabled: boolean;
          id: string;
          measurement_id: string;
          mode: string;
          notes: string | null;
          provider: string;
          updated_at: string;
        };
        Insert: {
          comedian_id: string;
          config?: Json;
          created_at?: string;
          enabled?: boolean;
          id?: string;
          measurement_id: string;
          mode?: string;
          notes?: string | null;
          provider: string;
          updated_at?: string;
        };
        Update: {
          comedian_id?: string;
          config?: Json;
          created_at?: string;
          enabled?: boolean;
          id?: string;
          measurement_id?: string;
          mode?: string;
          notes?: string | null;
          provider?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_analytics_tags_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_availability: {
        Row: {
          advance_notice_required: number | null;
          available_from: string;
          available_to: string;
          blocked_dates: Json | null;
          comedian_id: string;
          created_at: string | null;
          created_by: string | null;
          id: string;
          is_active: boolean | null;
          minimum_compensation: number | null;
          preferred_event_types: string[] | null;
          preferred_regions: Json | null;
          recurring_schedule: Json | null;
          requires_accommodation: boolean | null;
          travel_radius: number | null;
          updated_at: string | null;
        };
        Insert: {
          advance_notice_required?: number | null;
          available_from: string;
          available_to: string;
          blocked_dates?: Json | null;
          comedian_id: string;
          created_at?: string | null;
          created_by?: string | null;
          id?: string;
          is_active?: boolean | null;
          minimum_compensation?: number | null;
          preferred_event_types?: string[] | null;
          preferred_regions?: Json | null;
          recurring_schedule?: Json | null;
          requires_accommodation?: boolean | null;
          travel_radius?: number | null;
          updated_at?: string | null;
        };
        Update: {
          advance_notice_required?: number | null;
          available_from?: string;
          available_to?: string;
          blocked_dates?: Json | null;
          comedian_id?: string;
          created_at?: string | null;
          created_by?: string | null;
          id?: string;
          is_active?: boolean | null;
          minimum_compensation?: number | null;
          preferred_event_types?: string[] | null;
          preferred_regions?: Json | null;
          recurring_schedule?: Json | null;
          requires_accommodation?: boolean | null;
          travel_radius?: number | null;
          updated_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_availability_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_clicked_play_agg: {
        Row: {
          comedian_id: string;
          comedian_total_events: number | null;
          last_updated: string;
          referrer_data_growth: Json | null;
          referrer_data_totals: Json | null;
          state_growth: Json | null;
          state_totals: Json | null;
        };
        Insert: {
          comedian_id: string;
          comedian_total_events?: number | null;
          last_updated: string;
          referrer_data_growth?: Json | null;
          referrer_data_totals?: Json | null;
          state_growth?: Json | null;
          state_totals?: Json | null;
        };
        Update: {
          comedian_id?: string;
          comedian_total_events?: number | null;
          last_updated?: string;
          referrer_data_growth?: Json | null;
          referrer_data_totals?: Json | null;
          state_growth?: Json | null;
          state_totals?: Json | null;
        };
        Relationships: [];
      };
      comedian_contacts_near_venues: {
        Row: {
          comedian_id: string;
          contacts_within_50_miles: Json | null;
          last_updated: string | null;
          num_contacts_within_50_miles: number | null;
          total_contacts: number | null;
          total_reachable_emails: number | null;
          total_reachable_phones: number | null;
          venue_id: string;
        };
        Insert: {
          comedian_id: string;
          contacts_within_50_miles?: Json | null;
          last_updated?: string | null;
          num_contacts_within_50_miles?: number | null;
          total_contacts?: number | null;
          total_reachable_emails?: number | null;
          total_reachable_phones?: number | null;
          venue_id: string;
        };
        Update: {
          comedian_id?: string;
          contacts_within_50_miles?: Json | null;
          last_updated?: string | null;
          num_contacts_within_50_miles?: number | null;
          total_contacts?: number | null;
          total_reachable_emails?: number | null;
          total_reachable_phones?: number | null;
          venue_id?: string;
        };
        Relationships: [];
      };
      comedian_contacts_near_venues_summary: {
        Row: {
          comedian_id: string;
          ingestion_time: string | null;
          total_contacts: number | null;
          total_contacts_within_50_miles: number | null;
          total_reachable_emails: number | null;
          total_reachable_phones: number | null;
          venues_data: Json | null;
        };
        Insert: {
          comedian_id: string;
          ingestion_time?: string | null;
          total_contacts?: number | null;
          total_contacts_within_50_miles?: number | null;
          total_reachable_emails?: number | null;
          total_reachable_phones?: number | null;
          venues_data?: Json | null;
        };
        Update: {
          comedian_id?: string;
          ingestion_time?: string | null;
          total_contacts?: number | null;
          total_contacts_within_50_miles?: number | null;
          total_reachable_emails?: number | null;
          total_reachable_phones?: number | null;
          venues_data?: Json | null;
        };
        Relationships: [];
      };
      comedian_contacts_near_venues_v2: {
        Row: {
          comedian_id: string;
          contact_id: string;
          distance_miles: number;
          email: string | null;
          last_updated: string | null;
          phone: string | null;
          venue_id: string;
        };
        Insert: {
          comedian_id: string;
          contact_id: string;
          distance_miles: number;
          email?: string | null;
          last_updated?: string | null;
          phone?: string | null;
          venue_id: string;
        };
        Update: {
          comedian_id?: string;
          contact_id?: string;
          distance_miles?: number;
          email?: string | null;
          last_updated?: string | null;
          phone?: string | null;
          venue_id?: string;
        };
        Relationships: [];
      };
      comedian_dashboard_admins: {
        Row: {
          admin_id: string;
          comedian_id: string;
        };
        Insert: {
          admin_id: string;
          comedian_id: string;
        };
        Update: {
          admin_id?: string;
          comedian_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_dashboard_admins_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_dashboard_benchmarks: {
        Row: {
          day_of_week_data: Json | null;
          events_growth_rate: Json | null;
          metrics: Json | null;
          referral_data: Json | null;
          table_metric_timestamp: string;
          table_source: string | null;
          ts: string | null;
        };
        Insert: {
          day_of_week_data?: Json | null;
          events_growth_rate?: Json | null;
          metrics?: Json | null;
          referral_data?: Json | null;
          table_metric_timestamp: string;
          table_source?: string | null;
          ts?: string | null;
        };
        Update: {
          day_of_week_data?: Json | null;
          events_growth_rate?: Json | null;
          metrics?: Json | null;
          referral_data?: Json | null;
          table_metric_timestamp?: string;
          table_source?: string | null;
          ts?: string | null;
        };
        Relationships: [];
      };
      comedian_dashboard_benchmarks_duplicate: {
        Row: {
          day_of_week_data: Json | null;
          events_growth_rate: Json | null;
          metrics: Json | null;
          referral_data: Json | null;
          table_metric_timestamp: string;
          table_source: string | null;
          ts: string | null;
        };
        Insert: {
          day_of_week_data?: Json | null;
          events_growth_rate?: Json | null;
          metrics?: Json | null;
          referral_data?: Json | null;
          table_metric_timestamp: string;
          table_source?: string | null;
          ts?: string | null;
        };
        Update: {
          day_of_week_data?: Json | null;
          events_growth_rate?: Json | null;
          metrics?: Json | null;
          referral_data?: Json | null;
          table_metric_timestamp?: string;
          table_source?: string | null;
          ts?: string | null;
        };
        Relationships: [];
      };
      comedian_dashboard_benchmarks_v2: {
        Row: {
          bucket: string | null;
          comedian_id: string;
          created_at: string | null;
          metrics: Json | null;
          months_since_creation: number | null;
          ts: string | null;
        };
        Insert: {
          bucket?: string | null;
          comedian_id: string;
          created_at?: string | null;
          metrics?: Json | null;
          months_since_creation?: number | null;
          ts?: string | null;
        };
        Update: {
          bucket?: string | null;
          comedian_id?: string;
          created_at?: string | null;
          metrics?: Json | null;
          months_since_creation?: number | null;
          ts?: string | null;
        };
        Relationships: [];
      };
      comedian_dashboard_followers_details: {
        Row: {
          city: string | null;
          comedian_id: string;
          created_at: string;
          email: string | null;
          latitude: number | null;
          location_geo: unknown;
          longitude: number | null;
          phone: string | null;
          state: string | null;
          state_abbreviation: string | null;
          ts: string;
          user_comedian_id_ts: string | null;
          user_id: string;
          zipcode: string | null;
        };
        Insert: {
          city?: string | null;
          comedian_id: string;
          created_at: string;
          email?: string | null;
          latitude?: number | null;
          location_geo?: unknown;
          longitude?: number | null;
          phone?: string | null;
          state?: string | null;
          state_abbreviation?: string | null;
          ts: string;
          user_comedian_id_ts?: string | null;
          user_id: string;
          zipcode?: string | null;
        };
        Update: {
          city?: string | null;
          comedian_id?: string;
          created_at?: string;
          email?: string | null;
          latitude?: number | null;
          location_geo?: unknown;
          longitude?: number | null;
          phone?: string | null;
          state?: string | null;
          state_abbreviation?: string | null;
          ts?: string;
          user_comedian_id_ts?: string | null;
          user_id?: string;
          zipcode?: string | null;
        };
        Relationships: [];
      };
      comedian_dashboard_followers_details_old: {
        Row: {
          city: string | null;
          comedian_id: string | null;
          created_at: string | null;
          email: string | null;
          latitude: number | null;
          longitude: number | null;
          phone: string | null;
          state: string | null;
          state_abbreviation: string | null;
          ts: string | null;
          user_comedian_id_ts: string;
          user_id: string | null;
          zipcode: string | null;
        };
        Insert: {
          city?: string | null;
          comedian_id?: string | null;
          created_at?: string | null;
          email?: string | null;
          latitude?: number | null;
          longitude?: number | null;
          phone?: string | null;
          state?: string | null;
          state_abbreviation?: string | null;
          ts?: string | null;
          user_comedian_id_ts: string;
          user_id?: string | null;
          zipcode?: string | null;
        };
        Update: {
          city?: string | null;
          comedian_id?: string | null;
          created_at?: string | null;
          email?: string | null;
          latitude?: number | null;
          longitude?: number | null;
          phone?: string | null;
          state?: string | null;
          state_abbreviation?: string | null;
          ts?: string | null;
          user_comedian_id_ts?: string;
          user_id?: string | null;
          zipcode?: string | null;
        };
        Relationships: [];
      };
      comedian_dashboard_uploaded_ticket_data: {
        Row: {
          "% SOLD": string | null;
          "4/17/24": string | null;
          "4/18/24": string | null;
          CAPACITY: string | null;
          CITY: string | null;
          comedian_id: string | null;
          DATE: string | null;
          "HOLDS &\nCOMPS": string | null;
          "NET CAP": string | null;
          "On-Sale": string | null;
          OPEN: string | null;
          show_id: string;
          STATE: string | null;
          VENUE: string | null;
          venue_id: string | null;
          WRAP: number | null;
        };
        Insert: {
          "% SOLD"?: string | null;
          "4/17/24"?: string | null;
          "4/18/24"?: string | null;
          CAPACITY?: string | null;
          CITY?: string | null;
          comedian_id?: string | null;
          DATE?: string | null;
          "HOLDS &\nCOMPS"?: string | null;
          "NET CAP"?: string | null;
          "On-Sale"?: string | null;
          OPEN?: string | null;
          show_id: string;
          STATE?: string | null;
          VENUE?: string | null;
          venue_id?: string | null;
          WRAP?: number | null;
        };
        Update: {
          "% SOLD"?: string | null;
          "4/17/24"?: string | null;
          "4/18/24"?: string | null;
          CAPACITY?: string | null;
          CITY?: string | null;
          comedian_id?: string | null;
          DATE?: string | null;
          "HOLDS &\nCOMPS"?: string | null;
          "NET CAP"?: string | null;
          "On-Sale"?: string | null;
          OPEN?: string | null;
          show_id?: string;
          STATE?: string | null;
          VENUE?: string | null;
          venue_id?: string | null;
          WRAP?: number | null;
        };
        Relationships: [];
      };
      comedian_digest_email_events: {
        Row: {
          clicked_link: boolean;
          complained: boolean;
          digest_run_id: string;
          email: string;
          email_delivered_at: string | null;
          failed_delivery: boolean;
          opened_email: boolean;
          unsubscribed: boolean;
          updated_at: string;
          user_id: string;
        };
        Insert: {
          clicked_link?: boolean;
          complained?: boolean;
          digest_run_id: string;
          email?: string;
          email_delivered_at?: string | null;
          failed_delivery?: boolean;
          opened_email?: boolean;
          unsubscribed?: boolean;
          updated_at?: string;
          user_id: string;
        };
        Update: {
          clicked_link?: boolean;
          complained?: boolean;
          digest_run_id?: string;
          email?: string;
          email_delivered_at?: string | null;
          failed_delivery?: boolean;
          opened_email?: boolean;
          unsubscribed?: boolean;
          updated_at?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_digest_email_events_digest_run_id_fkey";
            columns: ["digest_run_id"];
            isOneToOne: false;
            referencedRelation: "comedian_weekly_digest_runs";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_fan_venue_distances: {
        Row: {
          comedian_id: string;
          distance_miles: number | null;
          email: string | null;
          last_updated: string | null;
          phone: string | null;
          user_id: string;
          venue_id: string;
        };
        Insert: {
          comedian_id: string;
          distance_miles?: number | null;
          email?: string | null;
          last_updated?: string | null;
          phone?: string | null;
          user_id: string;
          venue_id: string;
        };
        Update: {
          comedian_id?: string;
          distance_miles?: number | null;
          email?: string | null;
          last_updated?: string | null;
          phone?: string | null;
          user_id?: string;
          venue_id?: string;
        };
        Relationships: [];
      };
      comedian_fans_near_venues: {
        Row: {
          comedian_id: string;
          last_updated: string;
          num_users_within_50_miles: number | null;
          total_followers: number | null;
          total_reachable_emails: number;
          total_reachable_phones: number;
          users_within_50_miles: Json | null;
          venue_id: string;
        };
        Insert: {
          comedian_id: string;
          last_updated: string;
          num_users_within_50_miles?: number | null;
          total_followers?: number | null;
          total_reachable_emails?: number;
          total_reachable_phones?: number;
          users_within_50_miles?: Json | null;
          venue_id: string;
        };
        Update: {
          comedian_id?: string;
          last_updated?: string;
          num_users_within_50_miles?: number | null;
          total_followers?: number | null;
          total_reachable_emails?: number;
          total_reachable_phones?: number;
          users_within_50_miles?: Json | null;
          venue_id?: string;
        };
        Relationships: [];
      };
      comedian_fans_near_venues_summary: {
        Row: {
          comedian_id: string | null;
          comedian_id_update_time: string;
          ingestion_time: string | null;
          total_followers: number | null;
          total_followers_within_50_miles: number | null;
          total_reachable_emails: number;
          total_reachable_phones: number;
          venues_data: Json | null;
        };
        Insert: {
          comedian_id?: string | null;
          comedian_id_update_time: string;
          ingestion_time?: string | null;
          total_followers?: number | null;
          total_followers_within_50_miles?: number | null;
          total_reachable_emails?: number;
          total_reachable_phones?: number;
          venues_data?: Json | null;
        };
        Update: {
          comedian_id?: string | null;
          comedian_id_update_time?: string;
          ingestion_time?: string | null;
          total_followers?: number | null;
          total_followers_within_50_miles?: number | null;
          total_reachable_emails?: number;
          total_reachable_phones?: number;
          venues_data?: Json | null;
        };
        Relationships: [];
      };
      comedian_fans_near_venues_v2: {
        Row: {
          comedian_id: string;
          distance_miles: number;
          email: string | null;
          last_updated: string | null;
          phone: string | null;
          user_id: string;
          venue_id: string;
        };
        Insert: {
          comedian_id: string;
          distance_miles: number;
          email?: string | null;
          last_updated?: string | null;
          phone?: string | null;
          user_id: string;
          venue_id: string;
        };
        Update: {
          comedian_id?: string;
          distance_miles?: number;
          email?: string | null;
          last_updated?: string | null;
          phone?: string | null;
          user_id?: string;
          venue_id?: string;
        };
        Relationships: [];
      };
      comedian_followed_agg: {
        Row: {
          comedian_id: string;
          comedian_total_events: number | null;
          last_updated: string;
          referrer_data_growth: Json | null;
          referrer_data_totals: Json | null;
          state_growth: Json | null;
          state_totals: Json | null;
        };
        Insert: {
          comedian_id: string;
          comedian_total_events?: number | null;
          last_updated: string;
          referrer_data_growth?: Json | null;
          referrer_data_totals?: Json | null;
          state_growth?: Json | null;
          state_totals?: Json | null;
        };
        Update: {
          comedian_id?: string;
          comedian_total_events?: number | null;
          last_updated?: string;
          referrer_data_growth?: Json | null;
          referrer_data_totals?: Json | null;
          state_growth?: Json | null;
          state_totals?: Json | null;
        };
        Relationships: [];
      };
      comedian_followers_agg: {
        Row: {
          comedian_id: string;
          comedian_total_events: number | null;
          last_updated: string;
          referrer_data_growth: Json | null;
          referrer_data_totals: Json | null;
          state_growth: Json | null;
          state_totals: Json | null;
        };
        Insert: {
          comedian_id: string;
          comedian_total_events?: number | null;
          last_updated: string;
          referrer_data_growth?: Json | null;
          referrer_data_totals?: Json | null;
          state_growth?: Json | null;
          state_totals?: Json | null;
        };
        Update: {
          comedian_id?: string;
          comedian_total_events?: number | null;
          last_updated?: string;
          referrer_data_growth?: Json | null;
          referrer_data_totals?: Json | null;
          state_growth?: Json | null;
          state_totals?: Json | null;
        };
        Relationships: [];
      };
      comedian_followers_daily: {
        Row: {
          comedian_id: string;
          cumulative_fans: number;
          ds: string;
          id: string;
          last_updated: string;
          num_fans: number;
        };
        Insert: {
          comedian_id: string;
          cumulative_fans: number;
          ds: string;
          id: string;
          last_updated: string;
          num_fans: number;
        };
        Update: {
          comedian_id?: string;
          cumulative_fans?: number;
          ds?: string;
          id?: string;
          last_updated?: string;
          num_fans?: number;
        };
        Relationships: [];
      };
      comedian_followers_summary: {
        Row: {
          comedian_id: string;
          cumulative_growth_180d: number | null;
          cumulative_growth_30d: number | null;
          cumulative_growth_365d: number | null;
          cumulative_growth_7d: number | null;
          cumulative_growth_90d: number | null;
          cumulative_growth_percent_180d: number | null;
          cumulative_growth_percent_30d: number | null;
          cumulative_growth_percent_365d: number | null;
          cumulative_growth_percent_7d: number | null;
          cumulative_growth_percent_90d: number | null;
          display_name: string | null;
          join_date: string | null;
          last_updated: string;
          new_followers_180d: number | null;
          new_followers_30d: number | null;
          new_followers_365d: number | null;
          new_followers_7d: number | null;
          new_followers_90d: number | null;
          new_growth_180d: number | null;
          new_growth_30d: number | null;
          new_growth_365d: number | null;
          new_growth_7d: number | null;
          new_growth_90d: number | null;
          new_growth_percent_180d: number | null;
          new_growth_percent_30d: number | null;
          new_growth_percent_365d: number | null;
          new_growth_percent_7d: number | null;
          new_growth_percent_90d: number | null;
          total_followers: number | null;
        };
        Insert: {
          comedian_id: string;
          cumulative_growth_180d?: number | null;
          cumulative_growth_30d?: number | null;
          cumulative_growth_365d?: number | null;
          cumulative_growth_7d?: number | null;
          cumulative_growth_90d?: number | null;
          cumulative_growth_percent_180d?: number | null;
          cumulative_growth_percent_30d?: number | null;
          cumulative_growth_percent_365d?: number | null;
          cumulative_growth_percent_7d?: number | null;
          cumulative_growth_percent_90d?: number | null;
          display_name?: string | null;
          join_date?: string | null;
          last_updated: string;
          new_followers_180d?: number | null;
          new_followers_30d?: number | null;
          new_followers_365d?: number | null;
          new_followers_7d?: number | null;
          new_followers_90d?: number | null;
          new_growth_180d?: number | null;
          new_growth_30d?: number | null;
          new_growth_365d?: number | null;
          new_growth_7d?: number | null;
          new_growth_90d?: number | null;
          new_growth_percent_180d?: number | null;
          new_growth_percent_30d?: number | null;
          new_growth_percent_365d?: number | null;
          new_growth_percent_7d?: number | null;
          new_growth_percent_90d?: number | null;
          total_followers?: number | null;
        };
        Update: {
          comedian_id?: string;
          cumulative_growth_180d?: number | null;
          cumulative_growth_30d?: number | null;
          cumulative_growth_365d?: number | null;
          cumulative_growth_7d?: number | null;
          cumulative_growth_90d?: number | null;
          cumulative_growth_percent_180d?: number | null;
          cumulative_growth_percent_30d?: number | null;
          cumulative_growth_percent_365d?: number | null;
          cumulative_growth_percent_7d?: number | null;
          cumulative_growth_percent_90d?: number | null;
          display_name?: string | null;
          join_date?: string | null;
          last_updated?: string;
          new_followers_180d?: number | null;
          new_followers_30d?: number | null;
          new_followers_365d?: number | null;
          new_followers_7d?: number | null;
          new_followers_90d?: number | null;
          new_growth_180d?: number | null;
          new_growth_30d?: number | null;
          new_growth_365d?: number | null;
          new_growth_7d?: number | null;
          new_growth_90d?: number | null;
          new_growth_percent_180d?: number | null;
          new_growth_percent_30d?: number | null;
          new_growth_percent_365d?: number | null;
          new_growth_percent_7d?: number | null;
          new_growth_percent_90d?: number | null;
          total_followers?: number | null;
        };
        Relationships: [];
      };
      comedian_momentum_scores: {
        Row: {
          comedian_id: string;
          computed_at: string;
          ds: string;
          follow_delta_24h: number;
          follow_delta_7d: number;
          momentum_score: number;
          playback_count_24h: number;
          playback_count_7d: number;
          ticket_clicks_24h: number;
          ticket_clicks_7d: number;
        };
        Insert: {
          comedian_id: string;
          computed_at?: string;
          ds: string;
          follow_delta_24h?: number;
          follow_delta_7d?: number;
          momentum_score: number;
          playback_count_24h?: number;
          playback_count_7d?: number;
          ticket_clicks_24h?: number;
          ticket_clicks_7d?: number;
        };
        Update: {
          comedian_id?: string;
          computed_at?: string;
          ds?: string;
          follow_delta_24h?: number;
          follow_delta_7d?: number;
          momentum_score?: number;
          playback_count_24h?: number;
          playback_count_7d?: number;
          ticket_clicks_24h?: number;
          ticket_clicks_7d?: number;
        };
        Relationships: [];
      };
      comedian_representation_contacts: {
        Row: {
          comedian_id: string;
          contact_name: string;
          contact_type: string;
          created_at: string;
          email: string | null;
          id: string;
          organization_name: string | null;
          phone: string | null;
          sort_order: number;
          updated_at: string;
        };
        Insert: {
          comedian_id: string;
          contact_name: string;
          contact_type: string;
          created_at?: string;
          email?: string | null;
          id?: string;
          organization_name?: string | null;
          phone?: string | null;
          sort_order?: number;
          updated_at?: string;
        };
        Update: {
          comedian_id?: string;
          contact_name?: string;
          contact_type?: string;
          created_at?: string;
          email?: string | null;
          id?: string;
          organization_name?: string | null;
          phone?: string | null;
          sort_order?: number;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_representation_contacts_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_shows: {
        Row: {
          comedian_id: string | null;
          created_at: string;
          datetime: string | null;
          deleted_at: string | null;
          flags: Database["public"]["Enums"]["ticket_flags"][] | null;
          id: string;
          is_sold_out: boolean | null;
          location: string;
          metadata_text: string | null;
          poster_img: string | null;
          presale_code: string | null;
          published_at: string | null;
          ticket_link: string;
          title: string | null;
          tixologi_event_id: string | null;
          venue: string;
          venue_id: string;
          vip_ticket_link: string | null;
        };
        Insert: {
          comedian_id?: string | null;
          created_at?: string;
          datetime?: string | null;
          deleted_at?: string | null;
          flags?: Database["public"]["Enums"]["ticket_flags"][] | null;
          id?: string;
          is_sold_out?: boolean | null;
          location: string;
          metadata_text?: string | null;
          poster_img?: string | null;
          presale_code?: string | null;
          published_at?: string | null;
          ticket_link: string;
          title?: string | null;
          tixologi_event_id?: string | null;
          venue: string;
          venue_id: string;
          vip_ticket_link?: string | null;
        };
        Update: {
          comedian_id?: string | null;
          created_at?: string;
          datetime?: string | null;
          deleted_at?: string | null;
          flags?: Database["public"]["Enums"]["ticket_flags"][] | null;
          id?: string;
          is_sold_out?: boolean | null;
          location?: string;
          metadata_text?: string | null;
          poster_img?: string | null;
          presale_code?: string | null;
          published_at?: string | null;
          ticket_link?: string;
          title?: string | null;
          tixologi_event_id?: string | null;
          venue?: string;
          venue_id?: string;
          vip_ticket_link?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_shows_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "comedian_shows_venue_id_fkey";
            columns: ["venue_id"];
            isOneToOne: false;
            referencedRelation: "venues";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_shows_duplicate: {
        Row: {
          created_at: string;
          datetime: string | null;
          deprecated_comedian_id: string;
          flags: Database["public"]["Enums"]["ticket_flags"][] | null;
          id: string;
          is_sold_out: boolean | null;
          location: string;
          metadata_text: string | null;
          poster_img: string | null;
          presale_code: string | null;
          ticket_link: string;
          title: string | null;
          tixologi_event_id: string | null;
          venue: string;
          venue_id: string;
          vip_ticket_link: string | null;
        };
        Insert: {
          created_at?: string;
          datetime?: string | null;
          deprecated_comedian_id: string;
          flags?: Database["public"]["Enums"]["ticket_flags"][] | null;
          id?: string;
          is_sold_out?: boolean | null;
          location: string;
          metadata_text?: string | null;
          poster_img?: string | null;
          presale_code?: string | null;
          ticket_link: string;
          title?: string | null;
          tixologi_event_id?: string | null;
          venue: string;
          venue_id: string;
          vip_ticket_link?: string | null;
        };
        Update: {
          created_at?: string;
          datetime?: string | null;
          deprecated_comedian_id?: string;
          flags?: Database["public"]["Enums"]["ticket_flags"][] | null;
          id?: string;
          is_sold_out?: boolean | null;
          location?: string;
          metadata_text?: string | null;
          poster_img?: string | null;
          presale_code?: string | null;
          ticket_link?: string;
          title?: string | null;
          tixologi_event_id?: string | null;
          venue?: string;
          venue_id?: string;
          vip_ticket_link?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_shows_duplicate_comedian_id_fkey";
            columns: ["deprecated_comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "comedian_shows_duplicate_venue_id_fkey";
            columns: ["venue_id"];
            isOneToOne: false;
            referencedRelation: "venues";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_similarity_network: {
        Row: {
          comedian_id_a: string;
          comedian_id_b: string;
          computed_at: string;
          cosine_similarity: number;
          model_version: number;
          sync_date: string;
        };
        Insert: {
          comedian_id_a: string;
          comedian_id_b: string;
          computed_at: string;
          cosine_similarity: number;
          model_version: number;
          sync_date?: string;
        };
        Update: {
          comedian_id_a?: string;
          comedian_id_b?: string;
          computed_at?: string;
          cosine_similarity?: number;
          model_version?: number;
          sync_date?: string;
        };
        Relationships: [];
      };
      comedian_social_media_links: {
        Row: {
          comedian_id: string;
          created_at: string | null;
          letter: string | null;
          link: string;
          social_media_type: Database["public"]["Enums"]["social_media_type"];
        };
        Insert: {
          comedian_id: string;
          created_at?: string | null;
          letter?: string | null;
          link: string;
          social_media_type: Database["public"]["Enums"]["social_media_type"];
        };
        Update: {
          comedian_id?: string;
          created_at?: string | null;
          letter?: string | null;
          link?: string;
          social_media_type?: Database["public"]["Enums"]["social_media_type"];
        };
        Relationships: [
          {
            foreignKeyName: "comedian_social_media_links_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_stripe_customers: {
        Row: {
          created_at: string;
          stripe_customer_id: string;
          user_id: string;
        };
        Insert: {
          created_at?: string;
          stripe_customer_id: string;
          user_id: string;
        };
        Update: {
          created_at?: string;
          stripe_customer_id?: string;
          user_id?: string;
        };
        Relationships: [];
      };
      comedian_subscriptions: {
        Row: {
          comedian_id: string;
          id: number;
          price: number;
        };
        Insert: {
          comedian_id: string;
          id?: number;
          price: number;
        };
        Update: {
          comedian_id?: string;
          id?: number;
          price?: number;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_subscriptions_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_ticket_clicks_agg: {
        Row: {
          comedian_id: string;
          comedian_total_events: number | null;
          last_updated: string;
          referrer_data_growth: Json | null;
          referrer_data_totals: Json | null;
          state_growth: Json | null;
          state_totals: Json | null;
        };
        Insert: {
          comedian_id: string;
          comedian_total_events?: number | null;
          last_updated: string;
          referrer_data_growth?: Json | null;
          referrer_data_totals?: Json | null;
          state_growth?: Json | null;
          state_totals?: Json | null;
        };
        Update: {
          comedian_id?: string;
          comedian_total_events?: number | null;
          last_updated?: string;
          referrer_data_growth?: Json | null;
          referrer_data_totals?: Json | null;
          state_growth?: Json | null;
          state_totals?: Json | null;
        };
        Relationships: [];
      };
      comedian_tickets_lookback_agg_table: {
        Row: {
          ComedianId: string;
          day_of_week_counts: Json | null;
          referrer_counts: Json | null;
          total_counts: Json | null;
          ts: string | null;
          venue_counts: Json | null;
        };
        Insert: {
          ComedianId: string;
          day_of_week_counts?: Json | null;
          referrer_counts?: Json | null;
          total_counts?: Json | null;
          ts?: string | null;
          venue_counts?: Json | null;
        };
        Update: {
          ComedianId?: string;
          day_of_week_counts?: Json | null;
          referrer_counts?: Json | null;
          total_counts?: Json | null;
          ts?: string | null;
          venue_counts?: Json | null;
        };
        Relationships: [];
      };
      comedian_tm_attraction_id: {
        Row: {
          attraction_id: string | null;
          comedian_id: string | null;
          name: string | null;
        };
        Insert: {
          attraction_id?: string | null;
          comedian_id?: string | null;
          name?: string | null;
        };
        Update: {
          attraction_id?: string | null;
          comedian_id?: string | null;
          name?: string | null;
        };
        Relationships: [];
      };
      comedian_tm_events: {
        Row: {
          attraction_id: string | null;
          buy_url: string | null;
          city: string | null;
          comedian_id: string | null;
          comedian_name: string | null;
          country: string | null;
          currency: string | null;
          event_date: string | null;
          event_id: string | null;
          event_name: string | null;
          event_time: string | null;
          price_max: number | null;
          price_min: number | null;
          state: string | null;
          ticket_limit: number | null;
          ticket_status: string | null;
          venue_name: string | null;
        };
        Insert: {
          attraction_id?: string | null;
          buy_url?: string | null;
          city?: string | null;
          comedian_id?: string | null;
          comedian_name?: string | null;
          country?: string | null;
          currency?: string | null;
          event_date?: string | null;
          event_id?: string | null;
          event_name?: string | null;
          event_time?: string | null;
          price_max?: number | null;
          price_min?: number | null;
          state?: string | null;
          ticket_limit?: number | null;
          ticket_status?: string | null;
          venue_name?: string | null;
        };
        Update: {
          attraction_id?: string | null;
          buy_url?: string | null;
          city?: string | null;
          comedian_id?: string | null;
          comedian_name?: string | null;
          country?: string | null;
          currency?: string | null;
          event_date?: string | null;
          event_id?: string | null;
          event_name?: string | null;
          event_time?: string | null;
          price_max?: number | null;
          price_min?: number | null;
          state?: string | null;
          ticket_limit?: number | null;
          ticket_status?: string | null;
          venue_name?: string | null;
        };
        Relationships: [];
      };
      comedian_video_uploads: {
        Row: {
          collection_id: string | null;
          comedian_id: string;
          created_at: string | null;
          description: string | null;
          id: string;
          is_audio: boolean;
          is_pending: boolean;
          last_state: string | null;
          make_featured: boolean | null;
          poster_img_with_titles: string | null;
          preview_img: string | null;
          preview_video_id: string | null;
          price: number;
          published_at: string | null;
          required_subscription_tier: number | null;
          requires_subscription: boolean;
          runtime: number;
          slug: string;
          small_preview_img: string | null;
          subscription_product_id: string | null;
          title: string;
          uploading_user_id: string;
          video_id: string | null;
        };
        Insert: {
          collection_id?: string | null;
          comedian_id: string;
          created_at?: string | null;
          description?: string | null;
          id?: string;
          is_audio?: boolean;
          is_pending?: boolean;
          last_state?: string | null;
          make_featured?: boolean | null;
          poster_img_with_titles?: string | null;
          preview_img?: string | null;
          preview_video_id?: string | null;
          price?: number;
          published_at?: string | null;
          required_subscription_tier?: number | null;
          requires_subscription?: boolean;
          runtime?: number;
          slug: string;
          small_preview_img?: string | null;
          subscription_product_id?: string | null;
          title?: string;
          uploading_user_id: string;
          video_id?: string | null;
        };
        Update: {
          collection_id?: string | null;
          comedian_id?: string;
          created_at?: string | null;
          description?: string | null;
          id?: string;
          is_audio?: boolean;
          is_pending?: boolean;
          last_state?: string | null;
          make_featured?: boolean | null;
          poster_img_with_titles?: string | null;
          preview_img?: string | null;
          preview_video_id?: string | null;
          price?: number;
          published_at?: string | null;
          required_subscription_tier?: number | null;
          requires_subscription?: boolean;
          runtime?: number;
          slug?: string;
          small_preview_img?: string | null;
          subscription_product_id?: string | null;
          title?: string;
          uploading_user_id?: string;
          video_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_video_uploads_collection_id_fkey";
            columns: ["collection_id"];
            isOneToOne: false;
            referencedRelation: "collections";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "comedian_video_uploads_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "comedian_video_uploads_subscription_product_id_fkey";
            columns: ["subscription_product_id"];
            isOneToOne: false;
            referencedRelation: "products";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_weekly_digest_runs: {
        Row: {
          ai_model: string | null;
          ai_tip: string | null;
          buyers_prior: number;
          buyers_total: number;
          cohort_median_direct: number;
          comedian_id: string;
          comedian_name: string;
          comedian_slug: string;
          created_at: string;
          direct_share: number;
          follow_source_breakdown: Json;
          id: string;
          new_follows_gross: number;
          new_follows_gross_prior: number;
          new_follows_net: number;
          payload: Json;
          sent_at: string | null;
          state: string;
          ticket_source_breakdown: Json;
          total_followers: number;
          week_start: string;
        };
        Insert: {
          ai_model?: string | null;
          ai_tip?: string | null;
          buyers_prior?: number;
          buyers_total?: number;
          cohort_median_direct?: number;
          comedian_id: string;
          comedian_name: string;
          comedian_slug: string;
          created_at?: string;
          direct_share?: number;
          follow_source_breakdown?: Json;
          id?: string;
          new_follows_gross?: number;
          new_follows_gross_prior?: number;
          new_follows_net?: number;
          payload?: Json;
          sent_at?: string | null;
          state: string;
          ticket_source_breakdown?: Json;
          total_followers?: number;
          week_start: string;
        };
        Update: {
          ai_model?: string | null;
          ai_tip?: string | null;
          buyers_prior?: number;
          buyers_total?: number;
          cohort_median_direct?: number;
          comedian_id?: string;
          comedian_name?: string;
          comedian_slug?: string;
          created_at?: string;
          direct_share?: number;
          follow_source_breakdown?: Json;
          id?: string;
          new_follows_gross?: number;
          new_follows_gross_prior?: number;
          new_follows_net?: number;
          payload?: Json;
          sent_at?: string | null;
          state?: string;
          ticket_source_breakdown?: Json;
          total_followers?: number;
          week_start?: string;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_weekly_digest_runs_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_yt_channel_map: {
        Row: {
          channel_ids: Json | null;
          channel_names: Json | null;
          comedian_id: string;
          name: string | null;
        };
        Insert: {
          channel_ids?: Json | null;
          channel_names?: Json | null;
          comedian_id: string;
          name?: string | null;
        };
        Update: {
          channel_ids?: Json | null;
          channel_names?: Json | null;
          comedian_id?: string;
          name?: string | null;
        };
        Relationships: [];
      };
      comedian_yt_channel_metrics: {
        Row: {
          channel_banner_url: string | null;
          channel_default_language: string | null;
          channel_description: string | null;
          channel_id: string;
          channel_localized_description: string | null;
          channel_localized_title: string | null;
          channel_name: string | null;
          channel_thumbnail_url: string | null;
          comedian_id: string;
          comment_count: number | null;
          country: string | null;
          custom_url: string | null;
          featured_channels: string | null;
          is_family_safe: boolean | null;
          keywords: string | null;
          last_updated: string | null;
          long_uploads_status: string | null;
          privacy_status: string | null;
          subscriber_count: number | null;
          topic_categories: string | null;
          uploads_playlist_id: string | null;
          video_count: number | null;
          view_count: number | null;
        };
        Insert: {
          channel_banner_url?: string | null;
          channel_default_language?: string | null;
          channel_description?: string | null;
          channel_id: string;
          channel_localized_description?: string | null;
          channel_localized_title?: string | null;
          channel_name?: string | null;
          channel_thumbnail_url?: string | null;
          comedian_id: string;
          comment_count?: number | null;
          country?: string | null;
          custom_url?: string | null;
          featured_channels?: string | null;
          is_family_safe?: boolean | null;
          keywords?: string | null;
          last_updated?: string | null;
          long_uploads_status?: string | null;
          privacy_status?: string | null;
          subscriber_count?: number | null;
          topic_categories?: string | null;
          uploads_playlist_id?: string | null;
          video_count?: number | null;
          view_count?: number | null;
        };
        Update: {
          channel_banner_url?: string | null;
          channel_default_language?: string | null;
          channel_description?: string | null;
          channel_id?: string;
          channel_localized_description?: string | null;
          channel_localized_title?: string | null;
          channel_name?: string | null;
          channel_thumbnail_url?: string | null;
          comedian_id?: string;
          comment_count?: number | null;
          country?: string | null;
          custom_url?: string | null;
          featured_channels?: string | null;
          is_family_safe?: boolean | null;
          keywords?: string | null;
          last_updated?: string | null;
          long_uploads_status?: string | null;
          privacy_status?: string | null;
          subscriber_count?: number | null;
          topic_categories?: string | null;
          uploads_playlist_id?: string | null;
          video_count?: number | null;
          view_count?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_yt_channel_metrics_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_yt_top_videos: {
        Row: {
          channel_id: string;
          comedian_id: string | null;
          comment_count: number | null;
          created_date: string | null;
          embed_link: string | null;
          last_updated: string | null;
          like_count: number | null;
          rank: number | null;
          title: string;
          video_id: string;
          view_count: number | null;
        };
        Insert: {
          channel_id: string;
          comedian_id?: string | null;
          comment_count?: number | null;
          created_date?: string | null;
          embed_link?: string | null;
          last_updated?: string | null;
          like_count?: number | null;
          rank?: number | null;
          title: string;
          video_id: string;
          view_count?: number | null;
        };
        Update: {
          channel_id?: string;
          comedian_id?: string | null;
          comment_count?: number | null;
          created_date?: string | null;
          embed_link?: string | null;
          last_updated?: string | null;
          like_count?: number | null;
          rank?: number | null;
          title?: string;
          video_id?: string;
          view_count?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_yt_top_videos_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_zipcodes: {
        Row: {
          comedian_id: string | null;
          created_at: string | null;
          id: number;
          zipcode_id: number | null;
        };
        Insert: {
          comedian_id?: string | null;
          created_at?: string | null;
          id?: number;
          zipcode_id?: number | null;
        };
        Update: {
          comedian_id?: string | null;
          created_at?: string | null;
          id?: number;
          zipcode_id?: number | null;
        };
        Relationships: [];
      };
      comedians: {
        Row: {
          about: string | null;
          about_img: string | null;
          created_at: string | null;
          display_name: string;
          display_name_short: string;
          featured_special_id: string | null;
          fts_token: unknown;
          fts_token_nostop: unknown;
          id: string;
          is_external_preview: boolean | null;
          is_punchup_verified: boolean;
          merch_store_link: string | null;
          published_at: string | null;
          shopify_merch_collection_id: string | null;
          signature_img: string | null;
          slug: string;
          tip_jar_link: string | null;
        };
        Insert: {
          about?: string | null;
          about_img?: string | null;
          created_at?: string | null;
          display_name: string;
          display_name_short: string;
          featured_special_id?: string | null;
          fts_token?: unknown;
          fts_token_nostop?: unknown;
          id?: string;
          is_external_preview?: boolean | null;
          is_punchup_verified?: boolean;
          merch_store_link?: string | null;
          published_at?: string | null;
          shopify_merch_collection_id?: string | null;
          signature_img?: string | null;
          slug: string;
          tip_jar_link?: string | null;
        };
        Update: {
          about?: string | null;
          about_img?: string | null;
          created_at?: string | null;
          display_name?: string;
          display_name_short?: string;
          featured_special_id?: string | null;
          fts_token?: unknown;
          fts_token_nostop?: unknown;
          id?: string;
          is_external_preview?: boolean | null;
          is_punchup_verified?: boolean;
          merch_store_link?: string | null;
          published_at?: string | null;
          shopify_merch_collection_id?: string | null;
          signature_img?: string | null;
          slug?: string;
          tip_jar_link?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "comedians_featured_special_id_fkey";
            columns: ["featured_special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      comment_approval_queue: {
        Row: {
          comedian_id: string;
          comment_id: string;
          comment_text: string;
          created_at: string | null;
          expires_at: string | null;
          filter_reasons: string[] | null;
          id: string;
          matched_words: string[] | null;
          perspective_scores: Json | null;
          review_note: string | null;
          reviewed_at: string | null;
          reviewed_by: string | null;
          severity: string | null;
          special_id: string | null;
          status: string | null;
          user_avatar_url: string | null;
          user_id: string;
          user_name: string | null;
          video_id: string | null;
        };
        Insert: {
          comedian_id: string;
          comment_id: string;
          comment_text: string;
          created_at?: string | null;
          expires_at?: string | null;
          filter_reasons?: string[] | null;
          id?: string;
          matched_words?: string[] | null;
          perspective_scores?: Json | null;
          review_note?: string | null;
          reviewed_at?: string | null;
          reviewed_by?: string | null;
          severity?: string | null;
          special_id?: string | null;
          status?: string | null;
          user_avatar_url?: string | null;
          user_id: string;
          user_name?: string | null;
          video_id?: string | null;
        };
        Update: {
          comedian_id?: string;
          comment_id?: string;
          comment_text?: string;
          created_at?: string | null;
          expires_at?: string | null;
          filter_reasons?: string[] | null;
          id?: string;
          matched_words?: string[] | null;
          perspective_scores?: Json | null;
          review_note?: string | null;
          reviewed_at?: string | null;
          reviewed_by?: string | null;
          severity?: string | null;
          special_id?: string | null;
          status?: string | null;
          user_avatar_url?: string | null;
          user_id?: string;
          user_name?: string | null;
          video_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "comment_approval_queue_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      comment_moderation_actions: {
        Row: {
          action: string;
          comment_id: string;
          created_at: string | null;
          id: string;
          metadata: Json | null;
          moderator_id: string;
          reason: string | null;
        };
        Insert: {
          action: string;
          comment_id: string;
          created_at?: string | null;
          id?: string;
          metadata?: Json | null;
          moderator_id: string;
          reason?: string | null;
        };
        Update: {
          action?: string;
          comment_id?: string;
          created_at?: string | null;
          id?: string;
          metadata?: Json | null;
          moderator_id?: string;
          reason?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "comment_moderation_actions_comment_id_fkey";
            columns: ["comment_id"];
            isOneToOne: false;
            referencedRelation: "comments";
            referencedColumns: ["id"];
          },
        ];
      };
      comment_reactions: {
        Row: {
          comment_id: string;
          created_at: string;
          id: string;
          reaction_type: string;
          user_id: string;
        };
        Insert: {
          comment_id: string;
          created_at?: string;
          id?: string;
          reaction_type: string;
          user_id: string;
        };
        Update: {
          comment_id?: string;
          created_at?: string;
          id?: string;
          reaction_type?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "comment_reactions_comment_id_fkey";
            columns: ["comment_id"];
            isOneToOne: false;
            referencedRelation: "comments";
            referencedColumns: ["id"];
          },
        ];
      };
      comments: {
        Row: {
          author_comedian_id: string | null;
          content: string;
          content_id: string;
          content_type: string;
          created_at: string;
          hidden_at: string | null;
          hidden_by: string | null;
          hidden_reason: string | null;
          id: string;
          is_hidden: boolean | null;
          parent_comment_id: string | null;
          updated_at: string | null;
          user_id: string;
        };
        Insert: {
          author_comedian_id?: string | null;
          content: string;
          content_id: string;
          content_type?: string;
          created_at?: string;
          hidden_at?: string | null;
          hidden_by?: string | null;
          hidden_reason?: string | null;
          id?: string;
          is_hidden?: boolean | null;
          parent_comment_id?: string | null;
          updated_at?: string | null;
          user_id: string;
        };
        Update: {
          author_comedian_id?: string | null;
          content?: string;
          content_id?: string;
          content_type?: string;
          created_at?: string;
          hidden_at?: string | null;
          hidden_by?: string | null;
          hidden_reason?: string | null;
          id?: string;
          is_hidden?: boolean | null;
          parent_comment_id?: string | null;
          updated_at?: string | null;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "comments_author_comedian_id_fkey";
            columns: ["author_comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "comments_content_id_fkey";
            columns: ["content_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "video_comments_user_id_fkey1";
            columns: ["user_id"];
            isOneToOne: false;
            referencedRelation: "users_extra";
            referencedColumns: ["id"];
          },
        ];
      };
      contact_lists: {
        Row: {
          comedian_id: string | null;
          created_at: string;
          detected_columns: string[] | null;
          email_contact_count: number | null;
          file_size_bytes: number | null;
          geo_contact_count: number | null;
          has_email_contacts: boolean | null;
          has_geo_data: boolean | null;
          has_phone_contacts: boolean | null;
          id: string;
          last_messaged: string | null;
          name: string;
          num_contacts: number | null;
          num_subscribed: number | null;
          original_filename: string | null;
          phone_contact_count: number | null;
          processed: boolean;
          status: Database["public"]["Enums"]["contact_lists_status"];
          storage_path: string | null;
          updated_at: string;
          user_id: string | null;
          venue_page_id: string | null;
        };
        Insert: {
          comedian_id?: string | null;
          created_at?: string;
          detected_columns?: string[] | null;
          email_contact_count?: number | null;
          file_size_bytes?: number | null;
          geo_contact_count?: number | null;
          has_email_contacts?: boolean | null;
          has_geo_data?: boolean | null;
          has_phone_contacts?: boolean | null;
          id?: string;
          last_messaged?: string | null;
          name: string;
          num_contacts?: number | null;
          num_subscribed?: number | null;
          original_filename?: string | null;
          phone_contact_count?: number | null;
          processed?: boolean;
          status?: Database["public"]["Enums"]["contact_lists_status"];
          storage_path?: string | null;
          updated_at?: string;
          user_id?: string | null;
          venue_page_id?: string | null;
        };
        Update: {
          comedian_id?: string | null;
          created_at?: string;
          detected_columns?: string[] | null;
          email_contact_count?: number | null;
          file_size_bytes?: number | null;
          geo_contact_count?: number | null;
          has_email_contacts?: boolean | null;
          has_geo_data?: boolean | null;
          has_phone_contacts?: boolean | null;
          id?: string;
          last_messaged?: string | null;
          name?: string;
          num_contacts?: number | null;
          num_subscribed?: number | null;
          original_filename?: string | null;
          phone_contact_count?: number | null;
          processed?: boolean;
          status?: Database["public"]["Enums"]["contact_lists_status"];
          storage_path?: string | null;
          updated_at?: string;
          user_id?: string | null;
          venue_page_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "contact_lists_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "contact_lists_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      contact_lists_contacts: {
        Row: {
          contact_id: string;
          created_at: string | null;
          list_id: string;
        };
        Insert: {
          contact_id: string;
          created_at?: string | null;
          list_id: string;
        };
        Update: {
          contact_id?: string;
          created_at?: string | null;
          list_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "contact_lists_contacts_contact_id_fkey";
            columns: ["contact_id"];
            isOneToOne: false;
            referencedRelation: "contacts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "contact_lists_contacts_list_id_fkey";
            columns: ["list_id"];
            isOneToOne: false;
            referencedRelation: "contact_lists";
            referencedColumns: ["id"];
          },
        ];
      };
      contacts: {
        Row: {
          city: string | null;
          comedian_id: string | null;
          country: string | null;
          created_at: string;
          email: string | null;
          first_name: string | null;
          full_name: string | null;
          id: string;
          is_on_guest_list: boolean;
          last_name: string | null;
          matched_user_id: string | null;
          phone: string | null;
          state: string | null;
          unsubscribed: boolean | null;
          unsubscribed_at: string | null;
          venue_page_id: string | null;
          zipcode: string | null;
        };
        Insert: {
          city?: string | null;
          comedian_id?: string | null;
          country?: string | null;
          created_at?: string;
          email?: string | null;
          first_name?: string | null;
          full_name?: string | null;
          id?: string;
          is_on_guest_list?: boolean;
          last_name?: string | null;
          matched_user_id?: string | null;
          phone?: string | null;
          state?: string | null;
          unsubscribed?: boolean | null;
          unsubscribed_at?: string | null;
          venue_page_id?: string | null;
          zipcode?: string | null;
        };
        Update: {
          city?: string | null;
          comedian_id?: string | null;
          country?: string | null;
          created_at?: string;
          email?: string | null;
          first_name?: string | null;
          full_name?: string | null;
          id?: string;
          is_on_guest_list?: boolean;
          last_name?: string | null;
          matched_user_id?: string | null;
          phone?: string | null;
          state?: string | null;
          unsubscribed?: boolean | null;
          unsubscribed_at?: string | null;
          venue_page_id?: string | null;
          zipcode?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "contacts_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "contacts_matched_user_id_fkey";
            columns: ["matched_user_id"];
            isOneToOne: false;
            referencedRelation: "users_extra";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "contacts_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      creator_moderation_settings: {
        Row: {
          created_at: string;
          creator_id: string;
          custom_settings: Json | null;
          id: string;
          moderation_level: Database["public"]["Enums"]["moderation_level"];
          updated_at: string;
          updated_by: string | null;
        };
        Insert: {
          created_at?: string;
          creator_id: string;
          custom_settings?: Json | null;
          id?: string;
          moderation_level?: Database["public"]["Enums"]["moderation_level"];
          updated_at?: string;
          updated_by?: string | null;
        };
        Update: {
          created_at?: string;
          creator_id?: string;
          custom_settings?: Json | null;
          id?: string;
          moderation_level?: Database["public"]["Enums"]["moderation_level"];
          updated_at?: string;
          updated_by?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "creator_moderation_settings_creator_id_fkey";
            columns: ["creator_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      creators_tos_acceptance: {
        Row: {
          created_at: string | null;
          last_accepted_at: string | null;
          user_id: string;
        };
        Insert: {
          created_at?: string | null;
          last_accepted_at?: string | null;
          user_id: string;
        };
        Update: {
          created_at?: string | null;
          last_accepted_at?: string | null;
          user_id?: string;
        };
        Relationships: [];
      };
      credit_transactions: {
        Row: {
          amount: number;
          artist_id: string;
          balance_after: number;
          created_at: string;
          credit_pool: Database["public"]["Enums"]["credit_pool_type"];
          id: string;
          reference: string | null;
          reference_type:
            | Database["public"]["Enums"]["credit_reference_type"]
            | null;
          transaction_type: Database["public"]["Enums"]["credit_transaction_type"];
          user_id: string | null;
        };
        Insert: {
          amount: number;
          artist_id: string;
          balance_after: number;
          created_at?: string;
          credit_pool: Database["public"]["Enums"]["credit_pool_type"];
          id?: string;
          reference?: string | null;
          reference_type?:
            | Database["public"]["Enums"]["credit_reference_type"]
            | null;
          transaction_type: Database["public"]["Enums"]["credit_transaction_type"];
          user_id?: string | null;
        };
        Update: {
          amount?: number;
          artist_id?: string;
          balance_after?: number;
          created_at?: string;
          credit_pool?: Database["public"]["Enums"]["credit_pool_type"];
          id?: string;
          reference?: string | null;
          reference_type?:
            | Database["public"]["Enums"]["credit_reference_type"]
            | null;
          transaction_type?: Database["public"]["Enums"]["credit_transaction_type"];
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "credit_transactions_artist_id_fkey";
            columns: ["artist_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      default_hidden_words: {
        Row: {
          added_at: string | null;
          added_by: string | null;
          category: string | null;
          id: string;
          severity: string | null;
          word: string;
        };
        Insert: {
          added_at?: string | null;
          added_by?: string | null;
          category?: string | null;
          id?: string;
          severity?: string | null;
          word: string;
        };
        Update: {
          added_at?: string | null;
          added_by?: string | null;
          category?: string | null;
          id?: string;
          severity?: string | null;
          word?: string;
        };
        Relationships: [];
      };
      email_suppressions: {
        Row: {
          created_at: string;
          email: string;
          source: string;
        };
        Insert: {
          created_at?: string;
          email: string;
          source?: string;
        };
        Update: {
          created_at?: string;
          email?: string;
          source?: string;
        };
        Relationships: [];
      };
      email_templates: {
        Row: {
          comedian_id: string | null;
          created_at: string;
          created_by: string | null;
          deleted_at: string | null;
          draft: Json;
          forked_from_id: string | null;
          id: string;
          name: string;
          published: Json | null;
          published_at: string | null;
          published_by: string | null;
          published_version: number;
          revision: number;
          starter_template_id: string | null;
          updated_at: string;
        };
        Insert: {
          comedian_id?: string | null;
          created_at?: string;
          created_by?: string | null;
          deleted_at?: string | null;
          draft: Json;
          forked_from_id?: string | null;
          id?: string;
          name: string;
          published?: Json | null;
          published_at?: string | null;
          published_by?: string | null;
          published_version?: number;
          revision?: number;
          starter_template_id?: string | null;
          updated_at?: string;
        };
        Update: {
          comedian_id?: string | null;
          created_at?: string;
          created_by?: string | null;
          deleted_at?: string | null;
          draft?: Json;
          forked_from_id?: string | null;
          id?: string;
          name?: string;
          published?: Json | null;
          published_at?: string | null;
          published_by?: string | null;
          published_version?: number;
          revision?: number;
          starter_template_id?: string | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "email_templates_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "email_templates_forked_from_id_fkey";
            columns: ["forked_from_id"];
            isOneToOne: false;
            referencedRelation: "email_templates";
            referencedColumns: ["id"];
          },
        ];
      };
      emails_analytics_archive: {
        Row: {
          clicked_link: boolean | null;
          complained_or_marked_spam: boolean | null;
          created_at: string;
          email: string | null;
          email_delivered_at: string | null;
          failed_delivery: boolean | null;
          opened_email: boolean | null;
          southpaw_job_id: string;
          unsubscribed: boolean | null;
          user_id: string;
        };
        Insert: {
          clicked_link?: boolean | null;
          complained_or_marked_spam?: boolean | null;
          created_at?: string;
          email?: string | null;
          email_delivered_at?: string | null;
          failed_delivery?: boolean | null;
          opened_email?: boolean | null;
          southpaw_job_id: string;
          unsubscribed?: boolean | null;
          user_id: string;
        };
        Update: {
          clicked_link?: boolean | null;
          complained_or_marked_spam?: boolean | null;
          created_at?: string;
          email?: string | null;
          email_delivered_at?: string | null;
          failed_delivery?: boolean | null;
          opened_email?: boolean | null;
          southpaw_job_id?: string;
          unsubscribed?: boolean | null;
          user_id?: string;
        };
        Relationships: [];
      };
      event_sales_daily: {
        Row: {
          event_id: number;
          event_name: string | null;
          sales_data: Json;
          updated_at: string;
        };
        Insert: {
          event_id: number;
          event_name?: string | null;
          sales_data: Json;
          updated_at: string;
        };
        Update: {
          event_id?: number;
          event_name?: string | null;
          sales_data?: Json;
          updated_at?: string;
        };
        Relationships: [];
      };
      event_sales_summary: {
        Row: {
          average_ticket_price: number;
          event_capacity: number;
          event_date: string;
          event_id: string;
          event_name: string;
          open: number;
          percent_remaining: number;
          promos_lifetime: Json | null;
          referrers_1d: Json;
          referrers_28d: Json;
          referrers_7d: Json;
          referrers_lifetime: Json;
          revenue_1d_amount: number;
          revenue_1d_growth: number;
          revenue_28d_amount: number;
          revenue_28d_growth: number;
          revenue_7d_amount: number;
          revenue_7d_growth: number;
          status: string;
          tickets_1d_count: number;
          tickets_1d_growth: number;
          tickets_28d_count: number;
          tickets_28d_growth: number;
          tickets_7d_count: number;
          tickets_7d_growth: number;
          tickets_breakdown: Json | null;
          timezone: string | null;
          total_revenue: number;
          total_tickets_sold: number;
          updated_at: string;
          venue_id: string;
        };
        Insert: {
          average_ticket_price: number;
          event_capacity: number;
          event_date: string;
          event_id: string;
          event_name: string;
          open: number;
          percent_remaining: number;
          promos_lifetime?: Json | null;
          referrers_1d?: Json;
          referrers_28d?: Json;
          referrers_7d?: Json;
          referrers_lifetime?: Json;
          revenue_1d_amount: number;
          revenue_1d_growth: number;
          revenue_28d_amount: number;
          revenue_28d_growth: number;
          revenue_7d_amount: number;
          revenue_7d_growth: number;
          status: string;
          tickets_1d_count: number;
          tickets_1d_growth: number;
          tickets_28d_count: number;
          tickets_28d_growth: number;
          tickets_7d_count: number;
          tickets_7d_growth: number;
          tickets_breakdown?: Json | null;
          timezone?: string | null;
          total_revenue: number;
          total_tickets_sold: number;
          updated_at: string;
          venue_id: string;
        };
        Update: {
          average_ticket_price?: number;
          event_capacity?: number;
          event_date?: string;
          event_id?: string;
          event_name?: string;
          open?: number;
          percent_remaining?: number;
          promos_lifetime?: Json | null;
          referrers_1d?: Json;
          referrers_28d?: Json;
          referrers_7d?: Json;
          referrers_lifetime?: Json;
          revenue_1d_amount?: number;
          revenue_1d_growth?: number;
          revenue_28d_amount?: number;
          revenue_28d_growth?: number;
          revenue_7d_amount?: number;
          revenue_7d_growth?: number;
          status?: string;
          tickets_1d_count?: number;
          tickets_1d_growth?: number;
          tickets_28d_count?: number;
          tickets_28d_growth?: number;
          tickets_7d_count?: number;
          tickets_7d_growth?: number;
          tickets_breakdown?: Json | null;
          timezone?: string | null;
          total_revenue?: number;
          total_tickets_sold?: number;
          updated_at?: string;
          venue_id?: string;
        };
        Relationships: [];
      };
      event_tickets_agg_daily: {
        Row: {
          comedian_id: string | null;
          count: number | null;
          ds: string | null;
        };
        Insert: {
          comedian_id?: string | null;
          count?: number | null;
          ds?: string | null;
        };
        Update: {
          comedian_id?: string | null;
          count?: number | null;
          ds?: string | null;
        };
        Relationships: [];
      };
      events_clicked_play: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          comedian_id: string | null;
          distinct_id: string | null;
          event: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          Source: string | null;
          special_id: string | null;
          specialName: string | null;
          time: string | null;
          timestamp: string | null;
          utm_campaign: string | null;
          utm_medium: string | null;
          utm_source: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          Source?: string | null;
          special_id?: string | null;
          specialName?: string | null;
          time?: string | null;
          timestamp?: string | null;
          utm_campaign?: string | null;
          utm_medium?: string | null;
          utm_source?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          Source?: string | null;
          special_id?: string | null;
          specialName?: string | null;
          time?: string | null;
          timestamp?: string | null;
          utm_campaign?: string | null;
          utm_medium?: string | null;
          utm_source?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "events_clicked_play_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      events_clicked_play_specials_agg: {
        Row: {
          last_event_ts: string;
          special_id: string;
          total_clicks: number;
        };
        Insert: {
          last_event_ts?: string;
          special_id: string;
          total_clicks?: number;
        };
        Update: {
          last_event_ts?: string;
          special_id?: string;
          total_clicks?: number;
        };
        Relationships: [
          {
            foreignKeyName: "events_clicked_play_specials_agg_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: true;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      events_followed_comedian: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $region: string | null;
          ComedianId: string | null;
          distinct_id: string | null;
          event: string | null;
          event_1: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          Source: string | null;
          time: string | null;
          timestamp: string | null;
          utm_campaign: string | null;
          utm_medium: string | null;
          utm_source: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $region?: string | null;
          ComedianId?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          event_1?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          Source?: string | null;
          time?: string | null;
          timestamp?: string | null;
          utm_campaign?: string | null;
          utm_medium?: string | null;
          utm_source?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $region?: string | null;
          ComedianId?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          event_1?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          Source?: string | null;
          time?: string | null;
          timestamp?: string | null;
          utm_campaign?: string | null;
          utm_medium?: string | null;
          utm_source?: string | null;
        };
        Relationships: [];
      };
      events_followed_comedian_total_agg: {
        Row: {
          comedian_id: string;
          last_event_ts: string;
          total_follows: number;
        };
        Insert: {
          comedian_id: string;
          last_event_ts?: string;
          total_follows?: number;
        };
        Update: {
          comedian_id?: string;
          last_event_ts?: string;
          total_follows?: number;
        };
        Relationships: [
          {
            foreignKeyName: "events_followed_comedian_total_agg_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      events_followed_venue: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $os: string | null;
          $region: string | null;
          distinct_id: string | null;
          event: string | null;
          followLocation: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          mp_lib: string | null;
          Source: string | null;
          time: string | null;
          timestamp: string | null;
          utm_campaign: string | null;
          utm_medium: string | null;
          utm_source: string | null;
          VenueId: string;
          venueSlug: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $os?: string | null;
          $region?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          followLocation?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          mp_lib?: string | null;
          Source?: string | null;
          time?: string | null;
          timestamp?: string | null;
          utm_campaign?: string | null;
          utm_medium?: string | null;
          utm_source?: string | null;
          VenueId: string;
          venueSlug?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $os?: string | null;
          $region?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          followLocation?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          mp_lib?: string | null;
          Source?: string | null;
          time?: string | null;
          timestamp?: string | null;
          utm_campaign?: string | null;
          utm_medium?: string | null;
          utm_source?: string | null;
          VenueId?: string;
          venueSlug?: string | null;
        };
        Relationships: [];
      };
      events_tickets_merged: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $os: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          ComedianId: string | null;
          ComedianName: string | null;
          distinct_id: string | null;
          event: string | null;
          fbclid: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: number | null;
          longitude: number | null;
          mp_country_code: string | null;
          mp_lib: string | null;
          ShowDate: string | null;
          ShowId: string | null;
          ShowVenue: string | null;
          Source: string | null;
          time: number | null;
          timestamp: string | null;
          utm_campaign: string | null;
          utm_medium: string | null;
          utm_source: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $os?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          ComedianId?: string | null;
          ComedianName?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          fbclid?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: number | null;
          longitude?: number | null;
          mp_country_code?: string | null;
          mp_lib?: string | null;
          ShowDate?: string | null;
          ShowId?: string | null;
          ShowVenue?: string | null;
          Source?: string | null;
          time?: number | null;
          timestamp?: string | null;
          utm_campaign?: string | null;
          utm_medium?: string | null;
          utm_source?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $os?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          ComedianId?: string | null;
          ComedianName?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          fbclid?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: number | null;
          longitude?: number | null;
          mp_country_code?: string | null;
          mp_lib?: string | null;
          ShowDate?: string | null;
          ShowId?: string | null;
          ShowVenue?: string | null;
          Source?: string | null;
          time?: number | null;
          timestamp?: string | null;
          utm_campaign?: string | null;
          utm_medium?: string | null;
          utm_source?: string | null;
        };
        Relationships: [];
      };
      events_video_session_started: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          comedian_id: string | null;
          distinct_id: string | null;
          event: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          special_id: string | null;
          special_name: string | null;
          time: string | null;
          timestamp: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "events_video_session_started_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      events_video_viewed_10s: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          comedian_id: string | null;
          distinct_id: string | null;
          event: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          special_id: string | null;
          special_name: string | null;
          time: string | null;
          timestamp: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "events_video_viewed_10s_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      events_video_viewed_30s: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          comedian_id: string | null;
          distinct_id: string | null;
          event: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          special_id: string | null;
          special_name: string | null;
          time: string | null;
          timestamp: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "events_video_viewed_30s_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      events_video_viewed_3s: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          comedian_id: string | null;
          distinct_id: string | null;
          event: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          special_id: string | null;
          special_name: string | null;
          time: string | null;
          timestamp: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "events_video_viewed_3s_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      external_emails_analytics_archive: {
        Row: {
          clicked_link: boolean | null;
          complained_or_marked_spam: boolean | null;
          contact_id: string;
          created_at: string;
          email: string | null;
          email_delivered_at: string | null;
          failed_delivery: boolean | null;
          opened_email: boolean | null;
          southpaw_job_id: string;
          unsubscribed: boolean | null;
        };
        Insert: {
          clicked_link?: boolean | null;
          complained_or_marked_spam?: boolean | null;
          contact_id: string;
          created_at?: string;
          email?: string | null;
          email_delivered_at?: string | null;
          failed_delivery?: boolean | null;
          opened_email?: boolean | null;
          southpaw_job_id: string;
          unsubscribed?: boolean | null;
        };
        Update: {
          clicked_link?: boolean | null;
          complained_or_marked_spam?: boolean | null;
          contact_id?: string;
          created_at?: string;
          email?: string | null;
          email_delivered_at?: string | null;
          failed_delivery?: boolean | null;
          opened_email?: boolean | null;
          southpaw_job_id?: string;
          unsubscribed?: boolean | null;
        };
        Relationships: [
          {
            foreignKeyName: "external_emails_analytics_archive_contact_id_fkey";
            columns: ["contact_id"];
            isOneToOne: false;
            referencedRelation: "contacts";
            referencedColumns: ["id"];
          },
        ];
      };
      feature_announcements: {
        Row: {
          body: string | null;
          created_at: string;
          id: string;
          logo_id: string | null;
          slug: string;
          title: string | null;
        };
        Insert: {
          body?: string | null;
          created_at?: string;
          id?: string;
          logo_id?: string | null;
          slug: string;
          title?: string | null;
        };
        Update: {
          body?: string | null;
          created_at?: string;
          id?: string;
          logo_id?: string | null;
          slug?: string;
          title?: string | null;
        };
        Relationships: [];
      };
      feature_announcements_user_visits: {
        Row: {
          created_at: string;
          feature_announcement_id: string;
          user_id: string;
        };
        Insert: {
          created_at?: string;
          feature_announcement_id?: string;
          user_id?: string;
        };
        Update: {
          created_at?: string;
          feature_announcement_id?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "feature_announcements_user_visits_feature_announcement_id_fkey";
            columns: ["feature_announcement_id"];
            isOneToOne: false;
            referencedRelation: "feature_announcements";
            referencedColumns: ["id"];
          },
        ];
      };
      gift_card_audit_log: {
        Row: {
          action: string;
          created_at: string;
          gift_card_id: string | null;
          id: string;
          ip_address: unknown;
          metadata: Json | null;
          performed_by: string | null;
          user_agent: string | null;
        };
        Insert: {
          action: string;
          created_at?: string;
          gift_card_id?: string | null;
          id?: string;
          ip_address?: unknown;
          metadata?: Json | null;
          performed_by?: string | null;
          user_agent?: string | null;
        };
        Update: {
          action?: string;
          created_at?: string;
          gift_card_id?: string | null;
          id?: string;
          ip_address?: unknown;
          metadata?: Json | null;
          performed_by?: string | null;
          user_agent?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "gift_card_audit_log_gift_card_id_fkey";
            columns: ["gift_card_id"];
            isOneToOne: false;
            referencedRelation: "gift_cards";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "gift_card_audit_log_gift_card_id_fkey";
            columns: ["gift_card_id"];
            isOneToOne: false;
            referencedRelation: "gift_cards_with_balance";
            referencedColumns: ["id"];
          },
        ];
      };
      gift_card_configs: {
        Row: {
          created_at: string;
          custom_amount_enabled: boolean;
          default_expiration_days: number | null;
          denominations: number[];
          enabled: boolean;
          id: string;
          max_amount: number | null;
          min_amount: number | null;
          toast_enabled: boolean;
          toast_location_guid: string | null;
          updated_at: string;
          venue_id: string;
        };
        Insert: {
          created_at?: string;
          custom_amount_enabled?: boolean;
          default_expiration_days?: number | null;
          denominations?: number[];
          enabled?: boolean;
          id?: string;
          max_amount?: number | null;
          min_amount?: number | null;
          toast_enabled?: boolean;
          toast_location_guid?: string | null;
          updated_at?: string;
          venue_id: string;
        };
        Update: {
          created_at?: string;
          custom_amount_enabled?: boolean;
          default_expiration_days?: number | null;
          denominations?: number[];
          enabled?: boolean;
          id?: string;
          max_amount?: number | null;
          min_amount?: number | null;
          toast_enabled?: boolean;
          toast_location_guid?: string | null;
          updated_at?: string;
          venue_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "gift_card_configs_venue_id_fkey";
            columns: ["venue_id"];
            isOneToOne: true;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      gift_card_transactions: {
        Row: {
          amount: number;
          created_at: string;
          description: string | null;
          gift_card_id: string;
          id: string;
          metadata: Json | null;
          reference_id: string | null;
          stripe_payment_intent_id: string | null;
          type: Database["public"]["Enums"]["gift_card_transaction_type"];
        };
        Insert: {
          amount: number;
          created_at?: string;
          description?: string | null;
          gift_card_id: string;
          id: string;
          metadata?: Json | null;
          reference_id?: string | null;
          stripe_payment_intent_id?: string | null;
          type: Database["public"]["Enums"]["gift_card_transaction_type"];
        };
        Update: {
          amount?: number;
          created_at?: string;
          description?: string | null;
          gift_card_id?: string;
          id?: string;
          metadata?: Json | null;
          reference_id?: string | null;
          stripe_payment_intent_id?: string | null;
          type?: Database["public"]["Enums"]["gift_card_transaction_type"];
        };
        Relationships: [
          {
            foreignKeyName: "gift_card_transactions_gift_card_id_fkey";
            columns: ["gift_card_id"];
            isOneToOne: false;
            referencedRelation: "gift_cards";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "gift_card_transactions_gift_card_id_fkey";
            columns: ["gift_card_id"];
            isOneToOne: false;
            referencedRelation: "gift_cards_with_balance";
            referencedColumns: ["id"];
          },
        ];
      };
      gift_cards: {
        Row: {
          code: string;
          created_at: string;
          disabled_at: string | null;
          expires_at: string | null;
          id: string;
          initial_balance: number;
          message: string | null;
          purchased_at: string | null;
          recipient_email: string | null;
          recipient_name: string | null;
          sender_email: string | null;
          sender_name: string | null;
          status: Database["public"]["Enums"]["gift_card_status"];
          stripe_payment_intent_id: string | null;
          toast_card_id: string | null;
          venue_id: string;
        };
        Insert: {
          code: string;
          created_at?: string;
          disabled_at?: string | null;
          expires_at?: string | null;
          id: string;
          initial_balance: number;
          message?: string | null;
          purchased_at?: string | null;
          recipient_email?: string | null;
          recipient_name?: string | null;
          sender_email?: string | null;
          sender_name?: string | null;
          status?: Database["public"]["Enums"]["gift_card_status"];
          stripe_payment_intent_id?: string | null;
          toast_card_id?: string | null;
          venue_id: string;
        };
        Update: {
          code?: string;
          created_at?: string;
          disabled_at?: string | null;
          expires_at?: string | null;
          id?: string;
          initial_balance?: number;
          message?: string | null;
          purchased_at?: string | null;
          recipient_email?: string | null;
          recipient_name?: string | null;
          sender_email?: string | null;
          sender_name?: string | null;
          status?: Database["public"]["Enums"]["gift_card_status"];
          stripe_payment_intent_id?: string | null;
          toast_card_id?: string | null;
          venue_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "gift_cards_venue_id_fkey";
            columns: ["venue_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      global_fallback_comedian_recommendations: {
        Row: {
          comedian_id: string;
          comedian_name: string;
          rank: number | null;
          sync_date: string | null;
          weighted_strength: number | null;
        };
        Insert: {
          comedian_id?: string;
          comedian_name: string;
          rank?: number | null;
          sync_date?: string | null;
          weighted_strength?: number | null;
        };
        Update: {
          comedian_id?: string;
          comedian_name?: string;
          rank?: number | null;
          sync_date?: string | null;
          weighted_strength?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "global_fallback_comedian_recommendations_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      global_fallback_specials_recommendations: {
        Row: {
          rank: number | null;
          special_id: string;
          special_name: string;
          sync_date: string | null;
          weighted_strength: number | null;
        };
        Insert: {
          rank?: number | null;
          special_id?: string;
          special_name: string;
          sync_date?: string | null;
          weighted_strength?: number | null;
        };
        Update: {
          rank?: number | null;
          special_id?: string;
          special_name?: string;
          sync_date?: string | null;
          weighted_strength?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "global_fallback_specials_recommendations_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: true;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      groups_of_shows: {
        Row: {
          associated_shows: string[];
          created_at: string;
          id: string;
        };
        Insert: {
          associated_shows?: string[];
          created_at?: string;
          id?: string;
        };
        Update: {
          associated_shows?: string[];
          created_at?: string;
          id?: string;
        };
        Relationships: [];
      };
      helium_shows: {
        Row: {
          age_restriction: string | null;
          comedian_name: string | null;
          created_at: string | null;
          datetime: string | null;
          description: string | null;
          doors_time: string | null;
          event_tags: string[] | null;
          helium_show_id: string;
          id: string;
          is_parsed_successfully: boolean | null;
          is_sold_out: boolean | null;
          last_scraped_at: string | null;
          parsing_errors: string | null;
          poster_img: string | null;
          pricing_tiers: Json | null;
          show_policies: Json | null;
          show_time: string | null;
          source_url: string;
          ticket_link: string;
          tickets: Json | null;
          tickets_sold: number | null;
          title: string;
          total_capacity: number | null;
          updated_at: string | null;
          venue_address: string | null;
          venue_city: string | null;
          venue_id: string | null;
          venue_location: string;
          venue_name: string;
          venue_state: string | null;
        };
        Insert: {
          age_restriction?: string | null;
          comedian_name?: string | null;
          created_at?: string | null;
          datetime?: string | null;
          description?: string | null;
          doors_time?: string | null;
          event_tags?: string[] | null;
          helium_show_id: string;
          id?: string;
          is_parsed_successfully?: boolean | null;
          is_sold_out?: boolean | null;
          last_scraped_at?: string | null;
          parsing_errors?: string | null;
          poster_img?: string | null;
          pricing_tiers?: Json | null;
          show_policies?: Json | null;
          show_time?: string | null;
          source_url: string;
          ticket_link: string;
          tickets?: Json | null;
          tickets_sold?: number | null;
          title: string;
          total_capacity?: number | null;
          updated_at?: string | null;
          venue_address?: string | null;
          venue_city?: string | null;
          venue_id?: string | null;
          venue_location: string;
          venue_name: string;
          venue_state?: string | null;
        };
        Update: {
          age_restriction?: string | null;
          comedian_name?: string | null;
          created_at?: string | null;
          datetime?: string | null;
          description?: string | null;
          doors_time?: string | null;
          event_tags?: string[] | null;
          helium_show_id?: string;
          id?: string;
          is_parsed_successfully?: boolean | null;
          is_sold_out?: boolean | null;
          last_scraped_at?: string | null;
          parsing_errors?: string | null;
          poster_img?: string | null;
          pricing_tiers?: Json | null;
          show_policies?: Json | null;
          show_time?: string | null;
          source_url?: string;
          ticket_link?: string;
          tickets?: Json | null;
          tickets_sold?: number | null;
          title?: string;
          total_capacity?: number | null;
          updated_at?: string | null;
          venue_address?: string | null;
          venue_city?: string | null;
          venue_id?: string | null;
          venue_location?: string;
          venue_name?: string;
          venue_state?: string | null;
        };
        Relationships: [];
      };
      historical_user_zipcodes: {
        Row: {
          archived_at: string | null;
          country_code: string | null;
          from_ip: boolean | null;
          geohash_4: string | null;
          id: number;
          is_eu: boolean | null;
          original_created_at: string | null;
          original_id: number;
          user_id: string;
          zipcode: string | null;
        };
        Insert: {
          archived_at?: string | null;
          country_code?: string | null;
          from_ip?: boolean | null;
          geohash_4?: string | null;
          id?: number;
          is_eu?: boolean | null;
          original_created_at?: string | null;
          original_id: number;
          user_id: string;
          zipcode?: string | null;
        };
        Update: {
          archived_at?: string | null;
          country_code?: string | null;
          from_ip?: boolean | null;
          geohash_4?: string | null;
          id?: number;
          is_eu?: boolean | null;
          original_created_at?: string | null;
          original_id?: number;
          user_id?: string;
          zipcode?: string | null;
        };
        Relationships: [];
      };
      impersonation_sessions: {
        Row: {
          admin_email: string;
          admin_user_id: string;
          end_reason: string | null;
          ended_at: string | null;
          session_id: string;
          started_at: string;
          target_user_id: string;
        };
        Insert: {
          admin_email: string;
          admin_user_id: string;
          end_reason?: string | null;
          ended_at?: string | null;
          session_id: string;
          started_at?: string;
          target_user_id: string;
        };
        Update: {
          admin_email?: string;
          admin_user_id?: string;
          end_reason?: string | null;
          ended_at?: string | null;
          session_id?: string;
          started_at?: string;
          target_user_id?: string;
        };
        Relationships: [];
      };
      ingest_sync_cursors: {
        Row: {
          cursor: Json;
          last_error: string | null;
          last_run_at: string | null;
          locked_until: string | null;
          source: string;
          updated_at: string;
        };
        Insert: {
          cursor?: Json;
          last_error?: string | null;
          last_run_at?: string | null;
          locked_until?: string | null;
          source: string;
          updated_at?: string;
        };
        Update: {
          cursor?: Json;
          last_error?: string | null;
          last_run_at?: string | null;
          locked_until?: string | null;
          source?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      launch_candidates: {
        Row: {
          attempts: number;
          confidence: number | null;
          created_at: string;
          dismiss_reason: string | null;
          id: string;
          is_launch: boolean | null;
          media: Json;
          media_pending: boolean;
          observed_at: string;
          proposal: Json | null;
          rationale: string | null;
          release_id: string | null;
          reviewed_at: string | null;
          reviewed_by: string | null;
          source: string;
          source_key: string | null;
          source_ref: string | null;
          source_text: string;
          state: string;
          updated_at: string;
        };
        Insert: {
          attempts?: number;
          confidence?: number | null;
          created_at?: string;
          dismiss_reason?: string | null;
          id?: string;
          is_launch?: boolean | null;
          media?: Json;
          media_pending?: boolean;
          observed_at?: string;
          proposal?: Json | null;
          rationale?: string | null;
          release_id?: string | null;
          reviewed_at?: string | null;
          reviewed_by?: string | null;
          source: string;
          source_key?: string | null;
          source_ref?: string | null;
          source_text?: string;
          state?: string;
          updated_at?: string;
        };
        Update: {
          attempts?: number;
          confidence?: number | null;
          created_at?: string;
          dismiss_reason?: string | null;
          id?: string;
          is_launch?: boolean | null;
          media?: Json;
          media_pending?: boolean;
          observed_at?: string;
          proposal?: Json | null;
          rationale?: string | null;
          release_id?: string | null;
          reviewed_at?: string | null;
          reviewed_by?: string | null;
          source?: string;
          source_key?: string | null;
          source_ref?: string | null;
          source_text?: string;
          state?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "launch_candidates_release_id_fkey";
            columns: ["release_id"];
            isOneToOne: false;
            referencedRelation: "product_releases";
            referencedColumns: ["id"];
          },
        ];
      };
      library_exclusion: {
        Row: {
          comedian_id: string | null;
          created_at: string;
          id: number;
        };
        Insert: {
          comedian_id?: string | null;
          created_at?: string;
          id?: number;
        };
        Update: {
          comedian_id?: string | null;
          created_at?: string;
          id?: number;
        };
        Relationships: [
          {
            foreignKeyName: "public_library_exclusion_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      livestream_vendor_accounts: {
        Row: {
          created_at: string;
          created_by: string | null;
          display_name: string;
          id: string;
          signing_key_id: string;
          signing_key_secret: string;
          vendor_type: Database["public"]["Enums"]["livestream_vendor"] | null;
        };
        Insert: {
          created_at?: string;
          created_by?: string | null;
          display_name: string;
          id?: string;
          signing_key_id: string;
          signing_key_secret: string;
          vendor_type?: Database["public"]["Enums"]["livestream_vendor"] | null;
        };
        Update: {
          created_at?: string;
          created_by?: string | null;
          display_name?: string;
          id?: string;
          signing_key_id?: string;
          signing_key_secret?: string;
          vendor_type?: Database["public"]["Enums"]["livestream_vendor"] | null;
        };
        Relationships: [];
      };
      livestreams: {
        Row: {
          comedians: string[];
          created_at: string;
          created_by: string | null;
          id: string;
          is_public_policy: boolean;
          start_time: string | null;
          state: Database["public"]["Enums"]["livestream_state"];
          title: string;
          vendor_account: string;
          vendor_type: Database["public"]["Enums"]["livestream_vendor"];
          vendor_video_id: string;
          venue_page_id: string | null;
        };
        Insert: {
          comedians?: string[];
          created_at?: string;
          created_by?: string | null;
          id?: string;
          is_public_policy: boolean;
          start_time?: string | null;
          state?: Database["public"]["Enums"]["livestream_state"];
          title: string;
          vendor_account: string;
          vendor_type: Database["public"]["Enums"]["livestream_vendor"];
          vendor_video_id: string;
          venue_page_id?: string | null;
        };
        Update: {
          comedians?: string[];
          created_at?: string;
          created_by?: string | null;
          id?: string;
          is_public_policy?: boolean;
          start_time?: string | null;
          state?: Database["public"]["Enums"]["livestream_state"];
          title?: string;
          vendor_account?: string;
          vendor_type?: Database["public"]["Enums"]["livestream_vendor"];
          vendor_video_id?: string;
          venue_page_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "livestreams_vendor_account_fkey";
            columns: ["vendor_account"];
            isOneToOne: false;
            referencedRelation: "livestream_vendor_accounts";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "livestreams_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      mailgun_southpaw_aggregated_metrics: {
        Row: {
          clicked: number;
          delivered: number;
          opened: number;
          southpaw_job_id: string;
          total_audience: number;
          updated_at: string;
        };
        Insert: {
          clicked?: number;
          delivered?: number;
          opened?: number;
          southpaw_job_id: string;
          total_audience?: number;
          updated_at?: string;
        };
        Update: {
          clicked?: number;
          delivered?: number;
          opened?: number;
          southpaw_job_id?: string;
          total_audience?: number;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "mailgun_southpaw_aggregated_metrics_southpaw_job_id_fkey";
            columns: ["southpaw_job_id"];
            isOneToOne: true;
            referencedRelation: "southpaw_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      mark_normand_special_email_audience_tbl: {
        Row: {
          country_code: string | null;
          email: string | null;
          user_id: string | null;
          zipcode: string | null;
        };
        Insert: {
          country_code?: string | null;
          email?: string | null;
          user_id?: string | null;
          zipcode?: string | null;
        };
        Update: {
          country_code?: string | null;
          email?: string | null;
          user_id?: string | null;
          zipcode?: string | null;
        };
        Relationships: [];
      };
      newsletter_issues: {
        Row: {
          approved_at: string | null;
          approved_by: string | null;
          audience_count: number | null;
          comedian_id: string;
          created_at: string;
          created_by: string | null;
          deleted_at: string | null;
          dispatch_attempts: number;
          dispatch_claimed_at: string | null;
          dispatch_error: string | null;
          doc: Json;
          id: string;
          planned_for: string | null;
          preview_text: string;
          revision: number;
          scheduled_for: string | null;
          send_at: string | null;
          sent_at: string | null;
          series_id: string | null;
          southpaw_job_id: string | null;
          started_at: string;
          status: Database["public"]["Enums"]["newsletter_issue_status"];
          subject: string;
          updated_at: string;
        };
        Insert: {
          approved_at?: string | null;
          approved_by?: string | null;
          audience_count?: number | null;
          comedian_id: string;
          created_at?: string;
          created_by?: string | null;
          deleted_at?: string | null;
          dispatch_attempts?: number;
          dispatch_claimed_at?: string | null;
          dispatch_error?: string | null;
          doc?: Json;
          id?: string;
          planned_for?: string | null;
          preview_text?: string;
          revision?: number;
          scheduled_for?: string | null;
          send_at?: string | null;
          sent_at?: string | null;
          series_id?: string | null;
          southpaw_job_id?: string | null;
          started_at?: string;
          status?: Database["public"]["Enums"]["newsletter_issue_status"];
          subject?: string;
          updated_at?: string;
        };
        Update: {
          approved_at?: string | null;
          approved_by?: string | null;
          audience_count?: number | null;
          comedian_id?: string;
          created_at?: string;
          created_by?: string | null;
          deleted_at?: string | null;
          dispatch_attempts?: number;
          dispatch_claimed_at?: string | null;
          dispatch_error?: string | null;
          doc?: Json;
          id?: string;
          planned_for?: string | null;
          preview_text?: string;
          revision?: number;
          scheduled_for?: string | null;
          send_at?: string | null;
          sent_at?: string | null;
          series_id?: string | null;
          southpaw_job_id?: string | null;
          started_at?: string;
          status?: Database["public"]["Enums"]["newsletter_issue_status"];
          subject?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "newsletter_issues_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "newsletter_issues_series_id_fkey";
            columns: ["series_id"];
            isOneToOne: false;
            referencedRelation: "newsletter_series";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "newsletter_issues_southpaw_job_id_fkey";
            columns: ["southpaw_job_id"];
            isOneToOne: false;
            referencedRelation: "southpaw_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      newsletter_series: {
        Row: {
          cadence: Json;
          color_index: number | null;
          comedian_id: string;
          created_at: string;
          created_by: string | null;
          deleted_at: string | null;
          edition_noun: string | null;
          enabled: boolean;
          ends: Json;
          id: string;
          last_nudge_dismissed_at: string | null;
          name: string;
          reminder_lead_time: string;
          revision: number;
          send_mode: string;
          sent_count: number;
          time_of_day: string;
          updated_at: string;
        };
        Insert: {
          cadence: Json;
          color_index?: number | null;
          comedian_id: string;
          created_at?: string;
          created_by?: string | null;
          deleted_at?: string | null;
          edition_noun?: string | null;
          enabled?: boolean;
          ends?: Json;
          id?: string;
          last_nudge_dismissed_at?: string | null;
          name?: string;
          reminder_lead_time?: string;
          revision?: number;
          send_mode?: string;
          sent_count?: number;
          time_of_day?: string;
          updated_at?: string;
        };
        Update: {
          cadence?: Json;
          color_index?: number | null;
          comedian_id?: string;
          created_at?: string;
          created_by?: string | null;
          deleted_at?: string | null;
          edition_noun?: string | null;
          enabled?: boolean;
          ends?: Json;
          id?: string;
          last_nudge_dismissed_at?: string | null;
          name?: string;
          reminder_lead_time?: string;
          revision?: number;
          send_mode?: string;
          sent_count?: number;
          time_of_day?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "newsletter_series_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      notification_batches: {
        Row: {
          aggregated_data: Json;
          attempts: number;
          completed_at: string | null;
          created_at: string;
          error_message: string | null;
          event_count: number;
          failed_count: number;
          group_key: string;
          id: string;
          notification_type_id: string;
          recipient_count: number;
          sent_count: number;
          status: string;
        };
        Insert: {
          aggregated_data?: Json;
          attempts?: number;
          completed_at?: string | null;
          created_at?: string;
          error_message?: string | null;
          event_count?: number;
          failed_count?: number;
          group_key: string;
          id?: string;
          notification_type_id: string;
          recipient_count?: number;
          sent_count?: number;
          status?: string;
        };
        Update: {
          aggregated_data?: Json;
          attempts?: number;
          completed_at?: string | null;
          created_at?: string;
          error_message?: string | null;
          event_count?: number;
          failed_count?: number;
          group_key?: string;
          id?: string;
          notification_type_id?: string;
          recipient_count?: number;
          sent_count?: number;
          status?: string;
        };
        Relationships: [
          {
            foreignKeyName: "notification_batches_notification_type_id_fkey";
            columns: ["notification_type_id"];
            isOneToOne: false;
            referencedRelation: "notification_types";
            referencedColumns: ["id"];
          },
        ];
      };
      notification_events: {
        Row: {
          batch_id: string | null;
          created_at: string;
          group_key: string;
          id: string;
          notification_type_id: string;
          status: string;
          trigger_data: Json;
        };
        Insert: {
          batch_id?: string | null;
          created_at?: string;
          group_key: string;
          id?: string;
          notification_type_id: string;
          status?: string;
          trigger_data?: Json;
        };
        Update: {
          batch_id?: string | null;
          created_at?: string;
          group_key?: string;
          id?: string;
          notification_type_id?: string;
          status?: string;
          trigger_data?: Json;
        };
        Relationships: [
          {
            foreignKeyName: "notification_events_batch_fk";
            columns: ["batch_id"];
            isOneToOne: false;
            referencedRelation: "notification_batches";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "notification_events_notification_type_id_fkey";
            columns: ["notification_type_id"];
            isOneToOne: false;
            referencedRelation: "notification_types";
            referencedColumns: ["id"];
          },
        ];
      };
      notification_history: {
        Row: {
          action_url: string | null;
          batch_id: string | null;
          body: string | null;
          channels: string[];
          comedian_id: string | null;
          courier_bulk_job_id: string | null;
          created_at: string;
          delivery_status: string;
          id: string;
          image_url: string | null;
          is_read: boolean;
          notification_type_id: string;
          payload: Json;
          read_at: string | null;
          scheduled_notification_id: string | null;
          title: string | null;
          user_id: string;
        };
        Insert: {
          action_url?: string | null;
          batch_id?: string | null;
          body?: string | null;
          channels?: string[];
          comedian_id?: string | null;
          courier_bulk_job_id?: string | null;
          created_at?: string;
          delivery_status?: string;
          id?: string;
          image_url?: string | null;
          is_read?: boolean;
          notification_type_id: string;
          payload?: Json;
          read_at?: string | null;
          scheduled_notification_id?: string | null;
          title?: string | null;
          user_id: string;
        };
        Update: {
          action_url?: string | null;
          batch_id?: string | null;
          body?: string | null;
          channels?: string[];
          comedian_id?: string | null;
          courier_bulk_job_id?: string | null;
          created_at?: string;
          delivery_status?: string;
          id?: string;
          image_url?: string | null;
          is_read?: boolean;
          notification_type_id?: string;
          payload?: Json;
          read_at?: string | null;
          scheduled_notification_id?: string | null;
          title?: string | null;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "notification_history_batch_id_fkey";
            columns: ["batch_id"];
            isOneToOne: false;
            referencedRelation: "notification_batches";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "notification_history_notification_type_id_fkey";
            columns: ["notification_type_id"];
            isOneToOne: false;
            referencedRelation: "notification_types";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "notification_history_scheduled_notification_fk";
            columns: ["scheduled_notification_id"];
            isOneToOne: false;
            referencedRelation: "scheduled_notifications";
            referencedColumns: ["id"];
          },
        ];
      };
      notification_preferences: {
        Row: {
          created_at: string;
          id: number;
          notification_type:
            | Database["public"]["Enums"]["notification_type"]
            | null;
          receive_notification: boolean | null;
          user_id: string;
        };
        Insert: {
          created_at?: string;
          id?: number;
          notification_type?:
            | Database["public"]["Enums"]["notification_type"]
            | null;
          receive_notification?: boolean | null;
          user_id?: string;
        };
        Update: {
          created_at?: string;
          id?: number;
          notification_type?:
            | Database["public"]["Enums"]["notification_type"]
            | null;
          receive_notification?: boolean | null;
          user_id?: string;
        };
        Relationships: [];
      };
      notification_preferences_alt: {
        Row: {
          comedians_unsubscribed_from_emails: string[];
          comedians_unsubscribed_from_sms: string[];
          created_at: string;
          has_opted_in_sms: boolean;
          has_opted_in_sms_custom_messaging: boolean;
          id: string;
          manager_outreach: boolean;
          new_comedian: boolean;
          new_special: boolean;
          new_subscription_content: boolean;
          new_tour_date: boolean;
          newsletter_email_frequency: Database["public"]["Enums"]["email_frequency"];
          push_notifications_enabled: boolean;
          user_id: string;
          venue_pages_unsubscribed_from_emails: string[];
          venue_pages_unsubscribed_from_sms: string[];
        };
        Insert: {
          comedians_unsubscribed_from_emails?: string[];
          comedians_unsubscribed_from_sms?: string[];
          created_at?: string;
          has_opted_in_sms?: boolean;
          has_opted_in_sms_custom_messaging?: boolean;
          id?: string;
          manager_outreach?: boolean;
          new_comedian?: boolean;
          new_special?: boolean;
          new_subscription_content?: boolean;
          new_tour_date?: boolean;
          newsletter_email_frequency?: Database["public"]["Enums"]["email_frequency"];
          push_notifications_enabled?: boolean;
          user_id: string;
          venue_pages_unsubscribed_from_emails?: string[];
          venue_pages_unsubscribed_from_sms?: string[];
        };
        Update: {
          comedians_unsubscribed_from_emails?: string[];
          comedians_unsubscribed_from_sms?: string[];
          created_at?: string;
          has_opted_in_sms?: boolean;
          has_opted_in_sms_custom_messaging?: boolean;
          id?: string;
          manager_outreach?: boolean;
          new_comedian?: boolean;
          new_special?: boolean;
          new_subscription_content?: boolean;
          new_tour_date?: boolean;
          newsletter_email_frequency?: Database["public"]["Enums"]["email_frequency"];
          push_notifications_enabled?: boolean;
          user_id?: string;
          venue_pages_unsubscribed_from_emails?: string[];
          venue_pages_unsubscribed_from_sms?: string[];
        };
        Relationships: [];
      };
      notification_preferences_alt_duplicate: {
        Row: {
          comedians_unsubscribed_from_emails: string[];
          comedians_unsubscribed_from_sms: string[];
          created_at: string;
          has_opted_in_sms: boolean;
          has_opted_in_sms_custom_messaging: boolean;
          id: string;
          new_comedian: boolean;
          new_special: boolean;
          new_tour_date: boolean;
          newsletter_email_frequency: Database["public"]["Enums"]["email_frequency"];
          push_new_tour_date: boolean | null;
          user_id: string;
        };
        Insert: {
          comedians_unsubscribed_from_emails?: string[];
          comedians_unsubscribed_from_sms?: string[];
          created_at?: string;
          has_opted_in_sms?: boolean;
          has_opted_in_sms_custom_messaging?: boolean;
          id?: string;
          new_comedian?: boolean;
          new_special?: boolean;
          new_tour_date?: boolean;
          newsletter_email_frequency?: Database["public"]["Enums"]["email_frequency"];
          push_new_tour_date?: boolean | null;
          user_id: string;
        };
        Update: {
          comedians_unsubscribed_from_emails?: string[];
          comedians_unsubscribed_from_sms?: string[];
          created_at?: string;
          has_opted_in_sms?: boolean;
          has_opted_in_sms_custom_messaging?: boolean;
          id?: string;
          new_comedian?: boolean;
          new_special?: boolean;
          new_tour_date?: boolean;
          newsletter_email_frequency?: Database["public"]["Enums"]["email_frequency"];
          push_new_tour_date?: boolean | null;
          user_id?: string;
        };
        Relationships: [];
      };
      notification_types: {
        Row: {
          batch_window_inactivity_minutes: number;
          batch_window_max_minutes: number;
          cadence_key: string | null;
          cadence_max_per_window: number | null;
          cadence_window_hours: number | null;
          channels: string[];
          courier_email_template_id: string | null;
          courier_push_template_id: string | null;
          created_at: string;
          display_name: string;
          id: string;
          is_enabled: boolean;
          kind: string;
          requires_geo: boolean;
        };
        Insert: {
          batch_window_inactivity_minutes?: number;
          batch_window_max_minutes?: number;
          cadence_key?: string | null;
          cadence_max_per_window?: number | null;
          cadence_window_hours?: number | null;
          channels?: string[];
          courier_email_template_id?: string | null;
          courier_push_template_id?: string | null;
          created_at?: string;
          display_name: string;
          id: string;
          is_enabled?: boolean;
          kind?: string;
          requires_geo?: boolean;
        };
        Update: {
          batch_window_inactivity_minutes?: number;
          batch_window_max_minutes?: number;
          cadence_key?: string | null;
          cadence_max_per_window?: number | null;
          cadence_window_hours?: number | null;
          channels?: string[];
          courier_email_template_id?: string | null;
          courier_push_template_id?: string | null;
          created_at?: string;
          display_name?: string;
          id?: string;
          is_enabled?: boolean;
          kind?: string;
          requires_geo?: boolean;
        };
        Relationships: [];
      };
      nuxs: {
        Row: {
          created_at: string;
          description: string | null;
          slug: string;
        };
        Insert: {
          created_at?: string;
          description?: string | null;
          slug: string;
        };
        Update: {
          created_at?: string;
          description?: string | null;
          slug?: string;
        };
        Relationships: [];
      };
      page_group_members: {
        Row: {
          created_at: string;
          group_page_id: string;
          id: string;
          member_page_id: string;
        };
        Insert: {
          created_at?: string;
          group_page_id: string;
          id?: string;
          member_page_id: string;
        };
        Update: {
          created_at?: string;
          group_page_id?: string;
          id?: string;
          member_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "page_group_members_group_page_id_fkey";
            columns: ["group_page_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "page_group_members_member_page_id_fkey";
            columns: ["member_page_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      page_type: {
        Row: {
          created_at: string;
          id: string;
          page_id: string;
          type: Database["public"]["Enums"]["page_types"];
        };
        Insert: {
          created_at?: string;
          id?: string;
          page_id: string;
          type: Database["public"]["Enums"]["page_types"];
        };
        Update: {
          created_at?: string;
          id?: string;
          page_id?: string;
          type?: Database["public"]["Enums"]["page_types"];
        };
        Relationships: [
          {
            foreignKeyName: "page_type_page_id_fkey";
            columns: ["page_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      pending_charges: {
        Row: {
          charge_id: string;
          charge_type: string | null;
          created_at: string;
          id: string;
          product_id: string | null;
          special_id: string | null;
          subscription_id: string | null;
          user_email: string;
        };
        Insert: {
          charge_id: string;
          charge_type?: string | null;
          created_at?: string;
          id?: string;
          product_id?: string | null;
          special_id?: string | null;
          subscription_id?: string | null;
          user_email: string;
        };
        Update: {
          charge_id?: string;
          charge_type?: string | null;
          created_at?: string;
          id?: string;
          product_id?: string | null;
          special_id?: string | null;
          subscription_id?: string | null;
          user_email?: string;
        };
        Relationships: [];
      };
      pending_comedians: {
        Row: {
          about: string | null;
          about_img: string | null;
          claimed_existing_slug: boolean;
          created_at: string | null;
          display_name: string;
          display_name_short: string;
          featured_special_id: string | null;
          fts_token: unknown;
          fts_token_nostop: unknown;
          id: string;
          invitation_ref_id: string;
          is_external_preview: boolean | null;
          is_punchup_verified: boolean;
          merch_store_link: string | null;
          published_at: string | null;
          requested_admins: Json | null;
          requested_admins_emails: string[] | null;
          shopify_merch_collection_id: string | null;
          signature_img: string | null;
          slug: string;
          social_media_links: Json | null;
          tip_jar_link: string | null;
        };
        Insert: {
          about?: string | null;
          about_img?: string | null;
          claimed_existing_slug?: boolean;
          created_at?: string | null;
          display_name: string;
          display_name_short: string;
          featured_special_id?: string | null;
          fts_token?: unknown;
          fts_token_nostop?: unknown;
          id?: string;
          invitation_ref_id: string;
          is_external_preview?: boolean | null;
          is_punchup_verified?: boolean;
          merch_store_link?: string | null;
          published_at?: string | null;
          requested_admins?: Json | null;
          requested_admins_emails?: string[] | null;
          shopify_merch_collection_id?: string | null;
          signature_img?: string | null;
          slug: string;
          social_media_links?: Json | null;
          tip_jar_link?: string | null;
        };
        Update: {
          about?: string | null;
          about_img?: string | null;
          claimed_existing_slug?: boolean;
          created_at?: string | null;
          display_name?: string;
          display_name_short?: string;
          featured_special_id?: string | null;
          fts_token?: unknown;
          fts_token_nostop?: unknown;
          id?: string;
          invitation_ref_id?: string;
          is_external_preview?: boolean | null;
          is_punchup_verified?: boolean;
          merch_store_link?: string | null;
          published_at?: string | null;
          requested_admins?: Json | null;
          requested_admins_emails?: string[] | null;
          shopify_merch_collection_id?: string | null;
          signature_img?: string | null;
          slug?: string;
          social_media_links?: Json | null;
          tip_jar_link?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "pending_comedians_featured_special_id_fkey";
            columns: ["featured_special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "pending_comedians_invitation_ref_id_fkey";
            columns: ["invitation_ref_id"];
            isOneToOne: false;
            referencedRelation: "artist_invitations";
            referencedColumns: ["id"];
          },
        ];
      };
      pending_follows: {
        Row: {
          converted_at: string | null;
          converted_user_id: string | null;
          created_at: string;
          email: string | null;
          follow_location: string | null;
          follow_type: string;
          id: string;
          phone: string | null;
          target_id: string;
        };
        Insert: {
          converted_at?: string | null;
          converted_user_id?: string | null;
          created_at?: string;
          email?: string | null;
          follow_location?: string | null;
          follow_type: string;
          id?: string;
          phone?: string | null;
          target_id: string;
        };
        Update: {
          converted_at?: string | null;
          converted_user_id?: string | null;
          created_at?: string;
          email?: string | null;
          follow_location?: string | null;
          follow_type?: string;
          id?: string;
          phone?: string | null;
          target_id?: string;
        };
        Relationships: [];
      };
      performance_notification_settings: {
        Row: {
          auto_populate_comedian: boolean | null;
          booking_response_id: string;
          created_at: string | null;
          id: string;
          notify_on_confirm: boolean | null;
          notify_on_decline: boolean | null;
          populate_before_show_unit: string | null;
          populate_before_show_value: number | null;
          populated_at: string | null;
          reminder_before_show_unit: string | null;
          reminder_before_show_value: number | null;
          reminder_sent_at: string | null;
          send_comedian_reminder: boolean | null;
          show_id: string | null;
          updated_at: string | null;
          venue_manager_id: string;
        };
        Insert: {
          auto_populate_comedian?: boolean | null;
          booking_response_id: string;
          created_at?: string | null;
          id?: string;
          notify_on_confirm?: boolean | null;
          notify_on_decline?: boolean | null;
          populate_before_show_unit?: string | null;
          populate_before_show_value?: number | null;
          populated_at?: string | null;
          reminder_before_show_unit?: string | null;
          reminder_before_show_value?: number | null;
          reminder_sent_at?: string | null;
          send_comedian_reminder?: boolean | null;
          show_id?: string | null;
          updated_at?: string | null;
          venue_manager_id: string;
        };
        Update: {
          auto_populate_comedian?: boolean | null;
          booking_response_id?: string;
          created_at?: string | null;
          id?: string;
          notify_on_confirm?: boolean | null;
          notify_on_decline?: boolean | null;
          populate_before_show_unit?: string | null;
          populate_before_show_value?: number | null;
          populated_at?: string | null;
          reminder_before_show_unit?: string | null;
          reminder_before_show_value?: number | null;
          reminder_sent_at?: string | null;
          send_comedian_reminder?: boolean | null;
          show_id?: string | null;
          updated_at?: string | null;
          venue_manager_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "performance_notification_settings_booking_response_id_fkey";
            columns: ["booking_response_id"];
            isOneToOne: true;
            referencedRelation: "booking_contact_responses";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "performance_notification_settings_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "performance_notification_settings_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
        ];
      };
      podcast_comedians: {
        Row: {
          comedian_id: string;
          created_at: string;
          podcast_id: string;
        };
        Insert: {
          comedian_id: string;
          created_at?: string;
          podcast_id: string;
        };
        Update: {
          comedian_id?: string;
          created_at?: string;
          podcast_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "podcast_comedians_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "podcast_comedians_podcast_id_fkey";
            columns: ["podcast_id"];
            isOneToOne: false;
            referencedRelation: "podcasts";
            referencedColumns: ["id"];
          },
        ];
      };
      podcasts: {
        Row: {
          collection_id: string;
          created_at: string;
          id: string;
          poster_image_id: string | null;
        };
        Insert: {
          collection_id: string;
          created_at?: string;
          id?: string;
          poster_image_id?: string | null;
        };
        Update: {
          collection_id?: string;
          created_at?: string;
          id?: string;
          poster_image_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "podcasts_collection_id_fkey";
            columns: ["collection_id"];
            isOneToOne: false;
            referencedRelation: "collections";
            referencedColumns: ["id"];
          },
        ];
      };
      postal_codes: {
        Row: {
          city: string | null;
          country_code: string | null;
          created_at: string | null;
          latitude: number | null;
          location_geo: unknown;
          longitude: number | null;
          postal_code: string;
          state: string | null;
          state_abbreviation: string | null;
          verified: boolean | null;
        };
        Insert: {
          city?: string | null;
          country_code?: string | null;
          created_at?: string | null;
          latitude?: number | null;
          location_geo?: unknown;
          longitude?: number | null;
          postal_code: string;
          state?: string | null;
          state_abbreviation?: string | null;
          verified?: boolean | null;
        };
        Update: {
          city?: string | null;
          country_code?: string | null;
          created_at?: string | null;
          latitude?: number | null;
          location_geo?: unknown;
          longitude?: number | null;
          postal_code?: string;
          state?: string | null;
          state_abbreviation?: string | null;
          verified?: boolean | null;
        };
        Relationships: [];
      };
      product_collections: {
        Row: {
          collection_id: string;
          created_at: string;
          product_id: string;
        };
        Insert: {
          collection_id?: string;
          created_at?: string;
          product_id?: string;
        };
        Update: {
          collection_id?: string;
          created_at?: string;
          product_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "product_collections_collection_id_fkey";
            columns: ["collection_id"];
            isOneToOne: false;
            referencedRelation: "collections";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "product_collections_product_id_fkey";
            columns: ["product_id"];
            isOneToOne: false;
            referencedRelation: "products";
            referencedColumns: ["id"];
          },
        ];
      };
      product_releases: {
        Row: {
          audiences: string[];
          body: string | null;
          category: string;
          created_at: string;
          id: string;
          image_url: string | null;
          is_featured: boolean;
          is_published: boolean;
          link_label: string | null;
          link_url: string | null;
          release_month: string;
          status: string;
          summary: string;
          title: string;
          updated_at: string;
        };
        Insert: {
          audiences: string[];
          body?: string | null;
          category: string;
          created_at?: string;
          id?: string;
          image_url?: string | null;
          is_featured?: boolean;
          is_published?: boolean;
          link_label?: string | null;
          link_url?: string | null;
          release_month: string;
          status: string;
          summary: string;
          title: string;
          updated_at?: string;
        };
        Update: {
          audiences?: string[];
          body?: string | null;
          category?: string;
          created_at?: string;
          id?: string;
          image_url?: string | null;
          is_featured?: boolean;
          is_published?: boolean;
          link_label?: string | null;
          link_url?: string | null;
          release_month?: string;
          status?: string;
          summary?: string;
          title?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      product_specials: {
        Row: {
          created_at: string;
          product_id: string;
          special_id: string;
        };
        Insert: {
          created_at?: string;
          product_id: string;
          special_id: string;
        };
        Update: {
          created_at?: string;
          product_id?: string;
          special_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "product_specials_product_id_fkey";
            columns: ["product_id"];
            isOneToOne: false;
            referencedRelation: "products";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "product_specials_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      product_updates: {
        Row: {
          category: string;
          category_display: string;
          content: Json | null;
          created_at: string | null;
          created_by: string | null;
          date: string;
          emoji: string | null;
          featured: boolean | null;
          id: string;
          internal: boolean;
          published_at: string | null;
          slug: string;
          stakeholder: string;
          status: string | null;
          summary: string;
          tags: string[] | null;
          title: string;
          updated_at: string | null;
          updated_by: string | null;
          version: string | null;
        };
        Insert: {
          category: string;
          category_display: string;
          content?: Json | null;
          created_at?: string | null;
          created_by?: string | null;
          date: string;
          emoji?: string | null;
          featured?: boolean | null;
          id?: string;
          internal?: boolean;
          published_at?: string | null;
          slug: string;
          stakeholder: string;
          status?: string | null;
          summary: string;
          tags?: string[] | null;
          title: string;
          updated_at?: string | null;
          updated_by?: string | null;
          version?: string | null;
        };
        Update: {
          category?: string;
          category_display?: string;
          content?: Json | null;
          created_at?: string | null;
          created_by?: string | null;
          date?: string;
          emoji?: string | null;
          featured?: boolean | null;
          id?: string;
          internal?: boolean;
          published_at?: string | null;
          slug?: string;
          stakeholder?: string;
          status?: string | null;
          summary?: string;
          tags?: string[] | null;
          title?: string;
          updated_at?: string | null;
          updated_by?: string | null;
          version?: string | null;
        };
        Relationships: [];
      };
      products: {
        Row: {
          apple_product_id: string | null;
          category: Database["public"]["Enums"]["product_category"];
          comedian_id: string | null;
          created_at: string;
          description: string | null;
          description_details_bullets: string[] | null;
          flex_suggested_price: number | null;
          id: string;
          is_flex_price: boolean;
          is_production: boolean;
          name: string;
          preferred: boolean;
          price: number;
          stripe_price_id: string | null;
          stripe_product_id: string | null;
          subscription_tier: number | null;
        };
        Insert: {
          apple_product_id?: string | null;
          category: Database["public"]["Enums"]["product_category"];
          comedian_id?: string | null;
          created_at?: string;
          description?: string | null;
          description_details_bullets?: string[] | null;
          flex_suggested_price?: number | null;
          id?: string;
          is_flex_price?: boolean;
          is_production?: boolean;
          name: string;
          preferred?: boolean;
          price: number;
          stripe_price_id?: string | null;
          stripe_product_id?: string | null;
          subscription_tier?: number | null;
        };
        Update: {
          apple_product_id?: string | null;
          category?: Database["public"]["Enums"]["product_category"];
          comedian_id?: string | null;
          created_at?: string;
          description?: string | null;
          description_details_bullets?: string[] | null;
          flex_suggested_price?: number | null;
          id?: string;
          is_flex_price?: boolean;
          is_production?: boolean;
          name?: string;
          preferred?: boolean;
          price?: number;
          stripe_price_id?: string | null;
          stripe_product_id?: string | null;
          subscription_tier?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "products_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      push_notification_jobs: {
        Row: {
          comedian_id: string | null;
          created_at: string | null;
          error_message: string | null;
          id: string;
          job_type: string | null;
          metadata: Json | null;
          processed_at: string | null;
          show_ids: string[];
          status: Database["public"]["Enums"]["push_notification_job"] | null;
          updated_at: string | null;
          venue_id: string | null;
        };
        Insert: {
          comedian_id?: string | null;
          created_at?: string | null;
          error_message?: string | null;
          id?: string;
          job_type?: string | null;
          metadata?: Json | null;
          processed_at?: string | null;
          show_ids: string[];
          status?: Database["public"]["Enums"]["push_notification_job"] | null;
          updated_at?: string | null;
          venue_id?: string | null;
        };
        Update: {
          comedian_id?: string | null;
          created_at?: string | null;
          error_message?: string | null;
          id?: string;
          job_type?: string | null;
          metadata?: Json | null;
          processed_at?: string | null;
          show_ids?: string[];
          status?: Database["public"]["Enums"]["push_notification_job"] | null;
          updated_at?: string | null;
          venue_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "push_notification_jobs_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "push_notification_jobs_venue_id_fkey";
            columns: ["venue_id"];
            isOneToOne: false;
            referencedRelation: "venues";
            referencedColumns: ["id"];
          },
        ];
      };
      push_notification_logs: {
        Row: {
          clicked: boolean | null;
          cloud_task_id: string | null;
          comedian_id: string | null;
          delivered: boolean | null;
          error: Json | null;
          id: string;
          job_id: string | null;
          message_content: Json | null;
          notification_type: string | null;
          scheduled_for: string | null;
          sent_at: string | null;
          show_ids: string[];
          user_id: string | null;
          user_timezone: string | null;
          venue_id: string | null;
        };
        Insert: {
          clicked?: boolean | null;
          cloud_task_id?: string | null;
          comedian_id?: string | null;
          delivered?: boolean | null;
          error?: Json | null;
          id?: string;
          job_id?: string | null;
          message_content?: Json | null;
          notification_type?: string | null;
          scheduled_for?: string | null;
          sent_at?: string | null;
          show_ids: string[];
          user_id?: string | null;
          user_timezone?: string | null;
          venue_id?: string | null;
        };
        Update: {
          clicked?: boolean | null;
          cloud_task_id?: string | null;
          comedian_id?: string | null;
          delivered?: boolean | null;
          error?: Json | null;
          id?: string;
          job_id?: string | null;
          message_content?: Json | null;
          notification_type?: string | null;
          scheduled_for?: string | null;
          sent_at?: string | null;
          show_ids?: string[];
          user_id?: string | null;
          user_timezone?: string | null;
          venue_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "push_notification_logs_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "push_notification_logs_job_id_fkey";
            columns: ["job_id"];
            isOneToOne: false;
            referencedRelation: "push_notification_jobs";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "push_notification_logs_venue_id_fkey";
            columns: ["venue_id"];
            isOneToOne: false;
            referencedRelation: "venues";
            referencedColumns: ["id"];
          },
        ];
      };
      release_announcements: {
        Row: {
          announced_at: string;
          announced_by: string | null;
          audience: string;
          channel: string;
          created_at: string;
          id: string;
          note: string | null;
          release_id: string;
        };
        Insert: {
          announced_at?: string;
          announced_by?: string | null;
          audience: string;
          channel: string;
          created_at?: string;
          id?: string;
          note?: string | null;
          release_id: string;
        };
        Update: {
          announced_at?: string;
          announced_by?: string | null;
          audience?: string;
          channel?: string;
          created_at?: string;
          id?: string;
          note?: string | null;
          release_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "release_announcements_release_id_fkey";
            columns: ["release_id"];
            isOneToOne: false;
            referencedRelation: "product_releases";
            referencedColumns: ["id"];
          },
        ];
      };
      requests_for_shows: {
        Row: {
          comedian_id: string;
          country_code: string | null;
          created_at: string;
          id: string;
          request_dismissed: boolean;
          requested_zipcode: string | null;
          user_id: string | null;
        };
        Insert: {
          comedian_id: string;
          country_code?: string | null;
          created_at?: string;
          id?: string;
          request_dismissed: boolean;
          requested_zipcode?: string | null;
          user_id?: string | null;
        };
        Update: {
          comedian_id?: string;
          country_code?: string | null;
          created_at?: string;
          id?: string;
          request_dismissed?: boolean;
          requested_zipcode?: string | null;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "requests_for_shows_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      restricted_users: {
        Row: {
          comedian_id: string;
          id: string;
          is_active: boolean | null;
          reason: string | null;
          restricted_at: string | null;
          restricted_by: string | null;
          unrestricted_at: string | null;
          unrestricted_by: string | null;
          user_id: string;
        };
        Insert: {
          comedian_id: string;
          id?: string;
          is_active?: boolean | null;
          reason?: string | null;
          restricted_at?: string | null;
          restricted_by?: string | null;
          unrestricted_at?: string | null;
          unrestricted_by?: string | null;
          user_id: string;
        };
        Update: {
          comedian_id?: string;
          id?: string;
          is_active?: boolean | null;
          reason?: string | null;
          restricted_at?: string | null;
          restricted_by?: string | null;
          unrestricted_at?: string | null;
          unrestricted_by?: string | null;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "restricted_users_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      revenue_account_attribution: {
        Row: {
          account_id: string;
          comedian_id: string | null;
          created_at: string;
          exclude_reason: string | null;
          reviewed_at: string | null;
          reviewed_by: string | null;
          source: string;
          status: string;
          stripe_partner_name: string | null;
          type: string | null;
          updated_at: string;
          venue_page_id: string | null;
        };
        Insert: {
          account_id: string;
          comedian_id?: string | null;
          created_at?: string;
          exclude_reason?: string | null;
          reviewed_at?: string | null;
          reviewed_by?: string | null;
          source?: string;
          status?: string;
          stripe_partner_name?: string | null;
          type?: string | null;
          updated_at?: string;
          venue_page_id?: string | null;
        };
        Update: {
          account_id?: string;
          comedian_id?: string | null;
          created_at?: string;
          exclude_reason?: string | null;
          reviewed_at?: string | null;
          reviewed_by?: string | null;
          source?: string;
          status?: string;
          stripe_partner_name?: string | null;
          type?: string | null;
          updated_at?: string;
          venue_page_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "revenue_account_attribution_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "revenue_account_attribution_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      revenue_attribution_manual_months: {
        Row: {
          account_id: string;
          created_at: string;
          created_by: string | null;
          gmv_usd: number;
          month: string;
          take_usd: number;
          updated_at: string;
          updated_by: string | null;
        };
        Insert: {
          account_id: string;
          created_at?: string;
          created_by?: string | null;
          gmv_usd?: number;
          month: string;
          take_usd?: number;
          updated_at?: string;
          updated_by?: string | null;
        };
        Update: {
          account_id?: string;
          created_at?: string;
          created_by?: string | null;
          gmv_usd?: number;
          month?: string;
          take_usd?: number;
          updated_at?: string;
          updated_by?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "revenue_attribution_manual_months_account_id_fkey";
            columns: ["account_id"];
            isOneToOne: false;
            referencedRelation: "revenue_account_attribution";
            referencedColumns: ["account_id"];
          },
        ];
      };
      scheduled_notification_recipients: {
        Row: {
          created_at: string;
          id: string;
          scheduled_notification_id: string;
          user_id: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          scheduled_notification_id: string;
          user_id: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          scheduled_notification_id?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "scheduled_notification_recipient_scheduled_notification_id_fkey";
            columns: ["scheduled_notification_id"];
            isOneToOne: false;
            referencedRelation: "scheduled_notifications";
            referencedColumns: ["id"];
          },
        ];
      };
      scheduled_notifications: {
        Row: {
          action_url: string | null;
          attempts: number;
          body: string | null;
          channels: string[];
          claimed_at: string | null;
          comedian_id: string | null;
          completed_at: string | null;
          courier_data: Json;
          courier_email_template_id: string | null;
          courier_push_template_id: string | null;
          created_at: string;
          error_message: string | null;
          failed_count: number;
          id: string;
          image_url: string | null;
          notification_type_id: string;
          recipient_count: number;
          scheduled_for: string;
          sent_count: number;
          source_id: string | null;
          source_type: string | null;
          status: string;
          title: string | null;
        };
        Insert: {
          action_url?: string | null;
          attempts?: number;
          body?: string | null;
          channels: string[];
          claimed_at?: string | null;
          comedian_id?: string | null;
          completed_at?: string | null;
          courier_data?: Json;
          courier_email_template_id?: string | null;
          courier_push_template_id?: string | null;
          created_at?: string;
          error_message?: string | null;
          failed_count?: number;
          id?: string;
          image_url?: string | null;
          notification_type_id: string;
          recipient_count?: number;
          scheduled_for: string;
          sent_count?: number;
          source_id?: string | null;
          source_type?: string | null;
          status?: string;
          title?: string | null;
        };
        Update: {
          action_url?: string | null;
          attempts?: number;
          body?: string | null;
          channels?: string[];
          claimed_at?: string | null;
          comedian_id?: string | null;
          completed_at?: string | null;
          courier_data?: Json;
          courier_email_template_id?: string | null;
          courier_push_template_id?: string | null;
          created_at?: string;
          error_message?: string | null;
          failed_count?: number;
          id?: string;
          image_url?: string | null;
          notification_type_id?: string;
          recipient_count?: number;
          scheduled_for?: string;
          sent_count?: number;
          source_id?: string | null;
          source_type?: string | null;
          status?: string;
          title?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "scheduled_notifications_notification_type_id_fkey";
            columns: ["notification_type_id"];
            isOneToOne: false;
            referencedRelation: "notification_types";
            referencedColumns: ["id"];
          },
        ];
      };
      search_history: {
        Row: {
          created_at: string;
          id: number;
          num_results: number | null;
          search_term: string | null;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          id?: number;
          num_results?: number | null;
          search_term?: string | null;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: number;
          num_results?: number | null;
          search_term?: string | null;
          user_id?: string | null;
        };
        Relationships: [];
      };
      short_links: {
        Row: {
          attribution: Json | null;
          comedian_id: string | null;
          created_at: string | null;
          id: string;
          is_active: boolean | null;
          link_data: Json | null;
          short_code: string;
          short_code_name: string | null;
          southpaw_job_id: string | null;
          type: string;
          url: string;
          venue_page_id: string | null;
        };
        Insert: {
          attribution?: Json | null;
          comedian_id?: string | null;
          created_at?: string | null;
          id?: string;
          is_active?: boolean | null;
          link_data?: Json | null;
          short_code: string;
          short_code_name?: string | null;
          southpaw_job_id?: string | null;
          type?: string;
          url: string;
          venue_page_id?: string | null;
        };
        Update: {
          attribution?: Json | null;
          comedian_id?: string | null;
          created_at?: string | null;
          id?: string;
          is_active?: boolean | null;
          link_data?: Json | null;
          short_code?: string;
          short_code_name?: string | null;
          southpaw_job_id?: string | null;
          type?: string;
          url?: string;
          venue_page_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "short_links_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "short_links_southpaw_job_id_fkey";
            columns: ["southpaw_job_id"];
            isOneToOne: false;
            referencedRelation: "southpaw_jobs";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "short_links_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      short_links_details: {
        Row: {
          attribution: Json | null;
          click_count: number | null;
          click_count_from_email: number;
          click_count_from_sms: number;
          comedian_id: string | null;
          created_at: string | null;
          follow_count: number | null;
          id: string;
          is_active: boolean | null;
          link_data: Json | null;
          play_count: number;
          short_code: string;
          short_code_name: string | null;
          southpaw_job_id: string | null;
          ticket_clicks: number | null;
          type: string;
          url: string | null;
          venue_page_id: string | null;
        };
        Insert: {
          attribution?: Json | null;
          click_count?: number | null;
          click_count_from_email?: number;
          click_count_from_sms?: number;
          comedian_id?: string | null;
          created_at?: string | null;
          follow_count?: number | null;
          id?: string;
          is_active?: boolean | null;
          link_data?: Json | null;
          play_count?: number;
          short_code: string;
          short_code_name?: string | null;
          southpaw_job_id?: string | null;
          ticket_clicks?: number | null;
          type?: string;
          url?: string | null;
          venue_page_id?: string | null;
        };
        Update: {
          attribution?: Json | null;
          click_count?: number | null;
          click_count_from_email?: number;
          click_count_from_sms?: number;
          comedian_id?: string | null;
          created_at?: string | null;
          follow_count?: number | null;
          id?: string;
          is_active?: boolean | null;
          link_data?: Json | null;
          play_count?: number;
          short_code?: string;
          short_code_name?: string | null;
          southpaw_job_id?: string | null;
          ticket_clicks?: number | null;
          type?: string;
          url?: string | null;
          venue_page_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "short_links_details_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "short_links_details_id_fkey";
            columns: ["id"];
            isOneToOne: true;
            referencedRelation: "short_links";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "short_links_details_southpaw_job_id_fkey";
            columns: ["southpaw_job_id"];
            isOneToOne: false;
            referencedRelation: "southpaw_jobs";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "short_links_details_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      show_comedian_pairings: {
        Row: {
          comedian_id: string;
          hidden_from_comedian_page: boolean;
          ordering: number;
          show_id: string;
        };
        Insert: {
          comedian_id?: string;
          hidden_from_comedian_page?: boolean;
          ordering?: number;
          show_id?: string;
        };
        Update: {
          comedian_id?: string;
          hidden_from_comedian_page?: boolean;
          ordering?: number;
          show_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "show_comedian_pairings_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "show_comedian_pairings_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "show_comedian_pairings_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
        ];
      };
      show_creators: {
        Row: {
          created_at: string;
          creator_id: string;
          entity_id: string | null;
          entity_type: Database["public"]["Enums"]["show_creator_entity_type"];
          show_id: string;
        };
        Insert: {
          created_at?: string;
          creator_id: string;
          entity_id?: string | null;
          entity_type: Database["public"]["Enums"]["show_creator_entity_type"];
          show_id: string;
        };
        Update: {
          created_at?: string;
          creator_id?: string;
          entity_id?: string | null;
          entity_type?: Database["public"]["Enums"]["show_creator_entity_type"];
          show_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "show_creators_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: true;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "show_creators_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: true;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
        ];
      };
      show_group_link: {
        Row: {
          created_at: string;
          created_by: string | null;
          day_of_week: number | null;
          end_date: string | null;
          event_ids: number[] | null;
          facebook_pixel_id: string | null;
          id: string;
          link_type: string;
          show_name: string | null;
          show_time: string | null;
          slug: string;
          start_date: string | null;
          title: string;
          updated_at: string;
          venue_page_id: string;
        };
        Insert: {
          created_at?: string;
          created_by?: string | null;
          day_of_week?: number | null;
          end_date?: string | null;
          event_ids?: number[] | null;
          facebook_pixel_id?: string | null;
          id?: string;
          link_type: string;
          show_name?: string | null;
          show_time?: string | null;
          slug: string;
          start_date?: string | null;
          title: string;
          updated_at?: string;
          venue_page_id: string;
        };
        Update: {
          created_at?: string;
          created_by?: string | null;
          day_of_week?: number | null;
          end_date?: string | null;
          event_ids?: number[] | null;
          facebook_pixel_id?: string | null;
          id?: string;
          link_type?: string;
          show_name?: string | null;
          show_time?: string | null;
          slug?: string;
          start_date?: string | null;
          title?: string;
          updated_at?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "show_group_link_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      shows_series: {
        Row: {
          created_at: string;
          id: string;
          name: string;
          venue_page_id: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          name: string;
          venue_page_id: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          name?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "shows_series_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      sms_analytics_archive: {
        Row: {
          clicked_link: boolean | null;
          created_at: string;
          failed_delivery: boolean | null;
          phone: string | null;
          sms_delivered_at: string | null;
          southpaw_job_id: string;
          user_id: string;
        };
        Insert: {
          clicked_link?: boolean | null;
          created_at?: string;
          failed_delivery?: boolean | null;
          phone?: string | null;
          sms_delivered_at?: string | null;
          southpaw_job_id: string;
          user_id: string;
        };
        Update: {
          clicked_link?: boolean | null;
          created_at?: string;
          failed_delivery?: boolean | null;
          phone?: string | null;
          sms_delivered_at?: string | null;
          southpaw_job_id?: string;
          user_id?: string;
        };
        Relationships: [];
      };
      southpaw_audience: {
        Row: {
          created_at: string;
          email: string | null;
          job_id: string;
          phone: string | null;
          unsubtoken: string | null;
          user_id: string;
        };
        Insert: {
          created_at?: string;
          email?: string | null;
          job_id: string;
          phone?: string | null;
          unsubtoken?: string | null;
          user_id: string;
        };
        Update: {
          created_at?: string;
          email?: string | null;
          job_id?: string;
          phone?: string | null;
          unsubtoken?: string | null;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "southpaw_audience_job_id_fkey";
            columns: ["job_id"];
            isOneToOne: false;
            referencedRelation: "southpaw_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      southpaw_external_audience: {
        Row: {
          contact_id: string;
          created_at: string;
          email: string;
          job_id: string;
          phone: string | null;
          unsubtoken: string;
        };
        Insert: {
          contact_id: string;
          created_at?: string;
          email: string;
          job_id: string;
          phone?: string | null;
          unsubtoken: string;
        };
        Update: {
          contact_id?: string;
          created_at?: string;
          email?: string;
          job_id?: string;
          phone?: string | null;
          unsubtoken?: string;
        };
        Relationships: [
          {
            foreignKeyName: "southpaw_contacts_audience_job_id_fkey";
            columns: ["job_id"];
            isOneToOne: false;
            referencedRelation: "southpaw_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      southpaw_job_customizations: {
        Row: {
          created_at: string;
          customization: Json;
          southpaw_job_id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          customization: Json;
          southpaw_job_id: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          customization?: Json;
          southpaw_job_id?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "southpaw_job_customizations_southpaw_job_id_fkey";
            columns: ["southpaw_job_id"];
            isOneToOne: true;
            referencedRelation: "southpaw_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      southpaw_jobs: {
        Row: {
          campaign_name: string | null;
          comedian_id: string | null;
          composite_targeting: Json | null;
          contact_list_ids: string[];
          created_at: string;
          created_by: string;
          email_body: string;
          email_subject: string;
          email_template: string;
          hero_image: string;
          id: string;
          num_users_to_email: number;
          scheduled_for: string;
          sms_body: string | null;
          status: string;
          targeting_type: number;
          total_num_emails: number | null;
          total_num_texts: number | null;
          total_users_to_reach: number | null;
          venue_ids: string[];
          venue_page_id: string | null;
        };
        Insert: {
          campaign_name?: string | null;
          comedian_id?: string | null;
          composite_targeting?: Json | null;
          contact_list_ids?: string[];
          created_at?: string;
          created_by: string;
          email_body: string;
          email_subject: string;
          email_template: string;
          hero_image: string;
          id?: string;
          num_users_to_email?: number;
          scheduled_for: string;
          sms_body?: string | null;
          status: string;
          targeting_type?: number;
          total_num_emails?: number | null;
          total_num_texts?: number | null;
          total_users_to_reach?: number | null;
          venue_ids?: string[];
          venue_page_id?: string | null;
        };
        Update: {
          campaign_name?: string | null;
          comedian_id?: string | null;
          composite_targeting?: Json | null;
          contact_list_ids?: string[];
          created_at?: string;
          created_by?: string;
          email_body?: string;
          email_subject?: string;
          email_template?: string;
          hero_image?: string;
          id?: string;
          num_users_to_email?: number;
          scheduled_for?: string;
          sms_body?: string | null;
          status?: string;
          targeting_type?: number;
          total_num_emails?: number | null;
          total_num_texts?: number | null;
          total_users_to_reach?: number | null;
          venue_ids?: string[];
          venue_page_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "southpaw_jobs_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "southpaw_jobs_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      spatial_ref_sys: {
        Row: {
          auth_name: string | null;
          auth_srid: number | null;
          proj4text: string | null;
          srid: number;
          srtext: string | null;
        };
        Insert: {
          auth_name?: string | null;
          auth_srid?: number | null;
          proj4text?: string | null;
          srid: number;
          srtext?: string | null;
        };
        Update: {
          auth_name?: string | null;
          auth_srid?: number | null;
          proj4text?: string | null;
          srid?: number;
          srtext?: string | null;
        };
        Relationships: [];
      };
      special_boost: {
        Row: {
          boost_expiration_date: string | null;
          created_at: string;
          id: number;
          is_boosted: boolean | null;
          special_id: string | null;
        };
        Insert: {
          boost_expiration_date?: string | null;
          created_at?: string;
          id?: number;
          is_boosted?: boolean | null;
          special_id?: string | null;
        };
        Update: {
          boost_expiration_date?: string | null;
          created_at?: string;
          id?: number;
          is_boosted?: boolean | null;
          special_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "special_boost_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: true;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      special_comedian_tags: {
        Row: {
          comedian_id: string;
          created_at: string;
          id: string;
          ordering: number | null;
          requested_at: string;
          resolved_at: string | null;
          special_id: string;
          status: Database["public"]["Enums"]["content_tag_status"];
          tagged_by: string;
        };
        Insert: {
          comedian_id: string;
          created_at?: string;
          id?: string;
          ordering?: number | null;
          requested_at?: string;
          resolved_at?: string | null;
          special_id: string;
          status?: Database["public"]["Enums"]["content_tag_status"];
          tagged_by: string;
        };
        Update: {
          comedian_id?: string;
          created_at?: string;
          id?: string;
          ordering?: number | null;
          requested_at?: string;
          resolved_at?: string | null;
          special_id?: string;
          status?: Database["public"]["Enums"]["content_tag_status"];
          tagged_by?: string;
        };
        Relationships: [
          {
            foreignKeyName: "special_comedian_tags_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "special_comedian_tags_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "special_comedian_tags_tagged_by_fkey";
            columns: ["tagged_by"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      specials: {
        Row: {
          captions_bucket_path: string | null;
          comedian_id: string;
          created_at: string | null;
          description: string | null;
          fts_token: unknown;
          fts_token_nostop: unknown;
          id: string;
          is_audio: boolean;
          is_live: boolean;
          is_vertical: boolean | null;
          livestream: string | null;
          poster_img_with_titles: string | null;
          preview_img: string | null;
          preview_punchup_video_id: string | null;
          preview_video_id: string | null;
          price: number;
          product_id: string | null;
          published_at: string | null;
          punchup_video_id: string | null;
          required_subscription_tier: number | null;
          requires_purchase: boolean;
          requires_subscription: boolean;
          runtime: number;
          runtime_seconds: number | null;
          slug: string;
          small_preview_img: string | null;
          subscription_product_id: string | null;
          title: string;
          video_id: string | null;
        };
        Insert: {
          captions_bucket_path?: string | null;
          comedian_id: string;
          created_at?: string | null;
          description?: string | null;
          fts_token?: unknown;
          fts_token_nostop?: unknown;
          id?: string;
          is_audio?: boolean;
          is_live?: boolean;
          is_vertical?: boolean | null;
          livestream?: string | null;
          poster_img_with_titles?: string | null;
          preview_img?: string | null;
          preview_punchup_video_id?: string | null;
          preview_video_id?: string | null;
          price?: number;
          product_id?: string | null;
          published_at?: string | null;
          punchup_video_id?: string | null;
          required_subscription_tier?: number | null;
          requires_purchase?: boolean;
          requires_subscription?: boolean;
          runtime?: number;
          runtime_seconds?: number | null;
          slug: string;
          small_preview_img?: string | null;
          subscription_product_id?: string | null;
          title?: string;
          video_id?: string | null;
        };
        Update: {
          captions_bucket_path?: string | null;
          comedian_id?: string;
          created_at?: string | null;
          description?: string | null;
          fts_token?: unknown;
          fts_token_nostop?: unknown;
          id?: string;
          is_audio?: boolean;
          is_live?: boolean;
          is_vertical?: boolean | null;
          livestream?: string | null;
          poster_img_with_titles?: string | null;
          preview_img?: string | null;
          preview_punchup_video_id?: string | null;
          preview_video_id?: string | null;
          price?: number;
          product_id?: string | null;
          published_at?: string | null;
          punchup_video_id?: string | null;
          required_subscription_tier?: number | null;
          requires_purchase?: boolean;
          requires_subscription?: boolean;
          runtime?: number;
          runtime_seconds?: number | null;
          slug?: string;
          small_preview_img?: string | null;
          subscription_product_id?: string | null;
          title?: string;
          video_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "specials_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "specials_livestream_fkey";
            columns: ["livestream"];
            isOneToOne: false;
            referencedRelation: "livestreams";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "specials_preview_punchup_video_id_fkey";
            columns: ["preview_punchup_video_id"];
            isOneToOne: false;
            referencedRelation: "videos";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "specials_product_id_fkey";
            columns: ["product_id"];
            isOneToOne: false;
            referencedRelation: "products";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "specials_punchup_video_id_fkey";
            columns: ["punchup_video_id"];
            isOneToOne: false;
            referencedRelation: "videos";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "specials_subscription_product_id_fkey";
            columns: ["subscription_product_id"];
            isOneToOne: false;
            referencedRelation: "products";
            referencedColumns: ["id"];
          },
        ];
      };
      stripe_business_ids: {
        Row: {
          business_id: string | null;
          comedian_id: string | null;
          commission_rate_pct: number;
          created_at: string;
          id: number;
          manager_id: string | null;
          updated_at: string;
        };
        Insert: {
          business_id?: string | null;
          comedian_id?: string | null;
          commission_rate_pct?: number;
          created_at?: string;
          id?: number;
          manager_id?: string | null;
          updated_at?: string;
        };
        Update: {
          business_id?: string | null;
          comedian_id?: string | null;
          commission_rate_pct?: number;
          created_at?: string;
          id?: number;
          manager_id?: string | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "stripe_business_ids_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      stripe_business_ids_dev: {
        Row: {
          business_id: string | null;
          comedian_id: string | null;
          commission_rate_pct: number | null;
          created_at: string;
          id: number;
          manager_id: string | null;
          updated_at: string;
        };
        Insert: {
          business_id?: string | null;
          comedian_id?: string | null;
          commission_rate_pct?: number | null;
          created_at?: string;
          id?: number;
          manager_id?: string | null;
          updated_at?: string;
        };
        Update: {
          business_id?: string | null;
          comedian_id?: string | null;
          commission_rate_pct?: number | null;
          created_at?: string;
          id?: number;
          manager_id?: string | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "stripe_business_ids_dev_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: true;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      stripe_products: {
        Row: {
          comedian_id: string;
          created_at: string;
          id: string;
          price_id: string;
          tier: number;
        };
        Insert: {
          comedian_id: string;
          created_at?: string;
          id?: string;
          price_id: string;
          tier?: number;
        };
        Update: {
          comedian_id?: string;
          created_at?: string;
          id?: string;
          price_id?: string;
          tier?: number;
        };
        Relationships: [
          {
            foreignKeyName: "stripe_products_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      stripe_products_dev: {
        Row: {
          comedian_id: string;
          created_at: string;
          id: string;
          price_id: string;
          tier: number;
        };
        Insert: {
          comedian_id: string;
          created_at?: string;
          id?: string;
          price_id: string;
          tier?: number;
        };
        Update: {
          comedian_id?: string;
          created_at?: string;
          id?: string;
          price_id?: string;
          tier?: number;
        };
        Relationships: [
          {
            foreignKeyName: "stripe_products_dev_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      super_admins: {
        Row: {
          created_at: string | null;
          id: string;
          user_id: string;
        };
        Insert: {
          created_at?: string | null;
          id?: string;
          user_id: string;
        };
        Update: {
          created_at?: string | null;
          id?: string;
          user_id?: string;
        };
        Relationships: [];
      };
      support_answer_feedback: {
        Row: {
          comment: string | null;
          created_at: string;
          id: string;
          message_id: string;
          rating: string;
          reason: string | null;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          comment?: string | null;
          created_at?: string;
          id?: string;
          message_id: string;
          rating: string;
          reason?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          comment?: string | null;
          created_at?: string;
          id?: string;
          message_id?: string;
          rating?: string;
          reason?: string | null;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "support_answer_feedback_message_id_fkey";
            columns: ["message_id"];
            isOneToOne: false;
            referencedRelation: "support_messages";
            referencedColumns: ["id"];
          },
        ];
      };
      support_case_media: {
        Row: {
          case_id: string;
          created_at: string;
          created_by: string | null;
          description: string;
          description_source: string;
          duration_seconds: number | null;
          external_url: string | null;
          height: number | null;
          id: string;
          kind: string;
          mime_type: string | null;
          position: number;
          size_bytes: number | null;
          storage_path: string | null;
          updated_at: string;
          width: number | null;
        };
        Insert: {
          case_id: string;
          created_at?: string;
          created_by?: string | null;
          description: string;
          description_source?: string;
          duration_seconds?: number | null;
          external_url?: string | null;
          height?: number | null;
          id?: string;
          kind: string;
          mime_type?: string | null;
          position?: number;
          size_bytes?: number | null;
          storage_path?: string | null;
          updated_at?: string;
          width?: number | null;
        };
        Update: {
          case_id?: string;
          created_at?: string;
          created_by?: string | null;
          description?: string;
          description_source?: string;
          duration_seconds?: number | null;
          external_url?: string | null;
          height?: number | null;
          id?: string;
          kind?: string;
          mime_type?: string | null;
          position?: number;
          size_bytes?: number | null;
          storage_path?: string | null;
          updated_at?: string;
          width?: number | null;
        };
        Relationships: [
          {
            foreignKeyName: "support_case_media_case_id_fkey";
            columns: ["case_id"];
            isOneToOne: false;
            referencedRelation: "support_cases";
            referencedColumns: ["id"];
          },
        ];
      };
      support_cases: {
        Row: {
          category: string;
          content_updated_at: string;
          created_at: string;
          created_by: string | null;
          id: string;
          search_vector: unknown;
          solution: string;
          source: string;
          source_ref: string | null;
          status: string;
          symptoms: string;
          tags: string[];
          title: string;
          updated_at: string;
          updated_by: string | null;
          verified_at: string | null;
          verified_by: string | null;
        };
        Insert: {
          category: string;
          content_updated_at?: string;
          created_at?: string;
          created_by?: string | null;
          id?: string;
          search_vector?: unknown;
          solution: string;
          source?: string;
          source_ref?: string | null;
          status?: string;
          symptoms: string;
          tags?: string[];
          title: string;
          updated_at?: string;
          updated_by?: string | null;
          verified_at?: string | null;
          verified_by?: string | null;
        };
        Update: {
          category?: string;
          content_updated_at?: string;
          created_at?: string;
          created_by?: string | null;
          id?: string;
          search_vector?: unknown;
          solution?: string;
          source?: string;
          source_ref?: string | null;
          status?: string;
          symptoms?: string;
          tags?: string[];
          title?: string;
          updated_at?: string;
          updated_by?: string | null;
          verified_at?: string | null;
          verified_by?: string | null;
        };
        Relationships: [];
      };
      support_conversations: {
        Row: {
          created_at: string;
          effort: string;
          escalation_note: string | null;
          id: string;
          mode: string;
          model: string;
          prompt_version: string;
          resulting_case_id: string | null;
          status: string;
          status_changed_at: string | null;
          ticket_import_job_id: string | null;
          title: string;
          updated_at: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          effort: string;
          escalation_note?: string | null;
          id?: string;
          mode?: string;
          model: string;
          prompt_version: string;
          resulting_case_id?: string | null;
          status?: string;
          status_changed_at?: string | null;
          ticket_import_job_id?: string | null;
          title: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          effort?: string;
          escalation_note?: string | null;
          id?: string;
          mode?: string;
          model?: string;
          prompt_version?: string;
          resulting_case_id?: string | null;
          status?: string;
          status_changed_at?: string | null;
          ticket_import_job_id?: string | null;
          title?: string;
          updated_at?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "support_conversations_resulting_case_id_fkey";
            columns: ["resulting_case_id"];
            isOneToOne: false;
            referencedRelation: "support_cases";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "support_conversations_ticket_import_job_id_fkey";
            columns: ["ticket_import_job_id"];
            isOneToOne: false;
            referencedRelation: "ticket_import_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      support_messages: {
        Row: {
          cited_case_ids: string[];
          content: Json;
          conversation_id: string;
          created_at: string;
          display: Json | null;
          id: string;
          kind: string;
          latency_ms: number | null;
          model: string | null;
          role: string;
          seq: number;
          stop_reason: string | null;
          turn_id: string;
          usage: Json | null;
        };
        Insert: {
          cited_case_ids?: string[];
          content: Json;
          conversation_id: string;
          created_at?: string;
          display?: Json | null;
          id?: string;
          kind: string;
          latency_ms?: number | null;
          model?: string | null;
          role: string;
          seq: number;
          stop_reason?: string | null;
          turn_id: string;
          usage?: Json | null;
        };
        Update: {
          cited_case_ids?: string[];
          content?: Json;
          conversation_id?: string;
          created_at?: string;
          display?: Json | null;
          id?: string;
          kind?: string;
          latency_ms?: number | null;
          model?: string | null;
          role?: string;
          seq?: number;
          stop_reason?: string | null;
          turn_id?: string;
          usage?: Json | null;
        };
        Relationships: [
          {
            foreignKeyName: "support_messages_conversation_id_fkey";
            columns: ["conversation_id"];
            isOneToOne: false;
            referencedRelation: "support_conversations";
            referencedColumns: ["id"];
          },
        ];
      };
      support_search_events: {
        Row: {
          category: string | null;
          conversation_id: string;
          created_at: string;
          id: string;
          match_kind: string;
          query: string;
          result_case_ids: string[];
          result_layouts: Json;
          tool_use_id: string;
          turn_id: string;
        };
        Insert: {
          category?: string | null;
          conversation_id: string;
          created_at?: string;
          id?: string;
          match_kind: string;
          query: string;
          result_case_ids?: string[];
          result_layouts?: Json;
          tool_use_id: string;
          turn_id: string;
        };
        Update: {
          category?: string | null;
          conversation_id?: string;
          created_at?: string;
          id?: string;
          match_kind?: string;
          query?: string;
          result_case_ids?: string[];
          result_layouts?: Json;
          tool_use_id?: string;
          turn_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "support_search_events_conversation_id_fkey";
            columns: ["conversation_id"];
            isOneToOne: false;
            referencedRelation: "support_conversations";
            referencedColumns: ["id"];
          },
        ];
      };
      temp_auth_tokens: {
        Row: {
          access_token: string | null;
          created_at: string;
          expires_at: string | null;
          user_id: string;
        };
        Insert: {
          access_token?: string | null;
          created_at?: string;
          expires_at?: string | null;
          user_id?: string;
        };
        Update: {
          access_token?: string | null;
          created_at?: string;
          expires_at?: string | null;
          user_id?: string;
        };
        Relationships: [];
      };
      temp_mark_normand_run_data: {
        Row: {
          created_at: string;
          environment: string | null;
          error: number | null;
          id: number;
          is_dryrun: boolean;
          run_status: string | null;
          send_to_super_admins: boolean | null;
          sent: number | null;
          since: number | null;
          skipped: number | null;
          total: number | null;
          until: number | null;
          userid: string | null;
        };
        Insert: {
          created_at?: string;
          environment?: string | null;
          error?: number | null;
          id?: number;
          is_dryrun?: boolean;
          run_status?: string | null;
          send_to_super_admins?: boolean | null;
          sent?: number | null;
          since?: number | null;
          skipped?: number | null;
          total?: number | null;
          until?: number | null;
          userid?: string | null;
        };
        Update: {
          created_at?: string;
          environment?: string | null;
          error?: number | null;
          id?: number;
          is_dryrun?: boolean;
          run_status?: string | null;
          send_to_super_admins?: boolean | null;
          sent?: number | null;
          since?: number | null;
          skipped?: number | null;
          total?: number | null;
          until?: number | null;
          userid?: string | null;
        };
        Relationships: [];
      };
      test_best_user_scores: {
        Row: {
          rank_index: number | null;
          total_score: number | null;
          user_id: string | null;
        };
        Insert: {
          rank_index?: number | null;
          total_score?: number | null;
          user_id?: string | null;
        };
        Update: {
          rank_index?: number | null;
          total_score?: number | null;
          user_id?: string | null;
        };
        Relationships: [];
      };
      test_comedian_dashboard_ticket_data: {
        Row: {
          capacity: number | null;
          city: string | null;
          comedian_id: string | null;
          ingestion_date: string | null;
          matched_venue_name: string | null;
          net_cap: number | null;
          reporting_date: string | null;
          sales: number | null;
          show_id: string | null;
          show_time_timestamp: string | null;
          state: string | null;
          unique_id: string;
          venue: string | null;
          venue_id: string | null;
        };
        Insert: {
          capacity?: number | null;
          city?: string | null;
          comedian_id?: string | null;
          ingestion_date?: string | null;
          matched_venue_name?: string | null;
          net_cap?: number | null;
          reporting_date?: string | null;
          sales?: number | null;
          show_id?: string | null;
          show_time_timestamp?: string | null;
          state?: string | null;
          unique_id: string;
          venue?: string | null;
          venue_id?: string | null;
        };
        Update: {
          capacity?: number | null;
          city?: string | null;
          comedian_id?: string | null;
          ingestion_date?: string | null;
          matched_venue_name?: string | null;
          net_cap?: number | null;
          reporting_date?: string | null;
          sales?: number | null;
          show_id?: string | null;
          show_time_timestamp?: string | null;
          state?: string | null;
          unique_id?: string;
          venue?: string | null;
          venue_id?: string | null;
        };
        Relationships: [];
      };
      test_comedian_followers_daily: {
        Row: {
          comedian_id: string;
          cumulative_fans: number;
          ds: string;
          id: string;
          num_fans: number;
        };
        Insert: {
          comedian_id: string;
          cumulative_fans: number;
          ds: string;
          id: string;
          num_fans: number;
        };
        Update: {
          comedian_id?: string;
          cumulative_fans?: number;
          ds?: string;
          id?: string;
          num_fans?: number;
        };
        Relationships: [];
      };
      test_comedian_followers_metrics: {
        Row: {
          comedian_id: string;
          display_name: string | null;
          followers_180d: number | null;
          followers_30d: number | null;
          followers_365d: number | null;
          followers_7d: number | null;
          followers_90d: number | null;
          growth_180d: number | null;
          growth_30d: number | null;
          growth_365d: number | null;
          growth_7d: number | null;
          growth_90d: number | null;
          growth_percent_180d: number | null;
          growth_percent_30d: number | null;
          growth_percent_365d: number | null;
          growth_percent_7d: number | null;
          growth_percent_90d: number | null;
          join_date: string | null;
          last_updated: string;
          total_followers: number | null;
        };
        Insert: {
          comedian_id: string;
          display_name?: string | null;
          followers_180d?: number | null;
          followers_30d?: number | null;
          followers_365d?: number | null;
          followers_7d?: number | null;
          followers_90d?: number | null;
          growth_180d?: number | null;
          growth_30d?: number | null;
          growth_365d?: number | null;
          growth_7d?: number | null;
          growth_90d?: number | null;
          growth_percent_180d?: number | null;
          growth_percent_30d?: number | null;
          growth_percent_365d?: number | null;
          growth_percent_7d?: number | null;
          growth_percent_90d?: number | null;
          join_date?: string | null;
          last_updated: string;
          total_followers?: number | null;
        };
        Update: {
          comedian_id?: string;
          display_name?: string | null;
          followers_180d?: number | null;
          followers_30d?: number | null;
          followers_365d?: number | null;
          followers_7d?: number | null;
          followers_90d?: number | null;
          growth_180d?: number | null;
          growth_30d?: number | null;
          growth_365d?: number | null;
          growth_7d?: number | null;
          growth_90d?: number | null;
          growth_percent_180d?: number | null;
          growth_percent_30d?: number | null;
          growth_percent_365d?: number | null;
          growth_percent_7d?: number | null;
          growth_percent_90d?: number | null;
          join_date?: string | null;
          last_updated?: string;
          total_followers?: number | null;
        };
        Relationships: [];
      };
      test_comedian_followers_summary: {
        Row: {
          comedian_id: string;
          cumulative_growth_180d: number | null;
          cumulative_growth_30d: number | null;
          cumulative_growth_365d: number | null;
          cumulative_growth_7d: number | null;
          cumulative_growth_90d: number | null;
          cumulative_growth_percent_180d: number | null;
          cumulative_growth_percent_30d: number | null;
          cumulative_growth_percent_365d: number | null;
          cumulative_growth_percent_7d: number | null;
          cumulative_growth_percent_90d: number | null;
          display_name: string | null;
          join_date: string | null;
          last_updated: string;
          new_followers_180d: number | null;
          new_followers_30d: number | null;
          new_followers_365d: number | null;
          new_followers_7d: number | null;
          new_followers_90d: number | null;
          new_growth_180d: number | null;
          new_growth_30d: number | null;
          new_growth_365d: number | null;
          new_growth_7d: number | null;
          new_growth_90d: number | null;
          new_growth_percent_180d: number | null;
          new_growth_percent_30d: number | null;
          new_growth_percent_365d: number | null;
          new_growth_percent_7d: number | null;
          new_growth_percent_90d: number | null;
          total_followers: number | null;
        };
        Insert: {
          comedian_id: string;
          cumulative_growth_180d?: number | null;
          cumulative_growth_30d?: number | null;
          cumulative_growth_365d?: number | null;
          cumulative_growth_7d?: number | null;
          cumulative_growth_90d?: number | null;
          cumulative_growth_percent_180d?: number | null;
          cumulative_growth_percent_30d?: number | null;
          cumulative_growth_percent_365d?: number | null;
          cumulative_growth_percent_7d?: number | null;
          cumulative_growth_percent_90d?: number | null;
          display_name?: string | null;
          join_date?: string | null;
          last_updated: string;
          new_followers_180d?: number | null;
          new_followers_30d?: number | null;
          new_followers_365d?: number | null;
          new_followers_7d?: number | null;
          new_followers_90d?: number | null;
          new_growth_180d?: number | null;
          new_growth_30d?: number | null;
          new_growth_365d?: number | null;
          new_growth_7d?: number | null;
          new_growth_90d?: number | null;
          new_growth_percent_180d?: number | null;
          new_growth_percent_30d?: number | null;
          new_growth_percent_365d?: number | null;
          new_growth_percent_7d?: number | null;
          new_growth_percent_90d?: number | null;
          total_followers?: number | null;
        };
        Update: {
          comedian_id?: string;
          cumulative_growth_180d?: number | null;
          cumulative_growth_30d?: number | null;
          cumulative_growth_365d?: number | null;
          cumulative_growth_7d?: number | null;
          cumulative_growth_90d?: number | null;
          cumulative_growth_percent_180d?: number | null;
          cumulative_growth_percent_30d?: number | null;
          cumulative_growth_percent_365d?: number | null;
          cumulative_growth_percent_7d?: number | null;
          cumulative_growth_percent_90d?: number | null;
          display_name?: string | null;
          join_date?: string | null;
          last_updated?: string;
          new_followers_180d?: number | null;
          new_followers_30d?: number | null;
          new_followers_365d?: number | null;
          new_followers_7d?: number | null;
          new_followers_90d?: number | null;
          new_growth_180d?: number | null;
          new_growth_30d?: number | null;
          new_growth_365d?: number | null;
          new_growth_7d?: number | null;
          new_growth_90d?: number | null;
          new_growth_percent_180d?: number | null;
          new_growth_percent_30d?: number | null;
          new_growth_percent_365d?: number | null;
          new_growth_percent_7d?: number | null;
          new_growth_percent_90d?: number | null;
          total_followers?: number | null;
        };
        Relationships: [];
      };
      test_comedian_ticket_data_int: {
        Row: {
          capacity: number | null;
          city: string | null;
          comedian_id: string | null;
          country: string | null;
          ds: string | null;
          geographic_division: string | null;
          geographic_region: string | null;
          net_cap: number | null;
          onsale_date: string | null;
          pct_sales: number | null;
          prev_ds: string | null;
          prev_sales: string | null;
          sales: number | null;
          show_id: string | null;
          show_time: string | null;
          show_time_has_passed: boolean | null;
          state: string | null;
          venue: string | null;
          venue_id: string | null;
        };
        Insert: {
          capacity?: number | null;
          city?: string | null;
          comedian_id?: string | null;
          country?: string | null;
          ds?: string | null;
          geographic_division?: string | null;
          geographic_region?: string | null;
          net_cap?: number | null;
          onsale_date?: string | null;
          pct_sales?: number | null;
          prev_ds?: string | null;
          prev_sales?: string | null;
          sales?: number | null;
          show_id?: string | null;
          show_time?: string | null;
          show_time_has_passed?: boolean | null;
          state?: string | null;
          venue?: string | null;
          venue_id?: string | null;
        };
        Update: {
          capacity?: number | null;
          city?: string | null;
          comedian_id?: string | null;
          country?: string | null;
          ds?: string | null;
          geographic_division?: string | null;
          geographic_region?: string | null;
          net_cap?: number | null;
          onsale_date?: string | null;
          pct_sales?: number | null;
          prev_ds?: string | null;
          prev_sales?: string | null;
          sales?: number | null;
          show_id?: string | null;
          show_time?: string | null;
          show_time_has_passed?: boolean | null;
          state?: string | null;
          venue?: string | null;
          venue_id?: string | null;
        };
        Relationships: [];
      };
      test_comedian_venue_followers_summary: {
        Row: {
          comedian_id: string;
          comedian_id_update_time: string;
          ingestion_time: string | null;
          total_followers: number | null;
          total_followers_within_50_miles: number | null;
          venues_data: Json | null;
        };
        Insert: {
          comedian_id: string;
          comedian_id_update_time: string;
          ingestion_time?: string | null;
          total_followers?: number | null;
          total_followers_within_50_miles?: number | null;
          venues_data?: Json | null;
        };
        Update: {
          comedian_id?: string;
          comedian_id_update_time?: string;
          ingestion_time?: string | null;
          total_followers?: number | null;
          total_followers_within_50_miles?: number | null;
          venues_data?: Json | null;
        };
        Relationships: [];
      };
      test_event_sales_summary: {
        Row: {
          average_ticket_price: number;
          event_capacity: number;
          event_date: string;
          event_id: string;
          event_name: string;
          open: number;
          percent_remaining: number;
          promos_lifetime: Json | null;
          referrers_1d: Json;
          referrers_28d: Json;
          referrers_7d: Json;
          referrers_lifetime: Json;
          revenue_1d_amount: number;
          revenue_1d_growth: number;
          revenue_28d_amount: number;
          revenue_28d_growth: number;
          revenue_7d_amount: number;
          revenue_7d_growth: number;
          status: string;
          tickets_1d_count: number;
          tickets_1d_growth: number;
          tickets_28d_count: number;
          tickets_28d_growth: number;
          tickets_7d_count: number;
          tickets_7d_growth: number;
          tickets_breakdown: Json | null;
          timezone: string | null;
          total_revenue: number;
          total_tickets_sold: number;
          updated_at: string;
          venue_id: string;
        };
        Insert: {
          average_ticket_price: number;
          event_capacity: number;
          event_date: string;
          event_id: string;
          event_name: string;
          open: number;
          percent_remaining: number;
          promos_lifetime?: Json | null;
          referrers_1d?: Json;
          referrers_28d?: Json;
          referrers_7d?: Json;
          referrers_lifetime?: Json;
          revenue_1d_amount: number;
          revenue_1d_growth: number;
          revenue_28d_amount: number;
          revenue_28d_growth: number;
          revenue_7d_amount: number;
          revenue_7d_growth: number;
          status: string;
          tickets_1d_count: number;
          tickets_1d_growth: number;
          tickets_28d_count: number;
          tickets_28d_growth: number;
          tickets_7d_count: number;
          tickets_7d_growth: number;
          tickets_breakdown?: Json | null;
          timezone?: string | null;
          total_revenue: number;
          total_tickets_sold: number;
          updated_at: string;
          venue_id: string;
        };
        Update: {
          average_ticket_price?: number;
          event_capacity?: number;
          event_date?: string;
          event_id?: string;
          event_name?: string;
          open?: number;
          percent_remaining?: number;
          promos_lifetime?: Json | null;
          referrers_1d?: Json;
          referrers_28d?: Json;
          referrers_7d?: Json;
          referrers_lifetime?: Json;
          revenue_1d_amount?: number;
          revenue_1d_growth?: number;
          revenue_28d_amount?: number;
          revenue_28d_growth?: number;
          revenue_7d_amount?: number;
          revenue_7d_growth?: number;
          status?: string;
          tickets_1d_count?: number;
          tickets_1d_growth?: number;
          tickets_28d_count?: number;
          tickets_28d_growth?: number;
          tickets_7d_count?: number;
          tickets_7d_growth?: number;
          tickets_breakdown?: Json | null;
          timezone?: string | null;
          total_revenue?: number;
          total_tickets_sold?: number;
          updated_at?: string;
          venue_id?: string;
        };
        Relationships: [];
      };
      test_events: {
        Row: {
          created_at: string;
          id: number;
          name: string;
          value: number;
        };
        Insert: {
          created_at?: string;
          id?: number;
          name: string;
          value: number;
        };
        Update: {
          created_at?: string;
          id?: number;
          name?: string;
          value?: number;
        };
        Relationships: [];
      };
      test_events_clicked_play: {
        Row: {
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          city: string | null;
          comedian_id: string | null;
          distinct_id: string | null;
          event: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          special_id: string | null;
          specialName: string | null;
          state: string | null;
          time: string | null;
          timestamp: string | null;
        };
        Insert: {
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          city?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          specialName?: string | null;
          state?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Update: {
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          city?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          specialName?: string | null;
          state?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Relationships: [];
      };
      test_events_followed_comedian: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $region: string | null;
          ComedianId: string | null;
          distinct_id: string | null;
          event: string | null;
          event_1: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          time: string | null;
          timestamp: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $region?: string | null;
          ComedianId?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          event_1?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $region?: string | null;
          ComedianId?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          event_1?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Relationships: [];
      };
      test_events_tickets_merged: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          ComedianId: string | null;
          ComedianName: string | null;
          distinct_id: string | null;
          event: string | null;
          fbclid: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: number | null;
          longitude: number | null;
          mp_country_code: string | null;
          ShowDate: string | null;
          ShowId: string | null;
          ShowVenue: string | null;
          Source: string | null;
          time: number | null;
          timestamp: string | null;
          utm_campaign: string | null;
          utm_medium: string | null;
          utm_source: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          ComedianId?: string | null;
          ComedianName?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          fbclid?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: number | null;
          longitude?: number | null;
          mp_country_code?: string | null;
          ShowDate?: string | null;
          ShowId?: string | null;
          ShowVenue?: string | null;
          Source?: string | null;
          time?: number | null;
          timestamp?: string | null;
          utm_campaign?: string | null;
          utm_medium?: string | null;
          utm_source?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          ComedianId?: string | null;
          ComedianName?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          fbclid?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: number | null;
          longitude?: number | null;
          mp_country_code?: string | null;
          ShowDate?: string | null;
          ShowId?: string | null;
          ShowVenue?: string | null;
          Source?: string | null;
          time?: number | null;
          timestamp?: string | null;
          utm_campaign?: string | null;
          utm_medium?: string | null;
          utm_source?: string | null;
        };
        Relationships: [];
      };
      test_events_video_session_started: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          comedian_id: string | null;
          distinct_id: string | null;
          event: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          special_id: string | null;
          special_name: string | null;
          time: string | null;
          timestamp: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "test_events_video_session_started_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      test_events_video_viewed_10s: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          comedian_id: string | null;
          distinct_id: string | null;
          event: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          special_id: string | null;
          special_name: string | null;
          time: string | null;
          timestamp: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "test_events_video_viewed_10s_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      test_events_video_viewed_30s: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          comedian_id: string | null;
          distinct_id: string | null;
          event: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          special_id: string | null;
          special_name: string | null;
          time: string | null;
          timestamp: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "test_events_video_viewed_30s_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      test_events_video_viewed_3s: {
        Row: {
          $city: string | null;
          $current_url: string | null;
          $initial_referrer: string | null;
          $initial_referring_domain: string | null;
          $referrer: string | null;
          $referring_domain: string | null;
          $region: string | null;
          comedian_id: string | null;
          distinct_id: string | null;
          event: string | null;
          id: string;
          ingestion_time: string | null;
          latitude: string | null;
          longitude: string | null;
          mp_country_code: string | null;
          special_id: string | null;
          special_name: string | null;
          time: string | null;
          timestamp: string | null;
        };
        Insert: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Update: {
          $city?: string | null;
          $current_url?: string | null;
          $initial_referrer?: string | null;
          $initial_referring_domain?: string | null;
          $referrer?: string | null;
          $referring_domain?: string | null;
          $region?: string | null;
          comedian_id?: string | null;
          distinct_id?: string | null;
          event?: string | null;
          id?: string;
          ingestion_time?: string | null;
          latitude?: string | null;
          longitude?: string | null;
          mp_country_code?: string | null;
          special_id?: string | null;
          special_name?: string | null;
          time?: string | null;
          timestamp?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "test_events_video_viewed_3s_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      test_user_rankings_april25: {
        Row: {
          total_score: number;
          user_id: string;
        };
        Insert: {
          total_score: number;
          user_id?: string;
        };
        Update: {
          total_score?: number;
          user_id?: string;
        };
        Relationships: [];
      };
      test_users_pool: {
        Row: {
          email: string | null;
          id: string;
          is_in_use: boolean;
          last_updated_at: string | null;
        };
        Insert: {
          email?: string | null;
          id: string;
          is_in_use?: boolean;
          last_updated_at?: string | null;
        };
        Update: {
          email?: string | null;
          id?: string;
          is_in_use?: boolean;
          last_updated_at?: string | null;
        };
        Relationships: [];
      };
      test_venue_sales_data_raw: {
        Row: {
          attribution: Json | null;
          basket_size: number | null;
          event_capacity: number | null;
          event_name: string | null;
          initial_referrer: string | null;
          order_status: string | null;
          payout_amount: number | null;
          purchase_amount: number | null;
          show_date: string | null;
          show_id: string | null;
          ticket_sale_id: string;
          tickets: Json | null;
          timestamp: string | null;
          tixologi_event_id: string | null;
          venue_page_id: string | null;
        };
        Insert: {
          attribution?: Json | null;
          basket_size?: number | null;
          event_capacity?: number | null;
          event_name?: string | null;
          initial_referrer?: string | null;
          order_status?: string | null;
          payout_amount?: number | null;
          purchase_amount?: number | null;
          show_date?: string | null;
          show_id?: string | null;
          ticket_sale_id: string;
          tickets?: Json | null;
          timestamp?: string | null;
          tixologi_event_id?: string | null;
          venue_page_id?: string | null;
        };
        Update: {
          attribution?: Json | null;
          basket_size?: number | null;
          event_capacity?: number | null;
          event_name?: string | null;
          initial_referrer?: string | null;
          order_status?: string | null;
          payout_amount?: number | null;
          purchase_amount?: number | null;
          show_date?: string | null;
          show_id?: string | null;
          ticket_sale_id?: string;
          tickets?: Json | null;
          timestamp?: string | null;
          tixologi_event_id?: string | null;
          venue_page_id?: string | null;
        };
        Relationships: [];
      };
      ticket_als_show_factors: {
        Row: {
          factor_index: number;
          factor_value: number;
          model_version: number;
          show_id: string;
          sync_date: string;
          trained_at: string;
        };
        Insert: {
          factor_index: number;
          factor_value: number;
          model_version: number;
          show_id: string;
          sync_date?: string;
          trained_at: string;
        };
        Update: {
          factor_index?: number;
          factor_value?: number;
          model_version?: number;
          show_id?: string;
          sync_date?: string;
          trained_at?: string;
        };
        Relationships: [];
      };
      ticket_als_user_factors: {
        Row: {
          factor_index: number;
          factor_value: number;
          model_version: number;
          sync_date: string;
          trained_at: string;
          user_id: string;
        };
        Insert: {
          factor_index: number;
          factor_value: number;
          model_version: number;
          sync_date?: string;
          trained_at: string;
          user_id: string;
        };
        Update: {
          factor_index?: number;
          factor_value?: number;
          model_version?: number;
          sync_date?: string;
          trained_at?: string;
          user_id?: string;
        };
        Relationships: [];
      };
      ticket_import_bundles: {
        Row: {
          bundle_hash: string;
          counts: Json;
          created_at: string;
          event_refs: string[];
          frozen_by: string | null;
          group_number: number;
          id: string;
          import_ref: string;
          job_id: string;
          purged_at: string | null;
          revision: number;
          size_bytes: number;
          storage_path: string;
        };
        Insert: {
          bundle_hash: string;
          counts: Json;
          created_at?: string;
          event_refs: string[];
          frozen_by?: string | null;
          group_number: number;
          id?: string;
          import_ref: string;
          job_id: string;
          purged_at?: string | null;
          revision: number;
          size_bytes: number;
          storage_path: string;
        };
        Update: {
          bundle_hash?: string;
          counts?: Json;
          created_at?: string;
          event_refs?: string[];
          frozen_by?: string | null;
          group_number?: number;
          id?: string;
          import_ref?: string;
          job_id?: string;
          purged_at?: string | null;
          revision?: number;
          size_bytes?: number;
          storage_path?: string;
        };
        Relationships: [
          {
            foreignKeyName: "ticket_import_bundles_job_id_fkey";
            columns: ["job_id"];
            isOneToOne: false;
            referencedRelation: "ticket_import_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      ticket_import_files: {
        Row: {
          created_at: string;
          header_fingerprint: string | null;
          headers: Json | null;
          id: string;
          job_id: string;
          mime: string;
          original_name: string;
          parse_error: string | null;
          parsed_storage_path: string | null;
          profile: Json | null;
          purged_at: string | null;
          role: string | null;
          row_count: number | null;
          sha256: string | null;
          sheet_name: string | null;
          size_bytes: number;
          status: string;
          storage_path: string;
          updated_at: string;
          uploaded_by: string | null;
        };
        Insert: {
          created_at?: string;
          header_fingerprint?: string | null;
          headers?: Json | null;
          id?: string;
          job_id: string;
          mime: string;
          original_name: string;
          parse_error?: string | null;
          parsed_storage_path?: string | null;
          profile?: Json | null;
          purged_at?: string | null;
          role?: string | null;
          row_count?: number | null;
          sha256?: string | null;
          sheet_name?: string | null;
          size_bytes: number;
          status?: string;
          storage_path: string;
          updated_at?: string;
          uploaded_by?: string | null;
        };
        Update: {
          created_at?: string;
          header_fingerprint?: string | null;
          headers?: Json | null;
          id?: string;
          job_id?: string;
          mime?: string;
          original_name?: string;
          parse_error?: string | null;
          parsed_storage_path?: string | null;
          profile?: Json | null;
          purged_at?: string | null;
          role?: string | null;
          row_count?: number | null;
          sha256?: string | null;
          sheet_name?: string | null;
          size_bytes?: number;
          status?: string;
          storage_path?: string;
          updated_at?: string;
          uploaded_by?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "ticket_import_files_job_id_fkey";
            columns: ["job_id"];
            isOneToOne: false;
            referencedRelation: "ticket_import_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      ticket_import_incongruences: {
        Row: {
          answer: Json | null;
          code: string;
          created_at: string;
          id: string;
          job_id: string;
          message: string;
          note: string | null;
          path: string | null;
          resolution: string | null;
          resolved_at: string | null;
          resolved_by: string | null;
          revision: number;
          severity: string;
          source: string;
          subject: Json | null;
        };
        Insert: {
          answer?: Json | null;
          code: string;
          created_at?: string;
          id?: string;
          job_id: string;
          message: string;
          note?: string | null;
          path?: string | null;
          resolution?: string | null;
          resolved_at?: string | null;
          resolved_by?: string | null;
          revision: number;
          severity: string;
          source: string;
          subject?: Json | null;
        };
        Update: {
          answer?: Json | null;
          code?: string;
          created_at?: string;
          id?: string;
          job_id?: string;
          message?: string;
          note?: string | null;
          path?: string | null;
          resolution?: string | null;
          resolved_at?: string | null;
          resolved_by?: string | null;
          revision?: number;
          severity?: string;
          source?: string;
          subject?: Json | null;
        };
        Relationships: [
          {
            foreignKeyName: "ticket_import_incongruences_job_id_fkey";
            columns: ["job_id"];
            isOneToOne: false;
            referencedRelation: "ticket_import_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      ticket_import_job_events: {
        Row: {
          event_ref: string;
          first_day: string | null;
          job_id: string;
          name: string;
          prod_event_id: number | null;
          prod_show_id: string | null;
          prod_status: string;
          test_event_id: number | null;
          test_show_id: string | null;
          test_status: string;
          updated_at: string;
        };
        Insert: {
          event_ref: string;
          first_day?: string | null;
          job_id: string;
          name: string;
          prod_event_id?: number | null;
          prod_show_id?: string | null;
          prod_status?: string;
          test_event_id?: number | null;
          test_show_id?: string | null;
          test_status?: string;
          updated_at?: string;
        };
        Update: {
          event_ref?: string;
          first_day?: string | null;
          job_id?: string;
          name?: string;
          prod_event_id?: number | null;
          prod_show_id?: string | null;
          prod_status?: string;
          test_event_id?: number | null;
          test_show_id?: string | null;
          test_status?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "ticket_import_job_events_job_id_fkey";
            columns: ["job_id"];
            isOneToOne: false;
            referencedRelation: "ticket_import_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      ticket_import_jobs: {
        Row: {
          archived_at: string | null;
          archived_by: string | null;
          created_at: string;
          created_by: string | null;
          delivery_hours: number | null;
          delivery_mode: string | null;
          delta_of_revision: number | null;
          expected_totals: Json | null;
          frozen_at: string | null;
          frozen_by: string | null;
          id: string;
          partner_id_prod: number;
          partner_id_test: number | null;
          partner_prod_verified_at: string | null;
          partner_prod_verified_by: string | null;
          partner_test_verified_at: string | null;
          partner_test_verified_by: string | null;
          post_checked_at: string | null;
          purge_after: string | null;
          revision: number;
          run_lease_holder: string | null;
          run_lease_until: string | null;
          side_state: string | null;
          source_exported_at: string | null;
          source_platform: string;
          stage: string;
          test_seats_charts: Json;
          title: string;
          updated_at: string;
          venue_acknowledged_at: string | null;
          venue_acknowledged_by: string | null;
          venue_acknowledgement: Json | null;
          venue_page_id: string;
          venue_page_id_test: string | null;
          visited_steps: string[];
        };
        Insert: {
          archived_at?: string | null;
          archived_by?: string | null;
          created_at?: string;
          created_by?: string | null;
          delivery_hours?: number | null;
          delivery_mode?: string | null;
          delta_of_revision?: number | null;
          expected_totals?: Json | null;
          frozen_at?: string | null;
          frozen_by?: string | null;
          id?: string;
          partner_id_prod: number;
          partner_id_test?: number | null;
          partner_prod_verified_at?: string | null;
          partner_prod_verified_by?: string | null;
          partner_test_verified_at?: string | null;
          partner_test_verified_by?: string | null;
          post_checked_at?: string | null;
          purge_after?: string | null;
          revision?: number;
          run_lease_holder?: string | null;
          run_lease_until?: string | null;
          side_state?: string | null;
          source_exported_at?: string | null;
          source_platform: string;
          stage?: string;
          test_seats_charts?: Json;
          title: string;
          updated_at?: string;
          venue_acknowledged_at?: string | null;
          venue_acknowledged_by?: string | null;
          venue_acknowledgement?: Json | null;
          venue_page_id: string;
          venue_page_id_test?: string | null;
          visited_steps?: string[];
        };
        Update: {
          archived_at?: string | null;
          archived_by?: string | null;
          created_at?: string;
          created_by?: string | null;
          delivery_hours?: number | null;
          delivery_mode?: string | null;
          delta_of_revision?: number | null;
          expected_totals?: Json | null;
          frozen_at?: string | null;
          frozen_by?: string | null;
          id?: string;
          partner_id_prod?: number;
          partner_id_test?: number | null;
          partner_prod_verified_at?: string | null;
          partner_prod_verified_by?: string | null;
          partner_test_verified_at?: string | null;
          partner_test_verified_by?: string | null;
          post_checked_at?: string | null;
          purge_after?: string | null;
          revision?: number;
          run_lease_holder?: string | null;
          run_lease_until?: string | null;
          side_state?: string | null;
          source_exported_at?: string | null;
          source_platform?: string;
          stage?: string;
          test_seats_charts?: Json;
          title?: string;
          updated_at?: string;
          venue_acknowledged_at?: string | null;
          venue_acknowledged_by?: string | null;
          venue_acknowledgement?: Json | null;
          venue_page_id?: string;
          venue_page_id_test?: string | null;
          visited_steps?: string[];
        };
        Relationships: [
          {
            foreignKeyName: "ticket_import_jobs_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      ticket_import_mappings: {
        Row: {
          approved_at: string | null;
          approved_by: string | null;
          body: Json;
          created_at: string;
          id: string;
          job_id: string;
          kind: string;
          proposed_by_message: string | null;
          proposed_by_user: string | null;
          status: string;
          version: number;
        };
        Insert: {
          approved_at?: string | null;
          approved_by?: string | null;
          body: Json;
          created_at?: string;
          id?: string;
          job_id: string;
          kind: string;
          proposed_by_message?: string | null;
          proposed_by_user?: string | null;
          status?: string;
          version: number;
        };
        Update: {
          approved_at?: string | null;
          approved_by?: string | null;
          body?: Json;
          created_at?: string;
          id?: string;
          job_id?: string;
          kind?: string;
          proposed_by_message?: string | null;
          proposed_by_user?: string | null;
          status?: string;
          version?: number;
        };
        Relationships: [
          {
            foreignKeyName: "ticket_import_mappings_job_id_fkey";
            columns: ["job_id"];
            isOneToOne: false;
            referencedRelation: "ticket_import_jobs";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ticket_import_mappings_proposed_by_message_fkey";
            columns: ["proposed_by_message"];
            isOneToOne: false;
            referencedRelation: "support_messages";
            referencedColumns: ["id"];
          },
        ];
      };
      ticket_import_order_results: {
        Row: {
          bundle_id: string;
          bundle_index: number;
          env: string;
          event_ids: number[];
          excluded_at: string | null;
          excluded_by: string | null;
          excluded_reason: string | null;
          external_order_id: string;
          job_id: string;
          reason: string | null;
          status: string;
          ticket_ids: number[];
          transaction_ids: number[];
          updated_at: string;
          user_id: number | null;
          warnings: Json;
        };
        Insert: {
          bundle_id: string;
          bundle_index: number;
          env: string;
          event_ids?: number[];
          excluded_at?: string | null;
          excluded_by?: string | null;
          excluded_reason?: string | null;
          external_order_id: string;
          job_id: string;
          reason?: string | null;
          status: string;
          ticket_ids?: number[];
          transaction_ids?: number[];
          updated_at?: string;
          user_id?: number | null;
          warnings?: Json;
        };
        Update: {
          bundle_id?: string;
          bundle_index?: number;
          env?: string;
          event_ids?: number[];
          excluded_at?: string | null;
          excluded_by?: string | null;
          excluded_reason?: string | null;
          external_order_id?: string;
          job_id?: string;
          reason?: string | null;
          status?: string;
          ticket_ids?: number[];
          transaction_ids?: number[];
          updated_at?: string;
          user_id?: number | null;
          warnings?: Json;
        };
        Relationships: [
          {
            foreignKeyName: "ticket_import_order_results_bundle_job_fkey";
            columns: ["bundle_id", "job_id"];
            isOneToOne: false;
            referencedRelation: "ticket_import_bundles";
            referencedColumns: ["id", "job_id"];
          },
        ];
      };
      ticket_import_partner_links: {
        Row: {
          partner_id_test: number;
          set_at: string;
          set_by: string | null;
          venue_page_id: string;
          venue_page_id_test: string | null;
        };
        Insert: {
          partner_id_test: number;
          set_at?: string;
          set_by?: string | null;
          venue_page_id: string;
          venue_page_id_test?: string | null;
        };
        Update: {
          partner_id_test?: number;
          set_at?: string;
          set_by?: string | null;
          venue_page_id?: string;
          venue_page_id_test?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "ticket_import_partner_links_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: true;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      ticket_import_runs: {
        Row: {
          action: string;
          actor_email: string;
          actor_id: string | null;
          bundle_id: string | null;
          confirm: Json | null;
          env: string;
          error: string | null;
          finished_at: string | null;
          http_status: number | null;
          id: string;
          job_id: string;
          plan_hash: string | null;
          request_summary: Json;
          result: Json | null;
          started_at: string;
          status: string;
        };
        Insert: {
          action: string;
          actor_email: string;
          actor_id?: string | null;
          bundle_id?: string | null;
          confirm?: Json | null;
          env: string;
          error?: string | null;
          finished_at?: string | null;
          http_status?: number | null;
          id?: string;
          job_id: string;
          plan_hash?: string | null;
          request_summary?: Json;
          result?: Json | null;
          started_at?: string;
          status?: string;
        };
        Update: {
          action?: string;
          actor_email?: string;
          actor_id?: string | null;
          bundle_id?: string | null;
          confirm?: Json | null;
          env?: string;
          error?: string | null;
          finished_at?: string | null;
          http_status?: number | null;
          id?: string;
          job_id?: string;
          plan_hash?: string | null;
          request_summary?: Json;
          result?: Json | null;
          started_at?: string;
          status?: string;
        };
        Relationships: [
          {
            foreignKeyName: "ticket_import_runs_bundle_id_fkey";
            columns: ["bundle_id"];
            isOneToOne: false;
            referencedRelation: "ticket_import_bundles";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "ticket_import_runs_job_id_fkey";
            columns: ["job_id"];
            isOneToOne: false;
            referencedRelation: "ticket_import_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      ticket_import_stage_approvals: {
        Row: {
          actor_email: string;
          actor_id: string | null;
          bundle_hashes: string[];
          created_at: string;
          decision: string;
          event_ref_scope: string[] | null;
          event_scope: number[] | null;
          id: string;
          job_id: string;
          note: string | null;
          revision: number;
          stage: string;
        };
        Insert: {
          actor_email: string;
          actor_id?: string | null;
          bundle_hashes?: string[];
          created_at?: string;
          decision: string;
          event_ref_scope?: string[] | null;
          event_scope?: number[] | null;
          id?: string;
          job_id: string;
          note?: string | null;
          revision: number;
          stage: string;
        };
        Update: {
          actor_email?: string;
          actor_id?: string | null;
          bundle_hashes?: string[];
          created_at?: string;
          decision?: string;
          event_ref_scope?: string[] | null;
          event_scope?: number[] | null;
          id?: string;
          job_id?: string;
          note?: string | null;
          revision?: number;
          stage?: string;
        };
        Relationships: [
          {
            foreignKeyName: "ticket_import_stage_approvals_job_id_fkey";
            columns: ["job_id"];
            isOneToOne: false;
            referencedRelation: "ticket_import_jobs";
            referencedColumns: ["id"];
          },
        ];
      };
      ticketing_partners: {
        Row: {
          created_at: string;
          id: string;
          timezone: Database["public"]["Enums"]["timezone"] | null;
          tixologi_partner_id: string | null;
        };
        Insert: {
          created_at?: string;
          id: string;
          timezone?: Database["public"]["Enums"]["timezone"] | null;
          tixologi_partner_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: string;
          timezone?: Database["public"]["Enums"]["timezone"] | null;
          tixologi_partner_id?: string | null;
        };
        Relationships: [];
      };
      tm_affiliate_sales: {
        Row: {
          action_date: string;
          action_id: string;
          action_tracker: string | null;
          action_tracker_id: string | null;
          campaign: string | null;
          commission: number | null;
          created_at: string | null;
          currency: string | null;
          currency_exchange_rate: number | null;
          customer_city: string | null;
          customer_country: string | null;
          customer_region: string | null;
          event_code: string | null;
          event_date: string | null;
          event_id: string | null;
          event_name: string | null;
          face: number | null;
          id: string;
          ip_address: unknown;
          major_category: string | null;
          mobile_app_conversion: boolean | null;
          mp_currency_exchange_rate: number | null;
          onsale_date: string | null;
          orders: number | null;
          originating_website: string | null;
          primary_artist_name: string | null;
          promo_voucher_code: string | null;
          rate: number | null;
          raw_data: Json | null;
          referring_url: string | null;
          secondary_artist_name: string | null;
          shared_id: string | null;
          status: string | null;
          sub_category: string | null;
          sub_category_level_2: string | null;
          sub_id1: string | null;
          sub_id2: string | null;
          sub_id3: string | null;
          tickets: number | null;
          transaction_id: string | null;
          updated_at: string | null;
          venue_city: string | null;
          venue_id: string | null;
          venue_name: string | null;
          venue_state_code: string | null;
        };
        Insert: {
          action_date: string;
          action_id: string;
          action_tracker?: string | null;
          action_tracker_id?: string | null;
          campaign?: string | null;
          commission?: number | null;
          created_at?: string | null;
          currency?: string | null;
          currency_exchange_rate?: number | null;
          customer_city?: string | null;
          customer_country?: string | null;
          customer_region?: string | null;
          event_code?: string | null;
          event_date?: string | null;
          event_id?: string | null;
          event_name?: string | null;
          face?: number | null;
          id?: string;
          ip_address?: unknown;
          major_category?: string | null;
          mobile_app_conversion?: boolean | null;
          mp_currency_exchange_rate?: number | null;
          onsale_date?: string | null;
          orders?: number | null;
          originating_website?: string | null;
          primary_artist_name?: string | null;
          promo_voucher_code?: string | null;
          rate?: number | null;
          raw_data?: Json | null;
          referring_url?: string | null;
          secondary_artist_name?: string | null;
          shared_id?: string | null;
          status?: string | null;
          sub_category?: string | null;
          sub_category_level_2?: string | null;
          sub_id1?: string | null;
          sub_id2?: string | null;
          sub_id3?: string | null;
          tickets?: number | null;
          transaction_id?: string | null;
          updated_at?: string | null;
          venue_city?: string | null;
          venue_id?: string | null;
          venue_name?: string | null;
          venue_state_code?: string | null;
        };
        Update: {
          action_date?: string;
          action_id?: string;
          action_tracker?: string | null;
          action_tracker_id?: string | null;
          campaign?: string | null;
          commission?: number | null;
          created_at?: string | null;
          currency?: string | null;
          currency_exchange_rate?: number | null;
          customer_city?: string | null;
          customer_country?: string | null;
          customer_region?: string | null;
          event_code?: string | null;
          event_date?: string | null;
          event_id?: string | null;
          event_name?: string | null;
          face?: number | null;
          id?: string;
          ip_address?: unknown;
          major_category?: string | null;
          mobile_app_conversion?: boolean | null;
          mp_currency_exchange_rate?: number | null;
          onsale_date?: string | null;
          orders?: number | null;
          originating_website?: string | null;
          primary_artist_name?: string | null;
          promo_voucher_code?: string | null;
          rate?: number | null;
          raw_data?: Json | null;
          referring_url?: string | null;
          secondary_artist_name?: string | null;
          shared_id?: string | null;
          status?: string | null;
          sub_category?: string | null;
          sub_category_level_2?: string | null;
          sub_id1?: string | null;
          sub_id2?: string | null;
          sub_id3?: string | null;
          tickets?: number | null;
          transaction_id?: string | null;
          updated_at?: string | null;
          venue_city?: string | null;
          venue_id?: string | null;
          venue_name?: string | null;
          venue_state_code?: string | null;
        };
        Relationships: [];
      };
      tm_exported_event_data: {
        Row: {
          ACCESSIBLE_SEATING_DETAIL: string | null;
          API_ONSALE_START_DATETIME: string | null;
          ATTRACTION_CLASSIFICATION_GENRE: string | null;
          ATTRACTION_CLASSIFICATION_GENRE_ID: string | null;
          ATTRACTION_CLASSIFICATION_SEGMENT: string | null;
          ATTRACTION_CLASSIFICATION_SEGMENT_ID: string | null;
          ATTRACTION_CLASSIFICATION_SUB_GENRE: string | null;
          ATTRACTION_CLASSIFICATION_SUB_GENRE_ID: string | null;
          ATTRACTION_CLASSIFICATION_SUB_TYPE: string | null;
          ATTRACTION_CLASSIFICATION_SUB_TYPE_ID: string | null;
          ATTRACTION_CLASSIFICATION_TYPE: string | null;
          ATTRACTION_CLASSIFICATION_TYPE_ID: string | null;
          ATTRACTION_ID: string | null;
          ATTRACTION_IMAGE_URL: string | null;
          ATTRACTION_NAME: string | null;
          ATTRACTION_URL: string | null;
          CLASSIFICATION_GENRE: string | null;
          CLASSIFICATION_GENRE_ID: string | null;
          CLASSIFICATION_SEGMENT: string | null;
          CLASSIFICATION_SEGMENT_ID: string | null;
          CLASSIFICATION_SUB_GENRE: string | null;
          CLASSIFICATION_SUB_GENRE_ID: string | null;
          CLASSIFICATION_SUB_TYPE: string | null;
          CLASSIFICATION_SUB_TYPE_ID: string | null;
          CLASSIFICATION_TYPE: string | null;
          CLASSIFICATION_TYPE_ID: string | null;
          CURRENCY: string | null;
          EVENT_END_DATETIME: string | null;
          EVENT_END_LOCAL_DATE: string | null;
          EVENT_ID: string | null;
          EVENT_IMAGE_URL: string | null;
          EVENT_INFO: string | null;
          EVENT_NAME: string | null;
          EVENT_NOTES: string | null;
          EVENT_START_DATETIME: string | null;
          EVENT_START_LOCAL_DATE: string | null;
          EVENT_START_LOCAL_TIME: string | null;
          EVENT_STATUS: string | null;
          HOT_EVENT: boolean | null;
          IMPORTANT_INFORMATION: string | null;
          LEGACY_ATTRACTION_ID: string | null;
          LEGACY_EVENT_ID: string | null;
          LEGACY_VENUE_ID: string | null;
          MAX_PRICE: string | null;
          MAX_PRICE_WITH_FEES: string | null;
          MIN_PRICE: string | null;
          MIN_PRICE_WITH_FEES: string | null;
          ONSALE_END_DATETIME: string | null;
          ONSALE_START_DATETIME: string | null;
          PLEASE_NOTE: string | null;
          PRESALE_END_DATETIME: string | null;
          "PRESALE_NAME.1": string | null;
          PRESALE_START_DATETIME: string | null;
          PRIMARY_EVENT_URL: string | null;
          PROMOTER_ID: string | null;
          PROMOTER_NAME: string | null;
          SOURCE: string | null;
          TRANSACTABLE: string | null;
          VENUE_CITY: string | null;
          VENUE_COUNTRY_CODE: string | null;
          VENUE_ID: string | null;
          VENUE_LATITUDE: number | null;
          VENUE_LONGITUDE: number | null;
          VENUE_NAME: string | null;
          VENUE_STATE_CODE: string | null;
          VENUE_STREET: string | null;
          VENUE_TIMEZONE: string | null;
          VENUE_URL: string | null;
          VENUE_ZIP_CODE: number | null;
        };
        Insert: {
          ACCESSIBLE_SEATING_DETAIL?: string | null;
          API_ONSALE_START_DATETIME?: string | null;
          ATTRACTION_CLASSIFICATION_GENRE?: string | null;
          ATTRACTION_CLASSIFICATION_GENRE_ID?: string | null;
          ATTRACTION_CLASSIFICATION_SEGMENT?: string | null;
          ATTRACTION_CLASSIFICATION_SEGMENT_ID?: string | null;
          ATTRACTION_CLASSIFICATION_SUB_GENRE?: string | null;
          ATTRACTION_CLASSIFICATION_SUB_GENRE_ID?: string | null;
          ATTRACTION_CLASSIFICATION_SUB_TYPE?: string | null;
          ATTRACTION_CLASSIFICATION_SUB_TYPE_ID?: string | null;
          ATTRACTION_CLASSIFICATION_TYPE?: string | null;
          ATTRACTION_CLASSIFICATION_TYPE_ID?: string | null;
          ATTRACTION_ID?: string | null;
          ATTRACTION_IMAGE_URL?: string | null;
          ATTRACTION_NAME?: string | null;
          ATTRACTION_URL?: string | null;
          CLASSIFICATION_GENRE?: string | null;
          CLASSIFICATION_GENRE_ID?: string | null;
          CLASSIFICATION_SEGMENT?: string | null;
          CLASSIFICATION_SEGMENT_ID?: string | null;
          CLASSIFICATION_SUB_GENRE?: string | null;
          CLASSIFICATION_SUB_GENRE_ID?: string | null;
          CLASSIFICATION_SUB_TYPE?: string | null;
          CLASSIFICATION_SUB_TYPE_ID?: string | null;
          CLASSIFICATION_TYPE?: string | null;
          CLASSIFICATION_TYPE_ID?: string | null;
          CURRENCY?: string | null;
          EVENT_END_DATETIME?: string | null;
          EVENT_END_LOCAL_DATE?: string | null;
          EVENT_ID?: string | null;
          EVENT_IMAGE_URL?: string | null;
          EVENT_INFO?: string | null;
          EVENT_NAME?: string | null;
          EVENT_NOTES?: string | null;
          EVENT_START_DATETIME?: string | null;
          EVENT_START_LOCAL_DATE?: string | null;
          EVENT_START_LOCAL_TIME?: string | null;
          EVENT_STATUS?: string | null;
          HOT_EVENT?: boolean | null;
          IMPORTANT_INFORMATION?: string | null;
          LEGACY_ATTRACTION_ID?: string | null;
          LEGACY_EVENT_ID?: string | null;
          LEGACY_VENUE_ID?: string | null;
          MAX_PRICE?: string | null;
          MAX_PRICE_WITH_FEES?: string | null;
          MIN_PRICE?: string | null;
          MIN_PRICE_WITH_FEES?: string | null;
          ONSALE_END_DATETIME?: string | null;
          ONSALE_START_DATETIME?: string | null;
          PLEASE_NOTE?: string | null;
          PRESALE_END_DATETIME?: string | null;
          "PRESALE_NAME.1"?: string | null;
          PRESALE_START_DATETIME?: string | null;
          PRIMARY_EVENT_URL?: string | null;
          PROMOTER_ID?: string | null;
          PROMOTER_NAME?: string | null;
          SOURCE?: string | null;
          TRANSACTABLE?: string | null;
          VENUE_CITY?: string | null;
          VENUE_COUNTRY_CODE?: string | null;
          VENUE_ID?: string | null;
          VENUE_LATITUDE?: number | null;
          VENUE_LONGITUDE?: number | null;
          VENUE_NAME?: string | null;
          VENUE_STATE_CODE?: string | null;
          VENUE_STREET?: string | null;
          VENUE_TIMEZONE?: string | null;
          VENUE_URL?: string | null;
          VENUE_ZIP_CODE?: number | null;
        };
        Update: {
          ACCESSIBLE_SEATING_DETAIL?: string | null;
          API_ONSALE_START_DATETIME?: string | null;
          ATTRACTION_CLASSIFICATION_GENRE?: string | null;
          ATTRACTION_CLASSIFICATION_GENRE_ID?: string | null;
          ATTRACTION_CLASSIFICATION_SEGMENT?: string | null;
          ATTRACTION_CLASSIFICATION_SEGMENT_ID?: string | null;
          ATTRACTION_CLASSIFICATION_SUB_GENRE?: string | null;
          ATTRACTION_CLASSIFICATION_SUB_GENRE_ID?: string | null;
          ATTRACTION_CLASSIFICATION_SUB_TYPE?: string | null;
          ATTRACTION_CLASSIFICATION_SUB_TYPE_ID?: string | null;
          ATTRACTION_CLASSIFICATION_TYPE?: string | null;
          ATTRACTION_CLASSIFICATION_TYPE_ID?: string | null;
          ATTRACTION_ID?: string | null;
          ATTRACTION_IMAGE_URL?: string | null;
          ATTRACTION_NAME?: string | null;
          ATTRACTION_URL?: string | null;
          CLASSIFICATION_GENRE?: string | null;
          CLASSIFICATION_GENRE_ID?: string | null;
          CLASSIFICATION_SEGMENT?: string | null;
          CLASSIFICATION_SEGMENT_ID?: string | null;
          CLASSIFICATION_SUB_GENRE?: string | null;
          CLASSIFICATION_SUB_GENRE_ID?: string | null;
          CLASSIFICATION_SUB_TYPE?: string | null;
          CLASSIFICATION_SUB_TYPE_ID?: string | null;
          CLASSIFICATION_TYPE?: string | null;
          CLASSIFICATION_TYPE_ID?: string | null;
          CURRENCY?: string | null;
          EVENT_END_DATETIME?: string | null;
          EVENT_END_LOCAL_DATE?: string | null;
          EVENT_ID?: string | null;
          EVENT_IMAGE_URL?: string | null;
          EVENT_INFO?: string | null;
          EVENT_NAME?: string | null;
          EVENT_NOTES?: string | null;
          EVENT_START_DATETIME?: string | null;
          EVENT_START_LOCAL_DATE?: string | null;
          EVENT_START_LOCAL_TIME?: string | null;
          EVENT_STATUS?: string | null;
          HOT_EVENT?: boolean | null;
          IMPORTANT_INFORMATION?: string | null;
          LEGACY_ATTRACTION_ID?: string | null;
          LEGACY_EVENT_ID?: string | null;
          LEGACY_VENUE_ID?: string | null;
          MAX_PRICE?: string | null;
          MAX_PRICE_WITH_FEES?: string | null;
          MIN_PRICE?: string | null;
          MIN_PRICE_WITH_FEES?: string | null;
          ONSALE_END_DATETIME?: string | null;
          ONSALE_START_DATETIME?: string | null;
          PLEASE_NOTE?: string | null;
          PRESALE_END_DATETIME?: string | null;
          "PRESALE_NAME.1"?: string | null;
          PRESALE_START_DATETIME?: string | null;
          PRIMARY_EVENT_URL?: string | null;
          PROMOTER_ID?: string | null;
          PROMOTER_NAME?: string | null;
          SOURCE?: string | null;
          TRANSACTABLE?: string | null;
          VENUE_CITY?: string | null;
          VENUE_COUNTRY_CODE?: string | null;
          VENUE_ID?: string | null;
          VENUE_LATITUDE?: number | null;
          VENUE_LONGITUDE?: number | null;
          VENUE_NAME?: string | null;
          VENUE_STATE_CODE?: string | null;
          VENUE_STREET?: string | null;
          VENUE_TIMEZONE?: string | null;
          VENUE_URL?: string | null;
          VENUE_ZIP_CODE?: number | null;
        };
        Relationships: [];
      };
      tmp_comedian_dashboard_followers_details_duplicate: {
        Row: {
          city: string | null;
          comedian_id: string | null;
          created_at: string | null;
          email: string | null;
          geog: unknown;
          latitude: number | null;
          longitude: number | null;
          state: string | null;
          state_abbreviation: string | null;
          ts: string | null;
          user_comedian_id_ts: string;
          user_id: string | null;
          zipcode: string | null;
        };
        Insert: {
          city?: string | null;
          comedian_id?: string | null;
          created_at?: string | null;
          email?: string | null;
          geog?: unknown;
          latitude?: number | null;
          longitude?: number | null;
          state?: string | null;
          state_abbreviation?: string | null;
          ts?: string | null;
          user_comedian_id_ts: string;
          user_id?: string | null;
          zipcode?: string | null;
        };
        Update: {
          city?: string | null;
          comedian_id?: string | null;
          created_at?: string | null;
          email?: string | null;
          geog?: unknown;
          latitude?: number | null;
          longitude?: number | null;
          state?: string | null;
          state_abbreviation?: string | null;
          ts?: string | null;
          user_comedian_id_ts?: string;
          user_id?: string | null;
          zipcode?: string | null;
        };
        Relationships: [];
      };
      trending_specials: {
        Row: {
          id: number;
          rank: number;
          special_id: string;
          updated_at: string;
          view_count: number;
        };
        Insert: {
          id?: number;
          rank: number;
          special_id?: string;
          updated_at?: string;
          view_count?: number;
        };
        Update: {
          id?: number;
          rank?: number;
          special_id?: string;
          updated_at?: string;
          view_count?: number;
        };
        Relationships: [
          {
            foreignKeyName: "trending_specials_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      user_app_feedback: {
        Row: {
          attached_image_ids: string[] | null;
          created_at: string;
          general_feedback: string | null;
          id: number;
          improves_feedback: string | null;
          os: string | null;
          rating: number;
          user_id: string | null;
          version: string | null;
        };
        Insert: {
          attached_image_ids?: string[] | null;
          created_at?: string;
          general_feedback?: string | null;
          id?: number;
          improves_feedback?: string | null;
          os?: string | null;
          rating: number;
          user_id?: string | null;
          version?: string | null;
        };
        Update: {
          attached_image_ids?: string[] | null;
          created_at?: string;
          general_feedback?: string | null;
          id?: number;
          improves_feedback?: string | null;
          os?: string | null;
          rating?: number;
          user_id?: string | null;
          version?: string | null;
        };
        Relationships: [];
      };
      user_comedian_follows: {
        Row: {
          comedian_id: string;
          created_at: string | null;
          user_id: string;
        };
        Insert: {
          comedian_id: string;
          created_at?: string | null;
          user_id: string;
        };
        Update: {
          comedian_id?: string;
          created_at?: string | null;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_comedian_follows_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      user_comedian_notification_preferences: {
        Row: {
          comedian_id: string;
          created_at: string;
          new_free_content: boolean | null;
          new_subscription_content: boolean | null;
          new_tour_dates: boolean | null;
          push_notifications: boolean | null;
          updated_at: string;
          user_id: string;
        };
        Insert: {
          comedian_id: string;
          created_at?: string;
          new_free_content?: boolean | null;
          new_subscription_content?: boolean | null;
          new_tour_dates?: boolean | null;
          push_notifications?: boolean | null;
          updated_at?: string;
          user_id: string;
        };
        Update: {
          comedian_id?: string;
          created_at?: string;
          new_free_content?: boolean | null;
          new_subscription_content?: boolean | null;
          new_tour_dates?: boolean | null;
          push_notifications?: boolean | null;
          updated_at?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_comedian_notification_preferences_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      user_comedian_recommendations: {
        Row: {
          comedian_id: string;
          rank: number;
          sync_date: string | null;
          user_id: string;
        };
        Insert: {
          comedian_id: string;
          rank: number;
          sync_date?: string | null;
          user_id: string;
        };
        Update: {
          comedian_id?: string;
          rank?: number;
          sync_date?: string | null;
          user_id?: string;
        };
        Relationships: [];
      };
      user_comedian_recommendations_old: {
        Row: {
          comedian_id: string;
          rank: number;
          sync_date: string | null;
          user_id: string;
        };
        Insert: {
          comedian_id: string;
          rank: number;
          sync_date?: string | null;
          user_id: string;
        };
        Update: {
          comedian_id?: string;
          rank?: number;
          sync_date?: string | null;
          user_id?: string;
        };
        Relationships: [];
      };
      user_comedian_recommendations_staging: {
        Row: {
          comedian_id: string;
          rank: number;
          sync_date: string | null;
          user_id: string;
        };
        Insert: {
          comedian_id: string;
          rank: number;
          sync_date?: string | null;
          user_id: string;
        };
        Update: {
          comedian_id?: string;
          rank?: number;
          sync_date?: string | null;
          user_id?: string;
        };
        Relationships: [];
      };
      user_device_tokens: {
        Row: {
          app: string;
          created_at: string;
          device_id: string | null;
          is_production: boolean;
          last_seen_at: string;
          service: string;
          token: string;
          user_id: string;
        };
        Insert: {
          app: string;
          created_at?: string;
          device_id?: string | null;
          is_production?: boolean;
          last_seen_at?: string;
          service: string;
          token: string;
          user_id: string;
        };
        Update: {
          app?: string;
          created_at?: string;
          device_id?: string | null;
          is_production?: boolean;
          last_seen_at?: string;
          service?: string;
          token?: string;
          user_id?: string;
        };
        Relationships: [];
      };
      user_hidden_comedians: {
        Row: {
          comedian_id: string;
          created_at: string;
          id: string;
          user_id: string;
        };
        Insert: {
          comedian_id?: string;
          created_at?: string;
          id?: string;
          user_id?: string;
        };
        Update: {
          comedian_id?: string;
          created_at?: string;
          id?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_hidden_comedians_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      user_hidden_specials: {
        Row: {
          created_at: string;
          id: string;
          special_id: string;
          user_id: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          special_id: string;
          user_id: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          special_id?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_hidden_specials_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      user_nux_views: {
        Row: {
          created_at: string;
          id: number;
          nux_slug: string;
          user_id: string;
        };
        Insert: {
          created_at?: string;
          id?: number;
          nux_slug: string;
          user_id: string;
        };
        Update: {
          created_at?: string;
          id?: number;
          nux_slug?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_nux_views_nux_slug_fkey";
            columns: ["nux_slug"];
            isOneToOne: false;
            referencedRelation: "nuxs";
            referencedColumns: ["slug"];
          },
        ];
      };
      user_paid_collections: {
        Row: {
          collection_id: string;
          created_at: string;
          user_id: string;
        };
        Insert: {
          collection_id?: string;
          created_at?: string;
          user_id?: string;
        };
        Update: {
          collection_id?: string;
          created_at?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_paid_collections_collection_id_fkey";
            columns: ["collection_id"];
            isOneToOne: false;
            referencedRelation: "collections";
            referencedColumns: ["id"];
          },
        ];
      };
      user_paid_subscriptions: {
        Row: {
          canceled_at: string | null;
          comedian_id: string;
          created_at: string;
          external_subscription_id: string;
          id: string;
          last_renewed_at: string;
          last_updated_at: string;
          product_id: string;
          status: Database["public"]["Enums"]["subscription_status"];
          user_id: string | null;
          valid_until: string;
        };
        Insert: {
          canceled_at?: string | null;
          comedian_id: string;
          created_at?: string;
          external_subscription_id: string;
          id?: string;
          last_renewed_at?: string;
          last_updated_at?: string;
          product_id: string;
          status?: Database["public"]["Enums"]["subscription_status"];
          user_id?: string | null;
          valid_until: string;
        };
        Update: {
          canceled_at?: string | null;
          comedian_id?: string;
          created_at?: string;
          external_subscription_id?: string;
          id?: string;
          last_renewed_at?: string;
          last_updated_at?: string;
          product_id?: string;
          status?: Database["public"]["Enums"]["subscription_status"];
          user_id?: string | null;
          valid_until?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_paid_subscriptions_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "user_paid_subscriptions_product_id_fkey";
            columns: ["product_id"];
            isOneToOne: false;
            referencedRelation: "products";
            referencedColumns: ["id"];
          },
        ];
      };
      user_shows_attended: {
        Row: {
          attendance_id: string;
          comedian_id: string | null;
          created_at: string;
          show_id: string | null;
          user_id: string;
          venue_id: string | null;
        };
        Insert: {
          attendance_id?: string;
          comedian_id?: string | null;
          created_at?: string;
          show_id?: string | null;
          user_id: string;
          venue_id?: string | null;
        };
        Update: {
          attendance_id?: string;
          comedian_id?: string | null;
          created_at?: string;
          show_id?: string | null;
          user_id?: string;
          venue_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "user_shows_attended_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "user_shows_attended_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "user_shows_attended_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "user_shows_attended_venue_id_fkey";
            columns: ["venue_id"];
            isOneToOne: false;
            referencedRelation: "venues";
            referencedColumns: ["id"];
          },
        ];
      };
      user_specials: {
        Row: {
          created_at: string | null;
          id: number;
          special_id: string;
          user_id: string;
        };
        Insert: {
          created_at?: string | null;
          id?: number;
          special_id: string;
          user_id: string;
        };
        Update: {
          created_at?: string | null;
          id?: number;
          special_id?: string;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_specials_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      user_specials_playback: {
        Row: {
          created_at: string | null;
          last_playback_location: number;
          special_id: string;
          updated_at: string | null;
          user_id: string;
        };
        Insert: {
          created_at?: string | null;
          last_playback_location: number;
          special_id: string;
          updated_at?: string | null;
          user_id: string;
        };
        Update: {
          created_at?: string | null;
          last_playback_location?: number;
          special_id?: string;
          updated_at?: string | null;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_specials_playback_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      user_specials_reactions: {
        Row: {
          count: number;
          created_at: string;
          id: number;
          special_id: string;
          type: Database["public"]["Enums"]["user_specials_reactions_type"];
          user_id: string;
        };
        Insert: {
          count?: number;
          created_at?: string;
          id?: number;
          special_id: string;
          type?: Database["public"]["Enums"]["user_specials_reactions_type"];
          user_id: string;
        };
        Update: {
          count?: number;
          created_at?: string;
          id?: number;
          special_id?: string;
          type?: Database["public"]["Enums"]["user_specials_reactions_type"];
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_specials_reactions_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      user_specials_recommendations: {
        Row: {
          rank: number;
          special_id: string;
          sync_date: string | null;
          user_id: string;
        };
        Insert: {
          rank: number;
          special_id: string;
          sync_date?: string | null;
          user_id: string;
        };
        Update: {
          rank?: number;
          special_id?: string;
          sync_date?: string | null;
          user_id?: string;
        };
        Relationships: [];
      };
      user_specials_recommendations_old: {
        Row: {
          rank: number;
          special_id: string;
          sync_date: string | null;
          user_id: string;
        };
        Insert: {
          rank: number;
          special_id: string;
          sync_date?: string | null;
          user_id: string;
        };
        Update: {
          rank?: number;
          special_id?: string;
          sync_date?: string | null;
          user_id?: string;
        };
        Relationships: [];
      };
      user_specials_recommendations_staging: {
        Row: {
          rank: number;
          special_id: string;
          sync_date: string | null;
          user_id: string;
        };
        Insert: {
          rank: number;
          special_id: string;
          sync_date?: string | null;
          user_id: string;
        };
        Update: {
          rank?: number;
          special_id?: string;
          sync_date?: string | null;
          user_id?: string;
        };
        Relationships: [];
      };
      user_subscriptions: {
        Row: {
          created_at: string;
          id: number;
          stripe_product_id: string | null;
          stripe_subscription_id: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          id?: number;
          stripe_product_id?: string | null;
          stripe_subscription_id: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: number;
          stripe_product_id?: string | null;
          stripe_subscription_id?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "user_subscriptions_stripe_product_id_fkey";
            columns: ["stripe_product_id"];
            isOneToOne: false;
            referencedRelation: "stripe_products";
            referencedColumns: ["id"];
          },
        ];
      };
      user_subscriptions_dev: {
        Row: {
          created_at: string;
          id: number;
          stripe_product_id: string | null;
          stripe_subscription_id: string;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          id?: number;
          stripe_product_id?: string | null;
          stripe_subscription_id: string;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: number;
          stripe_product_id?: string | null;
          stripe_subscription_id?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "user_subscriptions_dev_stripe_product_id_fkey";
            columns: ["stripe_product_id"];
            isOneToOne: false;
            referencedRelation: "stripe_products_dev";
            referencedColumns: ["id"];
          },
        ];
      };
      user_subscriptions_v2: {
        Row: {
          comedian_id: string;
          created_at: string;
          id: string;
          last_updated_at: string;
          product_id: string;
          status: Database["public"]["Enums"]["subscription_status"];
          stripe_subscription_id: string | null;
          user_id: string;
        };
        Insert: {
          comedian_id: string;
          created_at?: string;
          id?: string;
          last_updated_at?: string;
          product_id: string;
          status?: Database["public"]["Enums"]["subscription_status"];
          stripe_subscription_id?: string | null;
          user_id: string;
        };
        Update: {
          comedian_id?: string;
          created_at?: string;
          id?: string;
          last_updated_at?: string;
          product_id?: string;
          status?: Database["public"]["Enums"]["subscription_status"];
          stripe_subscription_id?: string | null;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_subscriptions_v2_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "user_subscriptions_v2_product_id_fkey";
            columns: ["product_id"];
            isOneToOne: false;
            referencedRelation: "products";
            referencedColumns: ["id"];
          },
        ];
      };
      user_transactions: {
        Row: {
          amount: number;
          attribution: Json | null;
          content_id: string | null;
          created_at: string;
          environment: string;
          id: string;
          last_updated_at: string;
          payment_provider: Database["public"]["Enums"]["payment_provider"];
          payment_reference_id: string | null;
          payment_status: Database["public"]["Enums"]["payment_status"];
          product_id: string;
          purchase_source: string;
          user_id: string | null;
        };
        Insert: {
          amount: number;
          attribution?: Json | null;
          content_id?: string | null;
          created_at?: string;
          environment?: string;
          id?: string;
          last_updated_at?: string;
          payment_provider: Database["public"]["Enums"]["payment_provider"];
          payment_reference_id?: string | null;
          payment_status?: Database["public"]["Enums"]["payment_status"];
          product_id: string;
          purchase_source: string;
          user_id?: string | null;
        };
        Update: {
          amount?: number;
          attribution?: Json | null;
          content_id?: string | null;
          created_at?: string;
          environment?: string;
          id?: string;
          last_updated_at?: string;
          payment_provider?: Database["public"]["Enums"]["payment_provider"];
          payment_reference_id?: string | null;
          payment_status?: Database["public"]["Enums"]["payment_status"];
          product_id?: string;
          purchase_source?: string;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "user_transactions_product_id_fkey";
            columns: ["product_id"];
            isOneToOne: false;
            referencedRelation: "products";
            referencedColumns: ["id"];
          },
        ];
      };
      user_venue_follows: {
        Row: {
          created_at: string;
          user_id: string;
          venue_page_id: string;
        };
        Insert: {
          created_at?: string;
          user_id: string;
          venue_page_id: string;
        };
        Update: {
          created_at?: string;
          user_id?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "user_venue_follows_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      user_zipcodes: {
        Row: {
          country_code: string | null;
          created_at: string | null;
          from_ip: boolean | null;
          geohash_4: string | null;
          id: number;
          is_eu: boolean | null;
          user: string;
          zipcode: string | null;
        };
        Insert: {
          country_code?: string | null;
          created_at?: string | null;
          from_ip?: boolean | null;
          geohash_4?: string | null;
          id?: number;
          is_eu?: boolean | null;
          user: string;
          zipcode?: string | null;
        };
        Update: {
          country_code?: string | null;
          created_at?: string | null;
          from_ip?: boolean | null;
          geohash_4?: string | null;
          id?: number;
          is_eu?: boolean | null;
          user?: string;
          zipcode?: string | null;
        };
        Relationships: [];
      };
      users_extra: {
        Row: {
          email: string | null;
          id: string;
          is_power_user: boolean | null;
          last_active_at: string | null;
          phone: string | null;
        };
        Insert: {
          email?: string | null;
          id: string;
          is_power_user?: boolean | null;
          last_active_at?: string | null;
          phone?: string | null;
        };
        Update: {
          email?: string | null;
          id?: string;
          is_power_user?: boolean | null;
          last_active_at?: string | null;
          phone?: string | null;
        };
        Relationships: [];
      };
      venue_analytics_tags: {
        Row: {
          config: Json;
          created_at: string;
          enabled: boolean;
          id: string;
          measurement_id: string;
          mode: string;
          notes: string | null;
          provider: string;
          updated_at: string;
          venue_page_id: string;
        };
        Insert: {
          config?: Json;
          created_at?: string;
          enabled?: boolean;
          id?: string;
          measurement_id: string;
          mode?: string;
          notes?: string | null;
          provider: string;
          updated_at?: string;
          venue_page_id: string;
        };
        Update: {
          config?: Json;
          created_at?: string;
          enabled?: boolean;
          id?: string;
          measurement_id?: string;
          mode?: string;
          notes?: string | null;
          provider?: string;
          updated_at?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_analytics_tags_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_booking_preferences: {
        Row: {
          booking_manager_name: string | null;
          created_at: string | null;
          created_by: string | null;
          green_room_available: boolean | null;
          id: string;
          lighting_system_available: boolean | null;
          payment_terms: string | null;
          preferred_advance_booking: number | null;
          primary_contact_email: string;
          primary_contact_phone: string | null;
          recording_equipment_available: boolean | null;
          requires_contracts: boolean | null;
          secondary_contact_email: string | null;
          sound_system_available: boolean | null;
          typical_audience_size: number | null;
          typical_compensation_range_max: number | null;
          typical_compensation_range_min: number | null;
          typical_event_types: string[] | null;
          typical_performance_duration: number | null;
          updated_at: string | null;
          venue_page_id: string;
        };
        Insert: {
          booking_manager_name?: string | null;
          created_at?: string | null;
          created_by?: string | null;
          green_room_available?: boolean | null;
          id?: string;
          lighting_system_available?: boolean | null;
          payment_terms?: string | null;
          preferred_advance_booking?: number | null;
          primary_contact_email: string;
          primary_contact_phone?: string | null;
          recording_equipment_available?: boolean | null;
          requires_contracts?: boolean | null;
          secondary_contact_email?: string | null;
          sound_system_available?: boolean | null;
          typical_audience_size?: number | null;
          typical_compensation_range_max?: number | null;
          typical_compensation_range_min?: number | null;
          typical_event_types?: string[] | null;
          typical_performance_duration?: number | null;
          updated_at?: string | null;
          venue_page_id: string;
        };
        Update: {
          booking_manager_name?: string | null;
          created_at?: string | null;
          created_by?: string | null;
          green_room_available?: boolean | null;
          id?: string;
          lighting_system_available?: boolean | null;
          payment_terms?: string | null;
          preferred_advance_booking?: number | null;
          primary_contact_email?: string;
          primary_contact_phone?: string | null;
          recording_equipment_available?: boolean | null;
          requires_contracts?: boolean | null;
          secondary_contact_email?: string | null;
          sound_system_available?: boolean | null;
          typical_audience_size?: number | null;
          typical_compensation_range_max?: number | null;
          typical_compensation_range_min?: number | null;
          typical_event_types?: string[] | null;
          typical_performance_duration?: number | null;
          updated_at?: string | null;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_booking_preferences_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_crawl_versions: {
        Row: {
          agent_params: Json | null;
          crawl_id: string;
          created_at: string;
          created_by: string | null;
          error: string | null;
          id: string;
          result: Json | null;
          run_params: Json | null;
          source: string;
          state: Json | null;
          status: string;
          updated_at: string;
        };
        Insert: {
          agent_params?: Json | null;
          crawl_id: string;
          created_at?: string;
          created_by?: string | null;
          error?: string | null;
          id?: string;
          result?: Json | null;
          run_params?: Json | null;
          source: string;
          state?: Json | null;
          status?: string;
          updated_at?: string;
        };
        Update: {
          agent_params?: Json | null;
          crawl_id?: string;
          created_at?: string;
          created_by?: string | null;
          error?: string | null;
          id?: string;
          result?: Json | null;
          run_params?: Json | null;
          source?: string;
          state?: Json | null;
          status?: string;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_crawl_versions_crawl_id_fkey";
            columns: ["crawl_id"];
            isOneToOne: false;
            referencedRelation: "venue_crawls";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_crawls: {
        Row: {
          attributed_at: string | null;
          created_at: string;
          geocoded_at: string | null;
          id: string;
          latitude: number | null;
          longitude: number | null;
          normalized_url: string;
          updated_at: string;
          venue_id: string | null;
          venue_page_id: string | null;
        };
        Insert: {
          attributed_at?: string | null;
          created_at?: string;
          geocoded_at?: string | null;
          id?: string;
          latitude?: number | null;
          longitude?: number | null;
          normalized_url: string;
          updated_at?: string;
          venue_id?: string | null;
          venue_page_id?: string | null;
        };
        Update: {
          attributed_at?: string | null;
          created_at?: string;
          geocoded_at?: string | null;
          id?: string;
          latitude?: number | null;
          longitude?: number | null;
          normalized_url?: string;
          updated_at?: string;
          venue_id?: string | null;
          venue_page_id?: string | null;
        };
        Relationships: [];
      };
      venue_followers_daily: {
        Row: {
          comedian_clicks: Json;
          comedian_metadata: Json;
          comedian_referrers: Json;
          cumulative_followers: number;
          ds: string;
          id: string;
          last_updated: string;
          mp_library: Json;
          num_followers: number;
          os: Json;
          referrers: Json;
          time_of_day: Json;
          venue_page_id: string;
        };
        Insert: {
          comedian_clicks?: Json;
          comedian_metadata?: Json;
          comedian_referrers?: Json;
          cumulative_followers: number;
          ds: string;
          id: string;
          last_updated: string;
          mp_library?: Json;
          num_followers: number;
          os?: Json;
          referrers?: Json;
          time_of_day?: Json;
          venue_page_id: string;
        };
        Update: {
          comedian_clicks?: Json;
          comedian_metadata?: Json;
          comedian_referrers?: Json;
          cumulative_followers?: number;
          ds?: string;
          id?: string;
          last_updated?: string;
          mp_library?: Json;
          num_followers?: number;
          os?: Json;
          referrers?: Json;
          time_of_day?: Json;
          venue_page_id?: string;
        };
        Relationships: [];
      };
      venue_followers_summary: {
        Row: {
          followers_14d: number;
          followers_28d: number;
          followers_3d: number;
          followers_7d: number;
          followers_lifetime: number;
          last_updated: string;
          mp_library_14d: Json;
          mp_library_28d: Json;
          mp_library_3d: Json;
          mp_library_7d: Json;
          mp_library_lifetime: Json;
          os_14d: Json;
          os_28d: Json;
          os_3d: Json;
          os_7d: Json;
          os_lifetime: Json;
          referrers_14d: Json;
          referrers_28d: Json;
          referrers_3d: Json;
          referrers_7d: Json;
          referrers_lifetime: Json;
          time_of_day_14d: Json;
          time_of_day_28d: Json;
          time_of_day_3d: Json;
          time_of_day_7d: Json;
          time_of_day_lifetime: Json;
          venue_page_id: string;
        };
        Insert: {
          followers_14d?: number;
          followers_28d?: number;
          followers_3d?: number;
          followers_7d?: number;
          followers_lifetime?: number;
          last_updated: string;
          mp_library_14d?: Json;
          mp_library_28d?: Json;
          mp_library_3d?: Json;
          mp_library_7d?: Json;
          mp_library_lifetime?: Json;
          os_14d?: Json;
          os_28d?: Json;
          os_3d?: Json;
          os_7d?: Json;
          os_lifetime?: Json;
          referrers_14d?: Json;
          referrers_28d?: Json;
          referrers_3d?: Json;
          referrers_7d?: Json;
          referrers_lifetime?: Json;
          time_of_day_14d?: Json;
          time_of_day_28d?: Json;
          time_of_day_3d?: Json;
          time_of_day_7d?: Json;
          time_of_day_lifetime?: Json;
          venue_page_id: string;
        };
        Update: {
          followers_14d?: number;
          followers_28d?: number;
          followers_3d?: number;
          followers_7d?: number;
          followers_lifetime?: number;
          last_updated?: string;
          mp_library_14d?: Json;
          mp_library_28d?: Json;
          mp_library_3d?: Json;
          mp_library_7d?: Json;
          mp_library_lifetime?: Json;
          os_14d?: Json;
          os_28d?: Json;
          os_3d?: Json;
          os_7d?: Json;
          os_lifetime?: Json;
          referrers_14d?: Json;
          referrers_28d?: Json;
          referrers_3d?: Json;
          referrers_7d?: Json;
          referrers_lifetime?: Json;
          time_of_day_14d?: Json;
          time_of_day_28d?: Json;
          time_of_day_3d?: Json;
          time_of_day_7d?: Json;
          time_of_day_lifetime?: Json;
          venue_page_id?: string;
        };
        Relationships: [];
      };
      venue_lineup_attributions: {
        Row: {
          comedian_id: string | null;
          created_at: string;
          created_by: string | null;
          decision: string;
          display_name: string | null;
          id: string;
          name_key: string;
          updated_at: string;
          venue_page_id: string;
        };
        Insert: {
          comedian_id?: string | null;
          created_at?: string;
          created_by?: string | null;
          decision: string;
          display_name?: string | null;
          id?: string;
          name_key: string;
          updated_at?: string;
          venue_page_id: string;
        };
        Update: {
          comedian_id?: string | null;
          created_at?: string;
          created_by?: string | null;
          decision?: string;
          display_name?: string | null;
          id?: string;
          name_key?: string;
          updated_at?: string;
          venue_page_id?: string;
        };
        Relationships: [];
      };
      venue_pages: {
        Row: {
          carousel_auto_rotate_delay: number | null;
          carousel_mode: Database["public"]["Enums"]["venue_pages_carousel_type"];
          city_state: string;
          created_at: string;
          cta_banner: Json | null;
          default_show_view: Database["public"]["Enums"]["venue_page_show_view_type"];
          deprecated_timezone: Database["public"]["Enums"]["timezone"];
          description: string | null;
          directions_instructions: string | null;
          drinks_menu: string | null;
          email: string | null;
          external_site_info: Json[] | null;
          food_menu: string | null;
          hours: Json | null;
          id: string;
          info_tags: Json[];
          is_live: boolean;
          is_location_hidden: boolean;
          is_producer: boolean;
          location_ids: string[] | null;
          logo_img: string | null;
          name: string;
          parking_instructions: string | null;
          phone: string | null;
          published_at: string | null;
          shows_grouping_mode: Database["public"]["Enums"]["venue_pages_shows_grouping_type"];
          slug: string;
          timezones: Json | null;
          video_id: string | null;
        };
        Insert: {
          carousel_auto_rotate_delay?: number | null;
          carousel_mode?: Database["public"]["Enums"]["venue_pages_carousel_type"];
          city_state: string;
          created_at?: string;
          cta_banner?: Json | null;
          default_show_view?: Database["public"]["Enums"]["venue_page_show_view_type"];
          deprecated_timezone?: Database["public"]["Enums"]["timezone"];
          description?: string | null;
          directions_instructions?: string | null;
          drinks_menu?: string | null;
          email?: string | null;
          external_site_info?: Json[] | null;
          food_menu?: string | null;
          hours?: Json | null;
          id?: string;
          info_tags?: Json[];
          is_live?: boolean;
          is_location_hidden?: boolean;
          is_producer?: boolean;
          location_ids?: string[] | null;
          logo_img?: string | null;
          name: string;
          parking_instructions?: string | null;
          phone?: string | null;
          published_at?: string | null;
          shows_grouping_mode?: Database["public"]["Enums"]["venue_pages_shows_grouping_type"];
          slug: string;
          timezones?: Json | null;
          video_id?: string | null;
        };
        Update: {
          carousel_auto_rotate_delay?: number | null;
          carousel_mode?: Database["public"]["Enums"]["venue_pages_carousel_type"];
          city_state?: string;
          created_at?: string;
          cta_banner?: Json | null;
          default_show_view?: Database["public"]["Enums"]["venue_page_show_view_type"];
          deprecated_timezone?: Database["public"]["Enums"]["timezone"];
          description?: string | null;
          directions_instructions?: string | null;
          drinks_menu?: string | null;
          email?: string | null;
          external_site_info?: Json[] | null;
          food_menu?: string | null;
          hours?: Json | null;
          id?: string;
          info_tags?: Json[];
          is_live?: boolean;
          is_location_hidden?: boolean;
          is_producer?: boolean;
          location_ids?: string[] | null;
          logo_img?: string | null;
          name?: string;
          parking_instructions?: string | null;
          phone?: string | null;
          published_at?: string | null;
          shows_grouping_mode?: Database["public"]["Enums"]["venue_pages_shows_grouping_type"];
          slug?: string;
          timezones?: Json | null;
          video_id?: string | null;
        };
        Relationships: [];
      };
      venue_pages_admins: {
        Row: {
          admin_id: string;
          created_at: string;
          venue_page_id: string;
        };
        Insert: {
          admin_id: string;
          created_at?: string;
          venue_page_id: string;
        };
        Update: {
          admin_id?: string;
          created_at?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_admins_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_pages_carousel_items: {
        Row: {
          created_at: string;
          id: string;
          image_id: string | null;
          order: number;
          show_id: string | null;
          type: Database["public"]["Enums"]["venue_pages_carousel_item_type"];
          venue_page_id: string;
          video_id: string | null;
        };
        Insert: {
          created_at?: string;
          id?: string;
          image_id?: string | null;
          order: number;
          show_id?: string | null;
          type: Database["public"]["Enums"]["venue_pages_carousel_item_type"];
          venue_page_id: string;
          video_id?: string | null;
        };
        Update: {
          created_at?: string;
          id?: string;
          image_id?: string | null;
          order?: number;
          show_id?: string | null;
          type?: Database["public"]["Enums"]["venue_pages_carousel_item_type"];
          venue_page_id?: string;
          video_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_carousel_items_image_id_fkey";
            columns: ["image_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages_images";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "venue_pages_carousel_items_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "venue_pages_carousel_items_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "venue_pages_carousel_items_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "venue_pages_carousel_items_video_id_fkey";
            columns: ["video_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages_videos";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_pages_custom_links: {
        Row: {
          body: string;
          created_at: string;
          id: number;
          label: string;
          path: string;
          title: string;
          tixologi_tag_id: number | null;
          venue_page_id: string;
        };
        Insert: {
          body: string;
          created_at?: string;
          id?: number;
          label: string;
          path: string;
          title: string;
          tixologi_tag_id?: number | null;
          venue_page_id: string;
        };
        Update: {
          body?: string;
          created_at?: string;
          id?: number;
          label?: string;
          path?: string;
          title?: string;
          tixologi_tag_id?: number | null;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_custom_links_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_pages_email_settings: {
        Row: {
          created_at: string;
          sending_domain: string;
          sending_domain_mode: string;
          updated_at: string;
          venue_page_id: string;
        };
        Insert: {
          created_at?: string;
          sending_domain: string;
          sending_domain_mode: string;
          updated_at?: string;
          venue_page_id: string;
        };
        Update: {
          created_at?: string;
          sending_domain?: string;
          sending_domain_mode?: string;
          updated_at?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_email_settings_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: true;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_pages_external_links: {
        Row: {
          created_at: string;
          id: number;
          label: string;
          url: string;
          venue_page_id: string;
        };
        Insert: {
          created_at?: string;
          id?: number;
          label: string;
          url: string;
          venue_page_id: string;
        };
        Update: {
          created_at?: string;
          id?: number;
          label?: string;
          url?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_external_links_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_pages_faqs: {
        Row: {
          answer: string;
          created_at: string;
          id: number;
          question: string;
          venue_page_id: string | null;
        };
        Insert: {
          answer: string;
          created_at?: string;
          id?: number;
          question: string;
          venue_page_id?: string | null;
        };
        Update: {
          answer?: string;
          created_at?: string;
          id?: number;
          question?: string;
          venue_page_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_faqs_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_pages_images: {
        Row: {
          alt_text: string | null;
          created_at: string;
          id: string;
          image_id: string;
          venue_page_id: string;
        };
        Insert: {
          alt_text?: string | null;
          created_at?: string;
          id?: string;
          image_id: string;
          venue_page_id: string;
        };
        Update: {
          alt_text?: string | null;
          created_at?: string;
          id?: string;
          image_id?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_images_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_pages_nav_groups: {
        Row: {
          created_at: string;
          id: string;
          label: string;
          updated_at: string;
          venue_page_id: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          label: string;
          updated_at?: string;
          venue_page_id: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          label?: string;
          updated_at?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_nav_groups_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_pages_nav_items: {
        Row: {
          created_at: string | null;
          id: string;
          is_hidden: boolean | null;
          item_key: string;
          item_type: Database["public"]["Enums"]["venue_pages_nav_item_type"];
          parent_group_id: string | null;
          position: number;
          updated_at: string | null;
          venue_page_id: string;
        };
        Insert: {
          created_at?: string | null;
          id?: string;
          is_hidden?: boolean | null;
          item_key: string;
          item_type: Database["public"]["Enums"]["venue_pages_nav_item_type"];
          parent_group_id?: string | null;
          position: number;
          updated_at?: string | null;
          venue_page_id: string;
        };
        Update: {
          created_at?: string | null;
          id?: string;
          is_hidden?: boolean | null;
          item_key?: string;
          item_type?: Database["public"]["Enums"]["venue_pages_nav_item_type"];
          parent_group_id?: string | null;
          position?: number;
          updated_at?: string | null;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_nav_items_parent_group_id_fkey";
            columns: ["parent_group_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages_nav_groups";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "venue_pages_nav_items_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_pages_shows: {
        Row: {
          series_id: string | null;
          show_id: string;
          show_visibility_status: string;
          venue_page_id: string;
        };
        Insert: {
          series_id?: string | null;
          show_id: string;
          show_visibility_status?: string;
          venue_page_id: string;
        };
        Update: {
          series_id?: string | null;
          show_id?: string;
          show_visibility_status?: string;
          venue_page_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_shows_series_id_fkey";
            columns: ["series_id"];
            isOneToOne: false;
            referencedRelation: "shows_series";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "venue_pages_shows_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "venue_pages_shows_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "venue_pages_shows_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_pages_themes: {
        Row: {
          accent_color: string;
          created_at: string;
          header_background_color: string;
          header_border_color: string;
          id: number;
          logo_background_color: string;
          top_section_background_color: string;
          top_section_gradient_color: string | null;
          venue_page_id: string;
          venue_page_slug: string;
        };
        Insert: {
          accent_color?: string;
          created_at?: string;
          header_background_color?: string;
          header_border_color?: string;
          id?: number;
          logo_background_color?: string;
          top_section_background_color?: string;
          top_section_gradient_color?: string | null;
          venue_page_id: string;
          venue_page_slug: string;
        };
        Update: {
          accent_color?: string;
          created_at?: string;
          header_background_color?: string;
          header_border_color?: string;
          id?: number;
          logo_background_color?: string;
          top_section_background_color?: string;
          top_section_gradient_color?: string | null;
          venue_page_id?: string;
          venue_page_slug?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_themes_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "venue_pages_themes_venue_page_slug_fkey";
            columns: ["venue_page_slug"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["slug"];
          },
        ];
      };
      venue_pages_videos: {
        Row: {
          created_at: string;
          id: string;
          title: string | null;
          venue_page_id: string;
          video_id: string;
        };
        Insert: {
          created_at?: string;
          id?: string;
          title?: string | null;
          venue_page_id: string;
          video_id: string;
        };
        Update: {
          created_at?: string;
          id?: string;
          title?: string | null;
          venue_page_id?: string;
          video_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "venue_pages_videos_venue_page_id_fkey";
            columns: ["venue_page_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_sales_cumulative_rollup: {
        Row: {
          created_at: string;
          day: string;
          event_capacity: number;
          revenue_as_of_date: number;
          show_date: string;
          tickets_sold_as_of_date: number;
          tixologi_event_id: string;
          updated_at: string;
        };
        Insert: {
          created_at?: string;
          day: string;
          event_capacity: number;
          revenue_as_of_date?: number;
          show_date: string;
          tickets_sold_as_of_date?: number;
          tixologi_event_id: string;
          updated_at?: string;
        };
        Update: {
          created_at?: string;
          day?: string;
          event_capacity?: number;
          revenue_as_of_date?: number;
          show_date?: string;
          tickets_sold_as_of_date?: number;
          tixologi_event_id?: string;
          updated_at?: string;
        };
        Relationships: [];
      };
      venue_sales_daily: {
        Row: {
          sales_data: Json;
          updated_at: string;
          venue_page_id: string;
        };
        Insert: {
          sales_data: Json;
          updated_at: string;
          venue_page_id: string;
        };
        Update: {
          sales_data?: Json;
          updated_at?: string;
          venue_page_id?: string;
        };
        Relationships: [];
      };
      venue_sales_data_fake: {
        Row: {
          attribution: Json | null;
          basket_size: number | null;
          event_capacity: number | null;
          event_name: string | null;
          initial_referrer: string | null;
          order_status: string | null;
          payout_amount: number | null;
          purchase_amount: number | null;
          show_date: string | null;
          show_id: string | null;
          ticket_sale_id: string;
          tickets: Json | null;
          timestamp: string | null;
          tixologi_event_id: string | null;
          venue_page_id: string | null;
        };
        Insert: {
          attribution?: Json | null;
          basket_size?: number | null;
          event_capacity?: number | null;
          event_name?: string | null;
          initial_referrer?: string | null;
          order_status?: string | null;
          payout_amount?: number | null;
          purchase_amount?: number | null;
          show_date?: string | null;
          show_id?: string | null;
          ticket_sale_id: string;
          tickets?: Json | null;
          timestamp?: string | null;
          tixologi_event_id?: string | null;
          venue_page_id?: string | null;
        };
        Update: {
          attribution?: Json | null;
          basket_size?: number | null;
          event_capacity?: number | null;
          event_name?: string | null;
          initial_referrer?: string | null;
          order_status?: string | null;
          payout_amount?: number | null;
          purchase_amount?: number | null;
          show_date?: string | null;
          show_id?: string | null;
          ticket_sale_id?: string;
          tickets?: Json | null;
          timestamp?: string | null;
          tixologi_event_id?: string | null;
          venue_page_id?: string | null;
        };
        Relationships: [];
      };
      venue_sales_data_raw: {
        Row: {
          attribution: Json | null;
          basket_size: number | null;
          event_capacity: number | null;
          event_name: string | null;
          initial_referrer: string | null;
          order_status: string | null;
          payout_amount: number | null;
          purchase_amount: number | null;
          show_date: string | null;
          show_id: string | null;
          ticket_sale_id: string;
          tickets: Json | null;
          timestamp: string | null;
          tixologi_event_id: string | null;
          venue_page_id: string | null;
        };
        Insert: {
          attribution?: Json | null;
          basket_size?: number | null;
          event_capacity?: number | null;
          event_name?: string | null;
          initial_referrer?: string | null;
          order_status?: string | null;
          payout_amount?: number | null;
          purchase_amount?: number | null;
          show_date?: string | null;
          show_id?: string | null;
          ticket_sale_id: string;
          tickets?: Json | null;
          timestamp?: string | null;
          tixologi_event_id?: string | null;
          venue_page_id?: string | null;
        };
        Update: {
          attribution?: Json | null;
          basket_size?: number | null;
          event_capacity?: number | null;
          event_name?: string | null;
          initial_referrer?: string | null;
          order_status?: string | null;
          payout_amount?: number | null;
          purchase_amount?: number | null;
          show_date?: string | null;
          show_id?: string | null;
          ticket_sale_id?: string;
          tickets?: Json | null;
          timestamp?: string | null;
          tixologi_event_id?: string | null;
          venue_page_id?: string | null;
        };
        Relationships: [];
      };
      venue_sales_forecasts: {
        Row: {
          created_at: string | null;
          daily_revenue: number;
          daily_tickets: number;
          event_capacity: number;
          forecast_date: string;
          forecasted_revenue: number | null;
          forecasted_tickets: number | null;
          is_final_forecast: boolean | null;
          show_date: string;
          tixologi_event_id: string;
          updated_at: string | null;
        };
        Insert: {
          created_at?: string | null;
          daily_revenue?: number;
          daily_tickets?: number;
          event_capacity?: number;
          forecast_date: string;
          forecasted_revenue?: number | null;
          forecasted_tickets?: number | null;
          is_final_forecast?: boolean | null;
          show_date?: string;
          tixologi_event_id: string;
          updated_at?: string | null;
        };
        Update: {
          created_at?: string | null;
          daily_revenue?: number;
          daily_tickets?: number;
          event_capacity?: number;
          forecast_date?: string;
          forecasted_revenue?: number | null;
          forecasted_tickets?: number | null;
          is_final_forecast?: boolean | null;
          show_date?: string;
          tixologi_event_id?: string;
          updated_at?: string | null;
        };
        Relationships: [];
      };
      venue_sales_summary: {
        Row: {
          referrers_1d: Json;
          referrers_28d: Json;
          referrers_7d: Json;
          referrers_lifetime: Json;
          revenue_1d_amount: number;
          revenue_1d_growth: number | null;
          revenue_28d_amount: number;
          revenue_28d_growth: number | null;
          revenue_7d_amount: number;
          revenue_7d_growth: number | null;
          tickets_1d_count: number;
          tickets_1d_growth: number | null;
          tickets_28d_count: number;
          tickets_28d_growth: number | null;
          tickets_7d_count: number;
          tickets_7d_growth: number | null;
          total_revenue: number;
          total_tickets_sold: number;
          updated_at: string;
          venue_page_id: string;
        };
        Insert: {
          referrers_1d?: Json;
          referrers_28d?: Json;
          referrers_7d?: Json;
          referrers_lifetime?: Json;
          revenue_1d_amount: number;
          revenue_1d_growth?: number | null;
          revenue_28d_amount: number;
          revenue_28d_growth?: number | null;
          revenue_7d_amount: number;
          revenue_7d_growth?: number | null;
          tickets_1d_count: number;
          tickets_1d_growth?: number | null;
          tickets_28d_count: number;
          tickets_28d_growth?: number | null;
          tickets_7d_count: number;
          tickets_7d_growth?: number | null;
          total_revenue: number;
          total_tickets_sold: number;
          updated_at: string;
          venue_page_id: string;
        };
        Update: {
          referrers_1d?: Json;
          referrers_28d?: Json;
          referrers_7d?: Json;
          referrers_lifetime?: Json;
          revenue_1d_amount?: number;
          revenue_1d_growth?: number | null;
          revenue_28d_amount?: number;
          revenue_28d_growth?: number | null;
          revenue_7d_amount?: number;
          revenue_7d_growth?: number | null;
          tickets_1d_count?: number;
          tickets_1d_growth?: number | null;
          tickets_28d_count?: number;
          tickets_28d_growth?: number | null;
          tickets_7d_count?: number;
          tickets_7d_growth?: number | null;
          total_revenue?: number;
          total_tickets_sold?: number;
          updated_at?: string;
          venue_page_id?: string;
        };
        Relationships: [];
      };
      venue_ticket_clicks_daily: {
        Row: {
          comedian_clicks: Json;
          comedian_metadata: Json;
          comedian_referrers: Json;
          cumulative_ticket_link_clicks: number;
          ds: string;
          id: string;
          last_updated: string;
          mp_library: Json;
          num_ticket_link_clicks: number;
          os: Json;
          referrers: Json;
          time_of_day: Json;
          venue_page_id: string;
        };
        Insert: {
          comedian_clicks?: Json;
          comedian_metadata?: Json;
          comedian_referrers?: Json;
          cumulative_ticket_link_clicks: number;
          ds: string;
          id: string;
          last_updated: string;
          mp_library?: Json;
          num_ticket_link_clicks: number;
          os?: Json;
          referrers?: Json;
          time_of_day?: Json;
          venue_page_id: string;
        };
        Update: {
          comedian_clicks?: Json;
          comedian_metadata?: Json;
          comedian_referrers?: Json;
          cumulative_ticket_link_clicks?: number;
          ds?: string;
          id?: string;
          last_updated?: string;
          mp_library?: Json;
          num_ticket_link_clicks?: number;
          os?: Json;
          referrers?: Json;
          time_of_day?: Json;
          venue_page_id?: string;
        };
        Relationships: [];
      };
      venue_ticket_clicks_summary: {
        Row: {
          clicks_14d: number;
          clicks_28d: number;
          clicks_3d: number;
          clicks_7d: number;
          clicks_lifetime: number;
          comedian_clicks_14d: Json;
          comedian_clicks_28d: Json;
          comedian_clicks_3d: Json;
          comedian_clicks_7d: Json;
          comedian_clicks_lifetime: Json;
          comedian_metadata: Json;
          comedian_referrers_14d: Json;
          comedian_referrers_28d: Json;
          comedian_referrers_3d: Json;
          comedian_referrers_7d: Json;
          comedian_referrers_lifetime: Json;
          last_updated: string;
          mp_library_14d: Json;
          mp_library_28d: Json;
          mp_library_3d: Json;
          mp_library_7d: Json;
          mp_library_lifetime: Json;
          os_14d: Json;
          os_28d: Json;
          os_3d: Json;
          os_7d: Json;
          os_lifetime: Json;
          referrers_14d: Json;
          referrers_28d: Json;
          referrers_3d: Json;
          referrers_7d: Json;
          referrers_lifetime: Json;
          time_of_day_14d: Json;
          time_of_day_28d: Json;
          time_of_day_3d: Json;
          time_of_day_7d: Json;
          time_of_day_lifetime: Json;
          venue_page_id: string;
        };
        Insert: {
          clicks_14d?: number;
          clicks_28d?: number;
          clicks_3d?: number;
          clicks_7d?: number;
          clicks_lifetime?: number;
          comedian_clicks_14d?: Json;
          comedian_clicks_28d?: Json;
          comedian_clicks_3d?: Json;
          comedian_clicks_7d?: Json;
          comedian_clicks_lifetime?: Json;
          comedian_metadata?: Json;
          comedian_referrers_14d?: Json;
          comedian_referrers_28d?: Json;
          comedian_referrers_3d?: Json;
          comedian_referrers_7d?: Json;
          comedian_referrers_lifetime?: Json;
          last_updated: string;
          mp_library_14d?: Json;
          mp_library_28d?: Json;
          mp_library_3d?: Json;
          mp_library_7d?: Json;
          mp_library_lifetime?: Json;
          os_14d?: Json;
          os_28d?: Json;
          os_3d?: Json;
          os_7d?: Json;
          os_lifetime?: Json;
          referrers_14d?: Json;
          referrers_28d?: Json;
          referrers_3d?: Json;
          referrers_7d?: Json;
          referrers_lifetime?: Json;
          time_of_day_14d?: Json;
          time_of_day_28d?: Json;
          time_of_day_3d?: Json;
          time_of_day_7d?: Json;
          time_of_day_lifetime?: Json;
          venue_page_id: string;
        };
        Update: {
          clicks_14d?: number;
          clicks_28d?: number;
          clicks_3d?: number;
          clicks_7d?: number;
          clicks_lifetime?: number;
          comedian_clicks_14d?: Json;
          comedian_clicks_28d?: Json;
          comedian_clicks_3d?: Json;
          comedian_clicks_7d?: Json;
          comedian_clicks_lifetime?: Json;
          comedian_metadata?: Json;
          comedian_referrers_14d?: Json;
          comedian_referrers_28d?: Json;
          comedian_referrers_3d?: Json;
          comedian_referrers_7d?: Json;
          comedian_referrers_lifetime?: Json;
          last_updated?: string;
          mp_library_14d?: Json;
          mp_library_28d?: Json;
          mp_library_3d?: Json;
          mp_library_7d?: Json;
          mp_library_lifetime?: Json;
          os_14d?: Json;
          os_28d?: Json;
          os_3d?: Json;
          os_7d?: Json;
          os_lifetime?: Json;
          referrers_14d?: Json;
          referrers_28d?: Json;
          referrers_3d?: Json;
          referrers_7d?: Json;
          referrers_lifetime?: Json;
          time_of_day_14d?: Json;
          time_of_day_28d?: Json;
          time_of_day_3d?: Json;
          time_of_day_7d?: Json;
          time_of_day_lifetime?: Json;
          venue_page_id?: string;
        };
        Relationships: [];
      };
      venue_tixologi_events: {
        Row: {
          created_at: string | null;
          description: string | null;
          end_date: string | null;
          name: string;
          partner_id: number;
          start_date: string | null;
          status: string | null;
          ticket_types: Json | null;
          tickets_sold: number | null;
          time_zone: string | null;
          tixologi_event_id: number;
          updated_at: string | null;
          venue: string | null;
        };
        Insert: {
          created_at?: string | null;
          description?: string | null;
          end_date?: string | null;
          name: string;
          partner_id: number;
          start_date?: string | null;
          status?: string | null;
          ticket_types?: Json | null;
          tickets_sold?: number | null;
          time_zone?: string | null;
          tixologi_event_id: number;
          updated_at?: string | null;
          venue?: string | null;
        };
        Update: {
          created_at?: string | null;
          description?: string | null;
          end_date?: string | null;
          name?: string;
          partner_id?: number;
          start_date?: string | null;
          status?: string | null;
          ticket_types?: Json | null;
          tickets_sold?: number | null;
          time_zone?: string | null;
          tixologi_event_id?: number;
          updated_at?: string | null;
          venue?: string | null;
        };
        Relationships: [];
      };
      venue_tixologi_events_test: {
        Row: {
          created_at: string | null;
          description: string | null;
          end_date: string | null;
          name: string;
          partner_id: number;
          start_date: string | null;
          status: string | null;
          ticket_types: Json | null;
          tickets_sold: number | null;
          time_zone: string | null;
          tixologi_event_id: number;
          updated_at: string | null;
          venue: string | null;
        };
        Insert: {
          created_at?: string | null;
          description?: string | null;
          end_date?: string | null;
          name: string;
          partner_id: number;
          start_date?: string | null;
          status?: string | null;
          ticket_types?: Json | null;
          tickets_sold?: number | null;
          time_zone?: string | null;
          tixologi_event_id: number;
          updated_at?: string | null;
          venue?: string | null;
        };
        Update: {
          created_at?: string | null;
          description?: string | null;
          end_date?: string | null;
          name?: string;
          partner_id?: number;
          start_date?: string | null;
          status?: string | null;
          ticket_types?: Json | null;
          tickets_sold?: number | null;
          time_zone?: string | null;
          tixologi_event_id?: number;
          updated_at?: string | null;
          venue?: string | null;
        };
        Relationships: [];
      };
      venues: {
        Row: {
          address: string | null;
          capacity: number | null;
          city: string | null;
          country: string | null;
          country_code: string | null;
          created_at: string;
          created_by: string | null;
          id: string;
          is_deleted: boolean;
          is_pending: boolean;
          location: Json | null;
          location_geo: unknown;
          name: string | null;
          postal_code: string | null;
          state: string | null;
        };
        Insert: {
          address?: string | null;
          capacity?: number | null;
          city?: string | null;
          country?: string | null;
          country_code?: string | null;
          created_at?: string;
          created_by?: string | null;
          id?: string;
          is_deleted?: boolean;
          is_pending?: boolean;
          location?: Json | null;
          location_geo?: unknown;
          name?: string | null;
          postal_code?: string | null;
          state?: string | null;
        };
        Update: {
          address?: string | null;
          capacity?: number | null;
          city?: string | null;
          country?: string | null;
          country_code?: string | null;
          created_at?: string;
          created_by?: string | null;
          id?: string;
          is_deleted?: boolean;
          is_pending?: boolean;
          location?: Json | null;
          location_geo?: unknown;
          name?: string | null;
          postal_code?: string | null;
          state?: string | null;
        };
        Relationships: [];
      };
      venues_deprecated: {
        Row: {
          address: string | null;
          capacity: number | null;
          city: string | null;
          country: string | null;
          created_at: string;
          id: string;
          location: Json | null;
          name: string | null;
          postal_code: string | null;
          state: string | null;
        };
        Insert: {
          address?: string | null;
          capacity?: number | null;
          city?: string | null;
          country?: string | null;
          created_at?: string;
          id?: string;
          location?: Json | null;
          name?: string | null;
          postal_code?: string | null;
          state?: string | null;
        };
        Update: {
          address?: string | null;
          capacity?: number | null;
          city?: string | null;
          country?: string | null;
          created_at?: string;
          id?: string;
          location?: Json | null;
          name?: string | null;
          postal_code?: string | null;
          state?: string | null;
        };
        Relationships: [];
      };
      venues_with_capacity: {
        Row: {
          address: string | null;
          capacity: number | null;
          city: string | null;
          country: string | null;
          created_at: string;
          id: string;
          location: Json | null;
          location_geo: unknown;
          name: string | null;
          postal_code: string | null;
          state: string | null;
        };
        Insert: {
          address?: string | null;
          capacity?: number | null;
          city?: string | null;
          country?: string | null;
          created_at?: string;
          id: string;
          location?: Json | null;
          location_geo?: unknown;
          name?: string | null;
          postal_code?: string | null;
          state?: string | null;
        };
        Update: {
          address?: string | null;
          capacity?: number | null;
          city?: string | null;
          country?: string | null;
          created_at?: string;
          id?: string;
          location?: Json | null;
          location_geo?: unknown;
          name?: string | null;
          postal_code?: string | null;
          state?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "venues_with_capacity_id_fkey";
            columns: ["id"];
            isOneToOne: true;
            referencedRelation: "venues";
            referencedColumns: ["id"];
          },
        ];
      };
      video_metrics: {
        Row: {
          clicked_play_count: number | null;
          created_at: string;
          id: number;
          reactions_count: number | null;
          updated_at: string | null;
          video_id: string | null;
        };
        Insert: {
          clicked_play_count?: number | null;
          created_at?: string;
          id?: number;
          reactions_count?: number | null;
          updated_at?: string | null;
          video_id?: string | null;
        };
        Update: {
          clicked_play_count?: number | null;
          created_at?: string;
          id?: number;
          reactions_count?: number | null;
          updated_at?: string | null;
          video_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "video_metrics_video_id_fkey";
            columns: ["video_id"];
            isOneToOne: true;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      video_ranking_score: {
        Row: {
          created_at: string;
          ds: string | null;
          id: number;
          score: number | null;
          video_id: string;
        };
        Insert: {
          created_at?: string;
          ds?: string | null;
          id?: number;
          score?: number | null;
          video_id?: string;
        };
        Update: {
          created_at?: string;
          ds?: string | null;
          id?: number;
          score?: number | null;
          video_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "video_ranking_score_video_id_fkey";
            columns: ["video_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      videos: {
        Row: {
          cloudflare_preview_pct: number | null;
          cloudflare_preview_state: string | null;
          cloudflare_preview_video_id: string | null;
          cloudflare_source_pct: number | null;
          cloudflare_source_state: string | null;
          cloudflare_video_id: string | null;
          created_at: string;
          duration_seconds: number | null;
          failed_at: string | null;
          failure_reason: string | null;
          failure_stage: string | null;
          gcs_audio_path: string | null;
          gcs_raw_path: string | null;
          generate_preview: boolean;
          id: string;
          is_vertical: boolean | null;
          overall_status: string;
          pipeline_complete: boolean;
          playback_url: string | null;
          product_metadata: Json | null;
          requires_token: boolean;
          transcode_status: string | null;
          updated_at: string;
        };
        Insert: {
          cloudflare_preview_pct?: number | null;
          cloudflare_preview_state?: string | null;
          cloudflare_preview_video_id?: string | null;
          cloudflare_source_pct?: number | null;
          cloudflare_source_state?: string | null;
          cloudflare_video_id?: string | null;
          created_at?: string;
          duration_seconds?: number | null;
          failed_at?: string | null;
          failure_reason?: string | null;
          failure_stage?: string | null;
          gcs_audio_path?: string | null;
          gcs_raw_path?: string | null;
          generate_preview?: boolean;
          id?: string;
          is_vertical?: boolean | null;
          overall_status?: string;
          pipeline_complete?: boolean;
          playback_url?: string | null;
          product_metadata?: Json | null;
          requires_token?: boolean;
          transcode_status?: string | null;
          updated_at?: string;
        };
        Update: {
          cloudflare_preview_pct?: number | null;
          cloudflare_preview_state?: string | null;
          cloudflare_preview_video_id?: string | null;
          cloudflare_source_pct?: number | null;
          cloudflare_source_state?: string | null;
          cloudflare_video_id?: string | null;
          created_at?: string;
          duration_seconds?: number | null;
          failed_at?: string | null;
          failure_reason?: string | null;
          failure_stage?: string | null;
          gcs_audio_path?: string | null;
          gcs_raw_path?: string | null;
          generate_preview?: boolean;
          id?: string;
          is_vertical?: boolean | null;
          overall_status?: string;
          pipeline_complete?: boolean;
          playback_url?: string | null;
          product_metadata?: Json | null;
          requires_token?: boolean;
          transcode_status?: string | null;
          updated_at?: string;
        };
        Relationships: [];
      };
      waitlist: {
        Row: {
          authenticated: boolean | null;
          created_at: string;
          email: string | null;
          id: string;
          is_venue: boolean | null;
          owner_id: string | null;
          phone: string | null;
          receive_updates: boolean | null;
          show_id: string;
          show_name: string | null;
          user_id: string | null;
        };
        Insert: {
          authenticated?: boolean | null;
          created_at?: string;
          email?: string | null;
          id?: string;
          is_venue?: boolean | null;
          owner_id?: string | null;
          phone?: string | null;
          receive_updates?: boolean | null;
          show_id: string;
          show_name?: string | null;
          user_id?: string | null;
        };
        Update: {
          authenticated?: boolean | null;
          created_at?: string;
          email?: string | null;
          id?: string;
          is_venue?: boolean | null;
          owner_id?: string | null;
          phone?: string | null;
          receive_updates?: boolean | null;
          show_id?: string;
          show_name?: string | null;
          user_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "waitlist_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "waitlist_show_id_fkey";
            columns: ["show_id"];
            isOneToOne: false;
            referencedRelation: "comedian_shows_with_display";
            referencedColumns: ["id"];
          },
        ];
      };
      web_notifications: {
        Row: {
          comedian_id: string | null;
          content_id: string | null;
          created_at: string;
          notification_id: string;
          read: boolean;
          related_timestamp: string | null;
          type: Database["public"]["Enums"]["notification_type"] | null;
          user_id: string;
        };
        Insert: {
          comedian_id?: string | null;
          content_id?: string | null;
          created_at?: string;
          notification_id?: string;
          read?: boolean;
          related_timestamp?: string | null;
          type?: Database["public"]["Enums"]["notification_type"] | null;
          user_id?: string;
        };
        Update: {
          comedian_id?: string | null;
          content_id?: string | null;
          created_at?: string;
          notification_id?: string;
          read?: boolean;
          related_timestamp?: string | null;
          type?: Database["public"]["Enums"]["notification_type"] | null;
          user_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "public_web_notifications_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      weekly_email_audience: {
        Row: {
          batchname: Database["public"]["Enums"]["batchname"];
          country_code: string | null;
          email: string;
          rownum: number;
          user_id: string;
          zipcode: string;
        };
        Insert: {
          batchname?: Database["public"]["Enums"]["batchname"];
          country_code?: string | null;
          email: string;
          rownum: number;
          user_id: string;
          zipcode: string;
        };
        Update: {
          batchname?: Database["public"]["Enums"]["batchname"];
          country_code?: string | null;
          email?: string;
          rownum?: number;
          user_id?: string;
          zipcode?: string;
        };
        Relationships: [];
      };
      weekly_email_rollout: {
        Row: {
          created_at: string;
          email: string | null;
          id: number;
          user_id: string | null;
        };
        Insert: {
          created_at?: string;
          email?: string | null;
          id?: number;
          user_id?: string | null;
        };
        Update: {
          created_at?: string;
          email?: string | null;
          id?: number;
          user_id?: string | null;
        };
        Relationships: [];
      };
      widget_api_audit_log: {
        Row: {
          comedian_id: string | null;
          created_at: string;
          error_message: string | null;
          event_type: string;
          id: string;
          ip_address: string | null;
          metadata: Json | null;
          origin: string | null;
          partner_id: string | null;
          user_agent: string | null;
        };
        Insert: {
          comedian_id?: string | null;
          created_at?: string;
          error_message?: string | null;
          event_type: string;
          id?: string;
          ip_address?: string | null;
          metadata?: Json | null;
          origin?: string | null;
          partner_id?: string | null;
          user_agent?: string | null;
        };
        Update: {
          comedian_id?: string | null;
          created_at?: string;
          error_message?: string | null;
          event_type?: string;
          id?: string;
          ip_address?: string | null;
          metadata?: Json | null;
          origin?: string | null;
          partner_id?: string | null;
          user_agent?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "widget_api_audit_log_partner_id_fkey";
            columns: ["partner_id"];
            isOneToOne: false;
            referencedRelation: "widget_api_partners";
            referencedColumns: ["id"];
          },
        ];
      };
      widget_api_partner_domains: {
        Row: {
          created_at: string;
          domain_pattern: string;
          id: string;
          is_active: boolean;
          partner_id: string;
        };
        Insert: {
          created_at?: string;
          domain_pattern: string;
          id?: string;
          is_active?: boolean;
          partner_id: string;
        };
        Update: {
          created_at?: string;
          domain_pattern?: string;
          id?: string;
          is_active?: boolean;
          partner_id?: string;
        };
        Relationships: [
          {
            foreignKeyName: "widget_api_partner_domains_partner_id_fkey";
            columns: ["partner_id"];
            isOneToOne: false;
            referencedRelation: "widget_api_partners";
            referencedColumns: ["id"];
          },
        ];
      };
      widget_api_partners: {
        Row: {
          allowed_comedian_ids: string[] | null;
          allowed_venue_page_ids: string[] | null;
          api_key_encrypted: string | null;
          api_key_hash: string;
          api_key_prefix: string;
          comedian_id: string | null;
          contact_email: string | null;
          contact_name: string | null;
          created_at: string;
          created_by: string | null;
          description: string | null;
          id: string;
          is_active: boolean;
          kind: string;
          name: string;
          rate_limit_rpm: number;
          read_only: boolean;
          revoked_at: string | null;
          revoked_by: string | null;
          revoked_reason: string | null;
          updated_at: string;
        };
        Insert: {
          allowed_comedian_ids?: string[] | null;
          allowed_venue_page_ids?: string[] | null;
          api_key_encrypted?: string | null;
          api_key_hash: string;
          api_key_prefix: string;
          comedian_id?: string | null;
          contact_email?: string | null;
          contact_name?: string | null;
          created_at?: string;
          created_by?: string | null;
          description?: string | null;
          id?: string;
          is_active?: boolean;
          kind?: string;
          name: string;
          rate_limit_rpm?: number;
          read_only?: boolean;
          revoked_at?: string | null;
          revoked_by?: string | null;
          revoked_reason?: string | null;
          updated_at?: string;
        };
        Update: {
          allowed_comedian_ids?: string[] | null;
          allowed_venue_page_ids?: string[] | null;
          api_key_encrypted?: string | null;
          api_key_hash?: string;
          api_key_prefix?: string;
          comedian_id?: string | null;
          contact_email?: string | null;
          contact_name?: string | null;
          created_at?: string;
          created_by?: string | null;
          description?: string | null;
          id?: string;
          is_active?: boolean;
          kind?: string;
          name?: string;
          rate_limit_rpm?: number;
          read_only?: boolean;
          revoked_at?: string | null;
          revoked_by?: string | null;
          revoked_reason?: string | null;
          updated_at?: string;
        };
        Relationships: [
          {
            foreignKeyName: "widget_api_partners_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
        ];
      };
      widget_api_tokens: {
        Row: {
          allowed_comedian_ids: string[] | null;
          created_at: string;
          expires_at: string;
          id: string;
          partner_id: string;
          token_hash: string;
          used_at: string | null;
        };
        Insert: {
          allowed_comedian_ids?: string[] | null;
          created_at?: string;
          expires_at: string;
          id?: string;
          partner_id: string;
          token_hash: string;
          used_at?: string | null;
        };
        Update: {
          allowed_comedian_ids?: string[] | null;
          created_at?: string;
          expires_at?: string;
          id?: string;
          partner_id?: string;
          token_hash?: string;
          used_at?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "widget_api_tokens_partner_id_fkey";
            columns: ["partner_id"];
            isOneToOne: false;
            referencedRelation: "widget_api_partners";
            referencedColumns: ["id"];
          },
        ];
      };
      youtube_tokens: {
        Row: {
          access_token: string;
          created_at: string | null;
          expires_at: string;
          refresh_token: string | null;
          updated_at: string | null;
          user_id: string;
        };
        Insert: {
          access_token: string;
          created_at?: string | null;
          expires_at: string;
          refresh_token?: string | null;
          updated_at?: string | null;
          user_id: string;
        };
        Update: {
          access_token?: string;
          created_at?: string | null;
          expires_at?: string;
          refresh_token?: string | null;
          updated_at?: string | null;
          user_id?: string;
        };
        Relationships: [];
      };
      zipcodes_deprecated: {
        Row: {
          city: string | null;
          country_code: string | null;
          latitude: number | null;
          location_geo: unknown;
          longitude: number | null;
          state: string | null;
          state_abbreviation: string | null;
          verified: boolean;
          zipcode: number;
        };
        Insert: {
          city?: string | null;
          country_code?: string | null;
          latitude?: number | null;
          location_geo?: unknown;
          longitude?: number | null;
          state?: string | null;
          state_abbreviation?: string | null;
          verified?: boolean;
          zipcode: number;
        };
        Update: {
          city?: string | null;
          country_code?: string | null;
          latitude?: number | null;
          location_geo?: unknown;
          longitude?: number | null;
          state?: string | null;
          state_abbreviation?: string | null;
          verified?: boolean;
          zipcode?: number;
        };
        Relationships: [];
      };
    };
    Views: {
      audience_contacts: {
        Row: {
          city: string | null;
          comedian_id: string | null;
          created_at: string | null;
          email: string | null;
          id: string | null;
          latitude: number | null;
          location_geo: unknown;
          longitude: number | null;
          phone: string | null;
          source: string | null;
          state: string | null;
          state_abbreviation: string | null;
        };
        Relationships: [];
      };
      comedian_shows_with_display: {
        Row: {
          comedian_display_name: string | null;
          comedian_id: string | null;
          created_at: string | null;
          datetime: string | null;
          flags: Database["public"]["Enums"]["ticket_flags"][] | null;
          id: string | null;
          is_sold_out: boolean | null;
          location: string | null;
          metadata_text: string | null;
          poster_img: string | null;
          presale_code: string | null;
          ticket_link: string | null;
          title: string | null;
          tixologi_event_id: string | null;
          venue: string | null;
          venue_id: string | null;
          vip_ticket_link: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "comedian_shows_comedian_id_fkey";
            columns: ["comedian_id"];
            isOneToOne: false;
            referencedRelation: "comedians";
            referencedColumns: ["id"];
          },
          {
            foreignKeyName: "comedian_shows_venue_id_fkey";
            columns: ["venue_id"];
            isOneToOne: false;
            referencedRelation: "venues";
            referencedColumns: ["id"];
          },
        ];
      };
      comedian_tickets_lookback_aggregates: {
        Row: {
          ComedianId: string | null;
          day_of_week_counts: Json | null;
          referrer_counts: Json | null;
          total_counts: Json | null;
          ts: string | null;
          venue_counts: Json | null;
        };
        Relationships: [];
      };
      comedian_views_lookback_aggregates: {
        Row: {
          comedian_id: string | null;
          day_of_week_counts: Json | null;
          referrer_counts: Json | null;
          special_counts: Json | null;
          total_counts: Json | null;
          ts: string | null;
        };
        Relationships: [];
      };
      events_clicked_play_aggregate: {
        Row: {
          comedian_id: string | null;
          count: number | null;
          ds: string | null;
        };
        Relationships: [];
      };
      events_followed_comedian_aggregate: {
        Row: {
          comedian_id: string | null;
          count: number | null;
          ds: string | null;
        };
        Relationships: [];
      };
      events_tickets_aggregate: {
        Row: {
          comedian_id: string | null;
          count: number | null;
          ds: string | null;
        };
        Relationships: [];
      };
      events_video_viewed_3s_comedian_aggregate: {
        Row: {
          comedian_id: string | null;
          count: number | null;
          ds: string | null;
        };
        Relationships: [];
      };
      events_video_viewed_3s_special_aggregate: {
        Row: {
          count: number | null;
          ds: string | null;
          special_id: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "events_video_viewed_3s_special_id_fkey";
            columns: ["special_id"];
            isOneToOne: false;
            referencedRelation: "specials";
            referencedColumns: ["id"];
          },
        ];
      };
      geography_columns: {
        Row: {
          coord_dimension: number | null;
          f_geography_column: unknown;
          f_table_catalog: unknown;
          f_table_name: unknown;
          f_table_schema: unknown;
          srid: number | null;
          type: string | null;
        };
        Relationships: [];
      };
      geometry_columns: {
        Row: {
          coord_dimension: number | null;
          f_geometry_column: unknown;
          f_table_catalog: string | null;
          f_table_name: unknown;
          f_table_schema: unknown;
          srid: number | null;
          type: string | null;
        };
        Insert: {
          coord_dimension?: number | null;
          f_geometry_column?: unknown;
          f_table_catalog?: string | null;
          f_table_name?: unknown;
          f_table_schema?: unknown;
          srid?: number | null;
          type?: string | null;
        };
        Update: {
          coord_dimension?: number | null;
          f_geometry_column?: unknown;
          f_table_catalog?: string | null;
          f_table_name?: unknown;
          f_table_schema?: unknown;
          srid?: number | null;
          type?: string | null;
        };
        Relationships: [];
      };
      gift_cards_with_balance: {
        Row: {
          code: string | null;
          computed_status: string | null;
          created_at: string | null;
          current_balance: number | null;
          disabled_at: string | null;
          expires_at: string | null;
          id: string | null;
          initial_balance: number | null;
          message: string | null;
          purchased_at: string | null;
          recipient_email: string | null;
          recipient_name: string | null;
          sender_email: string | null;
          sender_name: string | null;
          status: Database["public"]["Enums"]["gift_card_status"] | null;
          stripe_payment_intent_id: string | null;
          toast_card_id: string | null;
          venue_id: string | null;
        };
        Insert: {
          code?: string | null;
          computed_status?: never;
          created_at?: string | null;
          current_balance?: never;
          disabled_at?: string | null;
          expires_at?: string | null;
          id?: string | null;
          initial_balance?: number | null;
          message?: string | null;
          purchased_at?: string | null;
          recipient_email?: string | null;
          recipient_name?: string | null;
          sender_email?: string | null;
          sender_name?: string | null;
          status?: Database["public"]["Enums"]["gift_card_status"] | null;
          stripe_payment_intent_id?: string | null;
          toast_card_id?: string | null;
          venue_id?: string | null;
        };
        Update: {
          code?: string | null;
          computed_status?: never;
          created_at?: string | null;
          current_balance?: never;
          disabled_at?: string | null;
          expires_at?: string | null;
          id?: string | null;
          initial_balance?: number | null;
          message?: string | null;
          purchased_at?: string | null;
          recipient_email?: string | null;
          recipient_name?: string | null;
          sender_email?: string | null;
          sender_name?: string | null;
          status?: Database["public"]["Enums"]["gift_card_status"] | null;
          stripe_payment_intent_id?: string | null;
          toast_card_id?: string | null;
          venue_id?: string | null;
        };
        Relationships: [
          {
            foreignKeyName: "gift_cards_venue_id_fkey";
            columns: ["venue_id"];
            isOneToOne: false;
            referencedRelation: "venue_pages";
            referencedColumns: ["id"];
          },
        ];
      };
      venue_dash_facts: {
        Row: {
          average_ticket_price_7d: number | null;
          event_day: string | null;
          tickets_7d_count: number | null;
          total_revenue: number | null;
          total_revenue_7d: number | null;
          total_tickets_sold: number | null;
          venue_id: string | null;
        };
        Insert: {
          average_ticket_price_7d?: never;
          event_day?: never;
          tickets_7d_count?: number | null;
          total_revenue?: number | null;
          total_revenue_7d?: number | null;
          total_tickets_sold?: number | null;
          venue_id?: string | null;
        };
        Update: {
          average_ticket_price_7d?: never;
          event_day?: never;
          tickets_7d_count?: number | null;
          total_revenue?: number | null;
          total_revenue_7d?: number | null;
          total_tickets_sold?: number | null;
          venue_id?: string | null;
        };
        Relationships: [];
      };
    };
    Functions: {
      _postgis_deprecate: {
        Args: { newname: string; oldname: string; version: string };
        Returns: undefined;
      };
      _postgis_index_extent: {
        Args: { col: string; tbl: unknown };
        Returns: unknown;
      };
      _postgis_pgsql_version: { Args: never; Returns: string };
      _postgis_scripts_pgsql_version: { Args: never; Returns: string };
      _postgis_selectivity: {
        Args: { att_name: string; geom: unknown; mode?: string; tbl: unknown };
        Returns: number;
      };
      _postgis_stats: {
        Args: { ""?: string; att_name: string; tbl: unknown };
        Returns: string;
      };
      _st_3dintersects: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      _st_contains: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      _st_containsproperly: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      _st_coveredby:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: boolean }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: boolean };
      _st_covers:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: boolean }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: boolean };
      _st_crosses: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      _st_dwithin: {
        Args: {
          geog1: unknown;
          geog2: unknown;
          tolerance: number;
          use_spheroid?: boolean;
        };
        Returns: boolean;
      };
      _st_equals: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      _st_intersects: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      _st_linecrossingdirection: {
        Args: { line1: unknown; line2: unknown };
        Returns: number;
      };
      _st_longestline: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      _st_maxdistance: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: number;
      };
      _st_orderingequals: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      _st_overlaps: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      _st_sortablehash: { Args: { geom: unknown }; Returns: number };
      _st_touches: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      _st_voronoi: {
        Args: {
          clip?: unknown;
          g1: unknown;
          return_polygons?: boolean;
          tolerance?: number;
        };
        Returns: unknown;
      };
      _st_within: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      ad_campaigns_near_location: {
        Args: {
          campaign_type: Database["public"]["Enums"]["ad_campaign_type"];
          latitude: number;
          longitude: number;
          range_km: number;
        };
        Returns: {
          comedian_id: string;
          created_at: string;
          end_date: string;
          id: string;
          location_name: string;
          show_ids: string[];
          start_date: string;
          type: Database["public"]["Enums"]["ad_campaign_type"];
        }[];
      };
      addauth: { Args: { "": string }; Returns: boolean };
      addgeometrycolumn:
        | {
            Args: {
              catalog_name: string;
              column_name: string;
              new_dim: number;
              new_srid_in: number;
              new_type: string;
              schema_name: string;
              table_name: string;
              use_typmod?: boolean;
            };
            Returns: string;
          }
        | {
            Args: {
              column_name: string;
              new_dim: number;
              new_srid: number;
              new_type: string;
              schema_name: string;
              table_name: string;
              use_typmod?: boolean;
            };
            Returns: string;
          }
        | {
            Args: {
              column_name: string;
              new_dim: number;
              new_srid: number;
              new_type: string;
              table_name: string;
              use_typmod?: boolean;
            };
            Returns: string;
          };
      calculate_gift_card_balance: {
        Args: { card_id: string };
        Returns: number;
      };
      calculate_import_net_new_contacts: {
        Args: {
          p_comedian_id: string;
          p_emails: string[];
          p_exclude_list_id?: string;
        };
        Returns: Json;
      };
      check_id_near_venue: {
        Args: { p_user_id: string; p_venue_id: string };
        Returns: boolean;
      };
      check_phone_exists: { Args: { phone_number: string }; Returns: boolean };
      claim_due_newsletter_issues: {
        Args: {
          p_max_attempts?: number;
          p_max_batch?: number;
          p_reclaim_minutes?: number;
          p_threshold_minutes?: number;
        };
        Returns: {
          approved_at: string | null;
          approved_by: string | null;
          audience_count: number | null;
          comedian_id: string;
          created_at: string;
          created_by: string | null;
          deleted_at: string | null;
          dispatch_attempts: number;
          dispatch_claimed_at: string | null;
          dispatch_error: string | null;
          doc: Json;
          id: string;
          planned_for: string | null;
          preview_text: string;
          revision: number;
          scheduled_for: string | null;
          send_at: string | null;
          sent_at: string | null;
          series_id: string | null;
          southpaw_job_id: string | null;
          started_at: string;
          status: Database["public"]["Enums"]["newsletter_issue_status"];
          subject: string;
          updated_at: string;
        }[];
        SetofOptions: {
          from: "*";
          to: "newsletter_issues";
          isOneToOne: false;
          isSetofReturn: true;
        };
      };
      claim_due_scheduled_notifications: {
        Args: { p_limit?: number };
        Returns: string[];
      };
      claim_notification_batch: {
        Args: { p_group_key: string; p_notification_type_id: string };
        Returns: string;
      };
      clean_event_name: { Args: { name: string }; Returns: string };
      clean_show: { Args: { "": string }; Returns: string };
      comedian_fans_near_venues_pipeline: { Args: never; Returns: undefined };
      compute_user_comedian_scores: {
        Args: {
          p_exclude_followed?: boolean;
          p_limit?: number;
          p_user_id: string;
        };
        Returns: {
          comedian_id: string;
          score: number;
        }[];
      };
      compute_user_show_scores: {
        Args: {
          p_filter_by_user_state?: boolean;
          p_limit?: number;
          p_only_upcoming?: boolean;
          p_user_id: string;
        };
        Returns: {
          score: number;
          show_id: string;
        }[];
      };
      count_signed_in_users: { Args: never; Returns: number };
      create_southpaw_audience: {
        Args: { p_comedian_id: string; p_job_id: string; p_show_id: string };
        Returns: undefined;
      };
      create_update_shows_with_comedians: {
        Args: {
          show_comedian_pairings?: Json;
          show_creator?: Json;
          show_records: Json;
          venue_page_show_pairings?: Json;
        };
        Returns: undefined;
      };
      credit_debit: {
        Args: {
          p_amount: number;
          p_artist_id: string;
          p_reference?: string;
          p_reference_type?: string;
          p_user_id?: string;
        };
        Returns: Json;
      };
      credit_debit_automations: {
        Args: {
          p_amount: number;
          p_artist_id: string;
          p_reference?: string;
          p_reference_type?: string;
          p_user_id?: string;
        };
        Returns: Json;
      };
      credit_deposit: {
        Args: {
          p_amount: number;
          p_artist_id: string;
          p_pool: Database["public"]["Enums"]["credit_pool_type"];
          p_reference?: string;
          p_reference_type?: string;
          p_transaction_type: Database["public"]["Enums"]["credit_transaction_type"];
          p_user_id?: string;
        };
        Returns: Json;
      };
      credit_deposit_automations_once: {
        Args: {
          p_amount: number;
          p_artist_id: string;
          p_reference: string;
          p_reference_type?: string;
          p_transaction_type: Database["public"]["Enums"]["credit_transaction_type"];
          p_user_id?: string;
        };
        Returns: Json;
      };
      credit_refund: {
        Args: {
          p_amount: number;
          p_artist_id: string;
          p_pool: Database["public"]["Enums"]["credit_pool_type"];
          p_reference?: string;
          p_reference_type?: string;
          p_user_id?: string;
        };
        Returns: Json;
      };
      credit_reset_automations_balance: {
        Args: {
          p_amount: number;
          p_artist_id: string;
          p_reference: string;
          p_reference_type?: string;
          p_transaction_type: Database["public"]["Enums"]["credit_transaction_type"];
          p_user_id?: string;
        };
        Returns: Json;
      };
      credit_reset_free_balance: {
        Args: { p_amount?: number; p_artist_id: string };
        Returns: Json;
      };
      delete_rows_past_retention: {
        Args: { __retention: number; __table_name: string };
        Returns: undefined;
      };
      delete_stale_global_fallback_comedian_recommendations: {
        Args: never;
        Returns: undefined;
      };
      delete_stale_global_fallback_specials_recommendations: {
        Args: never;
        Returns: undefined;
      };
      delete_stale_user_comedian_recommendations: {
        Args: never;
        Returns: undefined;
      };
      delete_stale_user_specials_recommendations: {
        Args: never;
        Returns: undefined;
      };
      delete_venue_sales_data: {
        Args: { venue_id: string };
        Returns: undefined;
      };
      describe_ai_whitelist: { Args: never; Returns: Json };
      describe_ai_whitelist_anon: { Args: never; Returns: Json };
      describe_ai_whitelist_mux: { Args: { p_mode?: string }; Returns: Json };
      disablelongtransactions: { Args: never; Returns: string };
      dmetaphone: { Args: { "": string }; Returns: string };
      dmetaphone_alt: { Args: { "": string }; Returns: string };
      drop_user_comedian_recs: { Args: never; Returns: undefined };
      drop_user_special_recs: { Args: never; Returns: undefined };
      dropgeometrycolumn:
        | {
            Args: {
              catalog_name: string;
              column_name: string;
              schema_name: string;
              table_name: string;
            };
            Returns: string;
          }
        | {
            Args: {
              column_name: string;
              schema_name: string;
              table_name: string;
            };
            Returns: string;
          }
        | {
            Args: { column_name: string; table_name: string };
            Returns: string;
          };
      dropgeometrytable:
        | {
            Args: {
              catalog_name: string;
              schema_name: string;
              table_name: string;
            };
            Returns: string;
          }
        | { Args: { schema_name: string; table_name: string }; Returns: string }
        | { Args: { table_name: string }; Returns: string };
      enablelongtransactions: { Args: never; Returns: string };
      enqueue_scheduled_notification: {
        Args: {
          p_action_url?: string;
          p_body?: string;
          p_channels: string[];
          p_comedian_id?: string;
          p_courier_data?: Json;
          p_courier_email_template_id?: string;
          p_courier_push_template_id?: string;
          p_image_url?: string;
          p_notification_type_id: string;
          p_recipient_user_ids: string[];
          p_scheduled_for: string;
          p_source_id?: string;
          p_source_type?: string;
          p_title?: string;
        };
        Returns: string;
      };
      equals: { Args: { geom1: unknown; geom2: unknown }; Returns: boolean };
      execute_sql: { Args: { sql: string }; Returns: Json[] };
      expire_old_queue_items: { Args: never; Returns: undefined };
      export_comedian_subscribers: {
        Args: { p_comedian_id: string };
        Returns: {
          city: string;
          country_code: string;
          email: string;
          email_subscribed: boolean;
          phone: string;
          state: string;
          subscribed_at: string;
          zipcode: string;
        }[];
      };
      export_venue_subscribers: {
        Args: { p_venue_page_id: string };
        Returns: {
          city: string;
          country_code: string;
          email: string;
          email_subscribed: boolean;
          phone: string;
          state: string;
          subscribed_at: string;
          zipcode: string;
        }[];
      };
      fetch_requests_for_comedian: {
        Args: { comedian_id_param: string };
        Returns: {
          created_at: string;
          requested_zipcode: string;
        }[];
      };
      geometry: { Args: { "": string }; Returns: unknown };
      geometry_above: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_below: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_cmp: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: number;
      };
      geometry_contained_3d: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_contains: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_contains_3d: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_distance_box: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: number;
      };
      geometry_distance_centroid: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: number;
      };
      geometry_eq: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_ge: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_gt: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_le: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_left: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_lt: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_overabove: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_overbelow: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_overlaps: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_overlaps_3d: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_overleft: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_overright: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_right: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_same: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_same_3d: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geometry_within: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      geomfromewkt: { Args: { "": string }; Returns: unknown };
      get_and_lock_available_test_user: {
        Args: never;
        Returns: Record<string, unknown>;
      };
      get_audience_cities: {
        Args: { p_comedian_id: string };
        Returns: {
          city: string;
          state: string;
        }[];
      };
      get_cities_count: { Args: { contact_list_id: string }; Returns: number };
      get_city_coordinates: {
        Args: { _city: string; _country: string; _region: string };
        Returns: {
          lat: number;
          long: number;
        }[];
      };
      get_comedian_combined_audience_counts: {
        Args: { p_comedian_id: string };
        Returns: Json;
      };
      get_comedian_contacts_summary: {
        Args: { p_comedian_id: string; p_max_distance_miles?: number };
        Returns: {
          city: string;
          contacts_within_distance: number;
          country: string;
          next_show: string;
          num_shows: number;
          state: string;
          total_reachable_emails: number;
          total_reachable_phones: number;
          venue_id: string;
          venue_name: string;
        }[];
      };
      get_comedian_fans_summary: {
        Args: { p_comedian_id: string; p_max_distance_miles?: number };
        Returns: {
          city: string;
          country: string;
          next_show: string;
          num_shows: number;
          state: string;
          total_reachable_emails: number;
          total_reachable_phones: number;
          users_within_distance: number;
          venue_id: string;
          venue_name: string;
        }[];
      };
      get_comedian_followers_by_city: {
        Args: { p_comedian_id: string };
        Returns: {
          city: string;
          follower_count: number;
          latitude: number;
          longitude: number;
          state: string;
        }[];
      };
      get_comedian_shows:
        | {
            Args: { comedian_id_input: string };
            Returns: {
              show_date: string;
              show_id: string;
              show_time: string;
              venue_address: string;
              venue_city: string;
              venue_country: string;
              venue_location: Json;
              venue_location_geo: unknown;
              venue_name: string;
              venue_postal_code: string;
              venue_state: string;
            }[];
          }
        | {
            Args: {
              input_city?: string;
              input_comedian_id: string;
              venue_names: string[];
            };
            Returns: {
              datetime: string;
              id: string;
              location: string;
              matched_venue_name: string;
              venue: string;
              venue_id: string;
            }[];
          };
      get_contact_list_stats: { Args: { p_list_id: string }; Returns: Json };
      get_contacts_count: {
        Args: { contact_list_id: string };
        Returns: number;
      };
      get_contacts_near_venue: {
        Args: {
          p_comedian_id: string;
          p_max_distance_miles?: number;
          p_venue_id: string;
        };
        Returns: {
          contact_id: string;
          distance_miles: number;
          email: string;
          phone: string;
        }[];
      };
      get_contacts_not_in_southpaw_audience: {
        Args: {
          contact_limit: number;
          contact_offset: number;
          southpaw_job_id: string;
        };
        Returns: {
          email: string;
          id: string;
          unsubtoken: string;
        }[];
      };
      get_dashboard_update_time: { Args: never; Returns: string };
      get_effective_moderation_thresholds: {
        Args: { p_comedian_id: string };
        Returns: {
          attribute: string;
          is_custom: boolean;
          threshold: number;
        }[];
      };
      get_eligible_users_for_push_job: {
        Args: { p_job_id: string };
        Returns: {
          tokens: string[];
          user_id: string;
        }[];
      };
      get_email_clicks_audience: {
        Args: {
          p_clicked_after_date?: string;
          p_clicked_before_date?: string;
          p_comedian_id: string;
        };
        Returns: {
          email: string;
          phone: string;
          user_id: string;
        }[];
      };
      get_email_for_user_id: { Args: { p_user_id: string }; Returns: string };
      get_external_show_creators_for_comedian: {
        Args: { p_comedian_id: string };
        Returns: {
          entity_id: string;
          entity_name: string;
          entity_type: Database["public"]["Enums"]["show_creator_entity_type"];
          show_id: string;
        }[];
      };
      get_fans_near_venue: {
        Args: {
          p_comedian_id: string;
          p_max_distance_miles?: number;
          p_venue_id: string;
        };
        Returns: {
          distance_miles: number;
          email: string;
          phone: string;
          user_id: string;
        }[];
      };
      get_follower_emails_near_venue: {
        Args: { comedian_id_input: string; venue_ids_input: string };
        Returns: {
          created_at: string;
          email: string;
          postal_code: string;
          venue: string;
          venue_id: string;
          zipcode: string;
        }[];
      };
      get_guest_list_audience: {
        Args: {
          p_comedian_id: string;
          p_followed_after_date?: string;
          p_followed_before_date?: string;
        };
        Returns: {
          email: string;
          phone: string;
          user_id: string;
        }[];
      };
      get_guest_list_counts: {
        Args: {
          p_comedian_id: string;
          p_followed_after_date?: string;
          p_followed_before_date?: string;
        };
        Returns: {
          both_count: number;
          email_count: number;
          fan_count: number;
          phone_count: number;
        }[];
      };
      get_id_for_email: { Args: { p_email: string }; Returns: string };
      get_latest_video_rankings: { Args: never; Returns: undefined };
      get_max_timestamp: { Args: { tablename: string }; Returns: string };
      get_next_short_code: { Args: never; Returns: string };
      get_non_recent_users: { Args: { recency: number }; Returns: string[] };
      get_play_counts_by_comedian: {
        Args: never;
        Returns: {
          comedian_id: string;
          total_plays: number;
        }[];
      };
      get_play_counts_by_special: {
        Args: never;
        Returns: {
          count: number;
          special_id: string;
        }[];
      };
      get_random_vertical_shorts: {
        Args: {
          exclude_ids: string[];
          limit_count: number;
          specific_id?: string;
        };
        Returns: Json[];
      };
      get_show_requests_for_comedian: {
        Args: { comedian_id_param: string };
        Returns: {
          created_at: string;
          requested_zipcode: string;
        }[];
      };
      get_ticket_clicks_audience: {
        Args: {
          p_clicked_after_date?: string;
          p_clicked_before_date?: string;
          p_comedian_id: string;
        };
        Returns: {
          email: string;
          phone: string;
          user_id: string;
        }[];
      };
      get_unique_contacts_count: {
        Args: { list_ids: string[] };
        Returns: number;
      };
      get_unlocked_specials: { Args: { userid: string }; Returns: string[] };
      get_venue_page_followers_audience: {
        Args: { p_venue_page_id: string };
        Returns: {
          email: string;
          phone: string;
          user_id: string;
        }[];
      };
      get_venue_page_waitlist_audience: {
        Args: { p_show_ids: string[]; p_venue_page_id: string };
        Returns: {
          email: string;
          phone: string;
          show_id: string;
          user_id: string;
        }[];
      };
      get_venue_page_waitlist_counts: {
        Args: { p_show_ids: string[]; p_venue_page_id: string };
        Returns: {
          show_id: string;
          waitlist_count: number;
        }[];
      };
      get_venue_sales_summary: { Args: { venue_id: string }; Returns: Json };
      get_venues: {
        Args: { cities: string[]; input_comedian_id: number; venues: string[] };
        Returns: {
          city: string;
          datetime: string;
          id: string;
          location: string;
          name: string;
          venue: string;
          venue_id: string;
        }[];
      };
      get_vertical_random_shorts_recent: {
        Args: {
          exclude_ids?: string[];
          limit_count: number;
          specific_id?: string;
        };
        Returns: Json[];
      };
      get_video_plays_audience: {
        Args: {
          p_comedian_id: string;
          p_played_after_date?: string;
          p_played_before_date?: string;
        };
        Returns: {
          email: string;
          phone: string;
          user_id: string;
        }[];
      };
      get_waitlist_audience: {
        Args: { p_comedian_id: string; p_show_ids?: string[] };
        Returns: {
          email: string;
          phone: string;
          show_id: string;
          user_id: string;
        }[];
      };
      gettransactionid: { Args: never; Returns: unknown };
      increment_follow_totals: {
        Args: { from_ts: string };
        Returns: undefined;
      };
      increment_link_click_count: {
        Args: { short_code_id: string };
        Returns: undefined;
      };
      increment_link_click_count_from_email: {
        Args: { job_id: string };
        Returns: undefined;
      };
      increment_link_click_count_from_sms: {
        Args: { short_code_id: string };
        Returns: undefined;
      };
      increment_link_follow_count: {
        Args: { short_code_id: string };
        Returns: undefined;
      };
      increment_link_play_count: {
        Args: { short_code_id: string };
        Returns: undefined;
      };
      increment_link_ticket_clicks: {
        Args: { short_code_id: string };
        Returns: undefined;
      };
      increment_play_totals: { Args: { from_ts: string }; Returns: undefined };
      is_authorized_to_see_dashboards_temporary: {
        Args: { p_user_id: string };
        Returns: boolean;
      };
      is_super_admin: { Args: { p_user_id: string }; Returns: boolean };
      is_user_restricted: {
        Args: { p_comedian_id: string; p_user_id: string };
        Returns: boolean;
      };
      longtransactionsenabled: { Args: never; Returns: boolean };
      mcp_demo_anon_demo_hash_int: {
        Args: { modulo: number; txt: string };
        Returns: number;
      };
      mcp_demo_anon_test_demo_hash_int: {
        Args: { modulo: number; txt: string };
        Returns: number;
      };
      populate_geometry_columns:
        | { Args: { tbl_oid: unknown; use_typmod?: boolean }; Returns: number }
        | { Args: { use_typmod?: boolean }; Returns: string };
      postgis_constraint_dims: {
        Args: { geomcolumn: string; geomschema: string; geomtable: string };
        Returns: number;
      };
      postgis_constraint_srid: {
        Args: { geomcolumn: string; geomschema: string; geomtable: string };
        Returns: number;
      };
      postgis_constraint_type: {
        Args: { geomcolumn: string; geomschema: string; geomtable: string };
        Returns: string;
      };
      postgis_extensions_upgrade: { Args: never; Returns: string };
      postgis_full_version: { Args: never; Returns: string };
      postgis_geos_version: { Args: never; Returns: string };
      postgis_lib_build_date: { Args: never; Returns: string };
      postgis_lib_revision: { Args: never; Returns: string };
      postgis_lib_version: { Args: never; Returns: string };
      postgis_libjson_version: { Args: never; Returns: string };
      postgis_liblwgeom_version: { Args: never; Returns: string };
      postgis_libprotobuf_version: { Args: never; Returns: string };
      postgis_libxml_version: { Args: never; Returns: string };
      postgis_proj_version: { Args: never; Returns: string };
      postgis_scripts_build_date: { Args: never; Returns: string };
      postgis_scripts_installed: { Args: never; Returns: string };
      postgis_scripts_released: { Args: never; Returns: string };
      postgis_svn_version: { Args: never; Returns: string };
      postgis_type_name: {
        Args: {
          coord_dimension: number;
          geomname: string;
          use_new_name?: boolean;
        };
        Returns: string;
      };
      postgis_version: { Args: never; Returns: string };
      postgis_wagyu_version: { Args: never; Returns: string };
      preview_guest_list_matches: {
        Args: { p_comedian_id: string; p_emails: string[] };
        Returns: Json;
      };
      prune_southpaw_audience: {
        Args: { p_job_id: string };
        Returns: undefined;
      };
      prune_southpaw_external_audience: {
        Args: { p_job_id: string };
        Returns: undefined;
      };
      prune_southpaw_external_venue_audience: {
        Args: { p_job_id: string };
        Returns: undefined;
      };
      prune_southpaw_venue_audience: {
        Args: { p_job_id: string };
        Returns: undefined;
      };
      prune_stale_recs: {
        Args: { _run_token: string; _shard_id: number; _table_name: string };
        Returns: undefined;
      };
      replace_revenue_attribution_manual_months: {
        Args: { p_account_id: string; p_actor: string; p_rows: Json };
        Returns: undefined;
      };
      resolve_follower_recipients_with_channels: {
        Args: { p_comedian_id: string };
        Returns: {
          user_id: string;
          wants_email: boolean;
          wants_push: boolean;
        }[];
      };
      resolve_geo_filtered_follower_recipients_with_channels: {
        Args: { p_comedian_id: string; p_geohash_cells: string[] };
        Returns: {
          user_id: string;
          wants_email: boolean;
          wants_push: boolean;
        }[];
      };
      resolve_geo_filtered_super_admin_recipients_with_channels: {
        Args: { p_comedian_id: string; p_geohash_cells: string[] };
        Returns: {
          user_id: string;
          wants_email: boolean;
          wants_push: boolean;
        }[];
      };
      resolve_paid_subscriber_recipients_with_channels: {
        Args: { p_comedian_id: string };
        Returns: {
          user_id: string;
          wants_email: boolean;
          wants_push: boolean;
        }[];
      };
      resolve_subscription_follower_recipients_with_channels: {
        Args: { p_comedian_id: string };
        Returns: {
          user_id: string;
          wants_email: boolean;
          wants_push: boolean;
        }[];
      };
      resolve_subscription_super_admin_recipients_with_channels: {
        Args: { p_comedian_id: string };
        Returns: {
          user_id: string;
          wants_email: boolean;
          wants_push: boolean;
        }[];
      };
      resolve_super_admin_recipients_with_channels: {
        Args: { p_comedian_id: string };
        Returns: {
          user_id: string;
          wants_email: boolean;
          wants_push: boolean;
        }[];
      };
      revoke_auth_session: {
        Args: { p_session_id: string };
        Returns: undefined;
      };
      search_comedians_fts_prefix: {
        Args: { prefix: string };
        Returns: {
          about: string | null;
          about_img: string | null;
          created_at: string | null;
          display_name: string;
          display_name_short: string;
          featured_special_id: string | null;
          fts_token: unknown;
          fts_token_nostop: unknown;
          id: string;
          is_external_preview: boolean | null;
          is_punchup_verified: boolean;
          merch_store_link: string | null;
          published_at: string | null;
          shopify_merch_collection_id: string | null;
          signature_img: string | null;
          slug: string;
          tip_jar_link: string | null;
        }[];
        SetofOptions: {
          from: "*";
          to: "comedians";
          isOneToOne: false;
          isSetofReturn: true;
        };
      };
      search_specials_fts_prefix: {
        Args: { prefix: string };
        Returns: {
          captions_bucket_path: string | null;
          comedian_id: string;
          created_at: string | null;
          description: string | null;
          fts_token: unknown;
          fts_token_nostop: unknown;
          id: string;
          is_audio: boolean;
          is_live: boolean;
          is_vertical: boolean | null;
          livestream: string | null;
          poster_img_with_titles: string | null;
          preview_img: string | null;
          preview_punchup_video_id: string | null;
          preview_video_id: string | null;
          price: number;
          product_id: string | null;
          published_at: string | null;
          punchup_video_id: string | null;
          required_subscription_tier: number | null;
          requires_purchase: boolean;
          requires_subscription: boolean;
          runtime: number;
          runtime_seconds: number | null;
          slug: string;
          small_preview_img: string | null;
          subscription_product_id: string | null;
          title: string;
          video_id: string | null;
        }[];
        SetofOptions: {
          from: "*";
          to: "specials";
          isOneToOne: false;
          isSetofReturn: true;
        };
      };
      search_support_cases: {
        Args: {
          p_category?: string;
          p_limit?: number;
          p_query: string;
          p_stale_after_days?: number;
          p_statuses?: string[];
        };
        Returns: {
          case_id: string;
          match_kind: string;
          rank: number;
          verification_status: string;
        }[];
      };
      show_limit: { Args: never; Returns: number };
      show_trgm: { Args: { "": string }; Returns: string[] };
      shows_near_zipcode: {
        Args: { range_km: number; zipcode: number };
        Returns: {
          comedian_id: string | null;
          created_at: string;
          datetime: string | null;
          deleted_at: string | null;
          flags: Database["public"]["Enums"]["ticket_flags"][] | null;
          id: string;
          is_sold_out: boolean | null;
          location: string;
          metadata_text: string | null;
          poster_img: string | null;
          presale_code: string | null;
          published_at: string | null;
          ticket_link: string;
          title: string | null;
          tixologi_event_id: string | null;
          venue: string;
          venue_id: string;
          vip_ticket_link: string | null;
        }[];
        SetofOptions: {
          from: "*";
          to: "comedian_shows";
          isOneToOne: false;
          isSetofReturn: true;
        };
      };
      soundex: { Args: { "": string }; Returns: string };
      st_3dclosestpoint: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_3ddistance: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: number;
      };
      st_3dintersects: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      st_3dlongestline: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_3dmakebox: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_3dmaxdistance: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: number;
      };
      st_3dshortestline: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_addpoint: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_angle:
        | { Args: { line1: unknown; line2: unknown }; Returns: number }
        | {
            Args: { pt1: unknown; pt2: unknown; pt3: unknown; pt4?: unknown };
            Returns: number;
          };
      st_area:
        | { Args: { geog: unknown; use_spheroid?: boolean }; Returns: number }
        | { Args: { "": string }; Returns: number };
      st_asencodedpolyline: {
        Args: { geom: unknown; nprecision?: number };
        Returns: string;
      };
      st_asewkt: { Args: { "": string }; Returns: string };
      st_asgeojson:
        | {
            Args: {
              geog: unknown;
              maxdecimaldigits?: number;
              options?: number;
            };
            Returns: string;
          }
        | {
            Args: {
              geom: unknown;
              maxdecimaldigits?: number;
              options?: number;
            };
            Returns: string;
          }
        | {
            Args: {
              geom_column?: string;
              maxdecimaldigits?: number;
              pretty_bool?: boolean;
              r: Record<string, unknown>;
            };
            Returns: string;
          }
        | { Args: { "": string }; Returns: string };
      st_asgml:
        | {
            Args: {
              geog: unknown;
              id?: string;
              maxdecimaldigits?: number;
              nprefix?: string;
              options?: number;
            };
            Returns: string;
          }
        | {
            Args: {
              geom: unknown;
              maxdecimaldigits?: number;
              options?: number;
            };
            Returns: string;
          }
        | { Args: { "": string }; Returns: string }
        | {
            Args: {
              geog: unknown;
              id?: string;
              maxdecimaldigits?: number;
              nprefix?: string;
              options?: number;
              version: number;
            };
            Returns: string;
          }
        | {
            Args: {
              geom: unknown;
              id?: string;
              maxdecimaldigits?: number;
              nprefix?: string;
              options?: number;
              version: number;
            };
            Returns: string;
          };
      st_askml:
        | {
            Args: {
              geog: unknown;
              maxdecimaldigits?: number;
              nprefix?: string;
            };
            Returns: string;
          }
        | {
            Args: {
              geom: unknown;
              maxdecimaldigits?: number;
              nprefix?: string;
            };
            Returns: string;
          }
        | { Args: { "": string }; Returns: string };
      st_aslatlontext: {
        Args: { geom: unknown; tmpl?: string };
        Returns: string;
      };
      st_asmarc21: {
        Args: { format?: string; geom: unknown };
        Returns: string;
      };
      st_asmvtgeom: {
        Args: {
          bounds: unknown;
          buffer?: number;
          clip_geom?: boolean;
          extent?: number;
          geom: unknown;
        };
        Returns: unknown;
      };
      st_assvg:
        | {
            Args: { geog: unknown; maxdecimaldigits?: number; rel?: number };
            Returns: string;
          }
        | {
            Args: { geom: unknown; maxdecimaldigits?: number; rel?: number };
            Returns: string;
          }
        | { Args: { "": string }; Returns: string };
      st_astext: { Args: { "": string }; Returns: string };
      st_astwkb:
        | {
            Args: {
              geom: unknown;
              prec?: number;
              prec_m?: number;
              prec_z?: number;
              with_boxes?: boolean;
              with_sizes?: boolean;
            };
            Returns: string;
          }
        | {
            Args: {
              geom: unknown[];
              ids: number[];
              prec?: number;
              prec_m?: number;
              prec_z?: number;
              with_boxes?: boolean;
              with_sizes?: boolean;
            };
            Returns: string;
          };
      st_asx3d: {
        Args: { geom: unknown; maxdecimaldigits?: number; options?: number };
        Returns: string;
      };
      st_azimuth:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: number }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: number };
      st_boundingdiagonal: {
        Args: { fits?: boolean; geom: unknown };
        Returns: unknown;
      };
      st_buffer:
        | {
            Args: { geom: unknown; options?: string; radius: number };
            Returns: unknown;
          }
        | {
            Args: { geom: unknown; quadsegs: number; radius: number };
            Returns: unknown;
          };
      st_centroid: { Args: { "": string }; Returns: unknown };
      st_clipbybox2d: {
        Args: { box: unknown; geom: unknown };
        Returns: unknown;
      };
      st_closestpoint: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_collect: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_concavehull: {
        Args: {
          param_allow_holes?: boolean;
          param_geom: unknown;
          param_pctconvex: number;
        };
        Returns: unknown;
      };
      st_contains: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      st_containsproperly: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      st_coorddim: { Args: { geometry: unknown }; Returns: number };
      st_coveredby:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: boolean }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: boolean };
      st_covers:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: boolean }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: boolean };
      st_crosses: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      st_curvetoline: {
        Args: { flags?: number; geom: unknown; tol?: number; toltype?: number };
        Returns: unknown;
      };
      st_delaunaytriangles: {
        Args: { flags?: number; g1: unknown; tolerance?: number };
        Returns: unknown;
      };
      st_difference: {
        Args: { geom1: unknown; geom2: unknown; gridsize?: number };
        Returns: unknown;
      };
      st_disjoint: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      st_distance:
        | {
            Args: { geog1: unknown; geog2: unknown; use_spheroid?: boolean };
            Returns: number;
          }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: number };
      st_distancesphere:
        | { Args: { geom1: unknown; geom2: unknown }; Returns: number }
        | {
            Args: { geom1: unknown; geom2: unknown; radius: number };
            Returns: number;
          };
      st_distancespheroid: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: number;
      };
      st_dwithin: {
        Args: {
          geog1: unknown;
          geog2: unknown;
          tolerance: number;
          use_spheroid?: boolean;
        };
        Returns: boolean;
      };
      st_equals: { Args: { geom1: unknown; geom2: unknown }; Returns: boolean };
      st_expand:
        | { Args: { box: unknown; dx: number; dy: number }; Returns: unknown }
        | {
            Args: { box: unknown; dx: number; dy: number; dz?: number };
            Returns: unknown;
          }
        | {
            Args: {
              dm?: number;
              dx: number;
              dy: number;
              dz?: number;
              geom: unknown;
            };
            Returns: unknown;
          };
      st_force3d: {
        Args: { geom: unknown; zvalue?: number };
        Returns: unknown;
      };
      st_force3dm: {
        Args: { geom: unknown; mvalue?: number };
        Returns: unknown;
      };
      st_force3dz: {
        Args: { geom: unknown; zvalue?: number };
        Returns: unknown;
      };
      st_force4d: {
        Args: { geom: unknown; mvalue?: number; zvalue?: number };
        Returns: unknown;
      };
      st_generatepoints:
        | { Args: { area: unknown; npoints: number }; Returns: unknown }
        | {
            Args: { area: unknown; npoints: number; seed: number };
            Returns: unknown;
          };
      st_geogfromtext: { Args: { "": string }; Returns: unknown };
      st_geographyfromtext: { Args: { "": string }; Returns: unknown };
      st_geohash:
        | { Args: { geog: unknown; maxchars?: number }; Returns: string }
        | { Args: { geom: unknown; maxchars?: number }; Returns: string };
      st_geomcollfromtext: { Args: { "": string }; Returns: unknown };
      st_geometricmedian: {
        Args: {
          fail_if_not_converged?: boolean;
          g: unknown;
          max_iter?: number;
          tolerance?: number;
        };
        Returns: unknown;
      };
      st_geometryfromtext: { Args: { "": string }; Returns: unknown };
      st_geomfromewkt: { Args: { "": string }; Returns: unknown };
      st_geomfromgeojson:
        | { Args: { "": Json }; Returns: unknown }
        | { Args: { "": Json }; Returns: unknown }
        | { Args: { "": string }; Returns: unknown };
      st_geomfromgml: { Args: { "": string }; Returns: unknown };
      st_geomfromkml: { Args: { "": string }; Returns: unknown };
      st_geomfrommarc21: { Args: { marc21xml: string }; Returns: unknown };
      st_geomfromtext: { Args: { "": string }; Returns: unknown };
      st_gmltosql: { Args: { "": string }; Returns: unknown };
      st_hasarc: { Args: { geometry: unknown }; Returns: boolean };
      st_hausdorffdistance: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: number;
      };
      st_hexagon: {
        Args: {
          cell_i: number;
          cell_j: number;
          origin?: unknown;
          size: number;
        };
        Returns: unknown;
      };
      st_hexagongrid: {
        Args: { bounds: unknown; size: number };
        Returns: Record<string, unknown>[];
      };
      st_interpolatepoint: {
        Args: { line: unknown; point: unknown };
        Returns: number;
      };
      st_intersection: {
        Args: { geom1: unknown; geom2: unknown; gridsize?: number };
        Returns: unknown;
      };
      st_intersects:
        | { Args: { geog1: unknown; geog2: unknown }; Returns: boolean }
        | { Args: { geom1: unknown; geom2: unknown }; Returns: boolean };
      st_isvaliddetail: {
        Args: { flags?: number; geom: unknown };
        Returns: Database["public"]["CompositeTypes"]["valid_detail"];
        SetofOptions: {
          from: "*";
          to: "valid_detail";
          isOneToOne: true;
          isSetofReturn: false;
        };
      };
      st_length:
        | { Args: { geog: unknown; use_spheroid?: boolean }; Returns: number }
        | { Args: { "": string }; Returns: number };
      st_letters: { Args: { font?: Json; letters: string }; Returns: unknown };
      st_linecrossingdirection: {
        Args: { line1: unknown; line2: unknown };
        Returns: number;
      };
      st_linefromencodedpolyline: {
        Args: { nprecision?: number; txtin: string };
        Returns: unknown;
      };
      st_linefromtext: { Args: { "": string }; Returns: unknown };
      st_linelocatepoint: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: number;
      };
      st_linetocurve: { Args: { geometry: unknown }; Returns: unknown };
      st_locatealong: {
        Args: { geometry: unknown; leftrightoffset?: number; measure: number };
        Returns: unknown;
      };
      st_locatebetween: {
        Args: {
          frommeasure: number;
          geometry: unknown;
          leftrightoffset?: number;
          tomeasure: number;
        };
        Returns: unknown;
      };
      st_locatebetweenelevations: {
        Args: { fromelevation: number; geometry: unknown; toelevation: number };
        Returns: unknown;
      };
      st_longestline: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_makebox2d: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_makeline: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_makevalid: {
        Args: { geom: unknown; params: string };
        Returns: unknown;
      };
      st_maxdistance: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: number;
      };
      st_minimumboundingcircle: {
        Args: { inputgeom: unknown; segs_per_quarter?: number };
        Returns: unknown;
      };
      st_mlinefromtext: { Args: { "": string }; Returns: unknown };
      st_mpointfromtext: { Args: { "": string }; Returns: unknown };
      st_mpolyfromtext: { Args: { "": string }; Returns: unknown };
      st_multilinestringfromtext: { Args: { "": string }; Returns: unknown };
      st_multipointfromtext: { Args: { "": string }; Returns: unknown };
      st_multipolygonfromtext: { Args: { "": string }; Returns: unknown };
      st_node: { Args: { g: unknown }; Returns: unknown };
      st_normalize: { Args: { geom: unknown }; Returns: unknown };
      st_offsetcurve: {
        Args: { distance: number; line: unknown; params?: string };
        Returns: unknown;
      };
      st_orderingequals: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      st_overlaps: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      st_perimeter: {
        Args: { geog: unknown; use_spheroid?: boolean };
        Returns: number;
      };
      st_pointfromtext: { Args: { "": string }; Returns: unknown };
      st_pointm: {
        Args: {
          mcoordinate: number;
          srid?: number;
          xcoordinate: number;
          ycoordinate: number;
        };
        Returns: unknown;
      };
      st_pointz: {
        Args: {
          srid?: number;
          xcoordinate: number;
          ycoordinate: number;
          zcoordinate: number;
        };
        Returns: unknown;
      };
      st_pointzm: {
        Args: {
          mcoordinate: number;
          srid?: number;
          xcoordinate: number;
          ycoordinate: number;
          zcoordinate: number;
        };
        Returns: unknown;
      };
      st_polyfromtext: { Args: { "": string }; Returns: unknown };
      st_polygonfromtext: { Args: { "": string }; Returns: unknown };
      st_project: {
        Args: { azimuth: number; distance: number; geog: unknown };
        Returns: unknown;
      };
      st_quantizecoordinates: {
        Args: {
          g: unknown;
          prec_m?: number;
          prec_x: number;
          prec_y?: number;
          prec_z?: number;
        };
        Returns: unknown;
      };
      st_reduceprecision: {
        Args: { geom: unknown; gridsize: number };
        Returns: unknown;
      };
      st_relate: { Args: { geom1: unknown; geom2: unknown }; Returns: string };
      st_removerepeatedpoints: {
        Args: { geom: unknown; tolerance?: number };
        Returns: unknown;
      };
      st_segmentize: {
        Args: { geog: unknown; max_segment_length: number };
        Returns: unknown;
      };
      st_setsrid:
        | { Args: { geog: unknown; srid: number }; Returns: unknown }
        | { Args: { geom: unknown; srid: number }; Returns: unknown };
      st_sharedpaths: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_shortestline: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_simplifypolygonhull: {
        Args: { geom: unknown; is_outer?: boolean; vertex_fraction: number };
        Returns: unknown;
      };
      st_split: { Args: { geom1: unknown; geom2: unknown }; Returns: unknown };
      st_square: {
        Args: {
          cell_i: number;
          cell_j: number;
          origin?: unknown;
          size: number;
        };
        Returns: unknown;
      };
      st_squaregrid: {
        Args: { bounds: unknown; size: number };
        Returns: Record<string, unknown>[];
      };
      st_srid:
        | { Args: { geog: unknown }; Returns: number }
        | { Args: { geom: unknown }; Returns: number };
      st_subdivide: {
        Args: { geom: unknown; gridsize?: number; maxvertices?: number };
        Returns: unknown[];
      };
      st_swapordinates: {
        Args: { geom: unknown; ords: unknown };
        Returns: unknown;
      };
      st_symdifference: {
        Args: { geom1: unknown; geom2: unknown; gridsize?: number };
        Returns: unknown;
      };
      st_symmetricdifference: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: unknown;
      };
      st_tileenvelope: {
        Args: {
          bounds?: unknown;
          margin?: number;
          x: number;
          y: number;
          zoom: number;
        };
        Returns: unknown;
      };
      st_touches: {
        Args: { geom1: unknown; geom2: unknown };
        Returns: boolean;
      };
      st_transform:
        | {
            Args: { from_proj: string; geom: unknown; to_proj: string };
            Returns: unknown;
          }
        | {
            Args: { from_proj: string; geom: unknown; to_srid: number };
            Returns: unknown;
          }
        | { Args: { geom: unknown; to_proj: string }; Returns: unknown };
      st_triangulatepolygon: { Args: { g1: unknown }; Returns: unknown };
      st_union:
        | { Args: { geom1: unknown; geom2: unknown }; Returns: unknown }
        | {
            Args: { geom1: unknown; geom2: unknown; gridsize: number };
            Returns: unknown;
          };
      st_voronoilines: {
        Args: { extend_to?: unknown; g1: unknown; tolerance?: number };
        Returns: unknown;
      };
      st_voronoipolygons: {
        Args: { extend_to?: unknown; g1: unknown; tolerance?: number };
        Returns: unknown;
      };
      st_within: { Args: { geom1: unknown; geom2: unknown }; Returns: boolean };
      st_wkbtosql: { Args: { wkb: string }; Returns: unknown };
      st_wkttosql: { Args: { "": string }; Returns: unknown };
      st_wrapx: {
        Args: { geom: unknown; move: number; wrap: number };
        Returns: unknown;
      };
      swap_comedian_fans_near_venues: { Args: never; Returns: undefined };
      swap_user_comedian_recs: { Args: never; Returns: undefined };
      swap_user_specials_recs: { Args: never; Returns: undefined };
      sync_comedian_similarity_network: { Args: never; Returns: undefined };
      sync_ticket_als_show_factors: { Args: never; Returns: undefined };
      sync_ticket_als_user_factors: { Args: never; Returns: undefined };
      sync_user_comedian_recommendations: { Args: never; Returns: undefined };
      sync_user_specials_recommendations: { Args: never; Returns: undefined };
      test_update_notification_preference: {
        Args: {
          comedian_id: string;
          preference: Database["public"]["Enums"]["notification_preference"];
        };
        Returns: boolean;
      };
      text_soundex: { Args: { "": string }; Returns: string };
      try_publish_special_on_create: {
        Args: { p_special_id: string };
        Returns: boolean;
      };
      u_count: { Args: never; Returns: number };
      unlockrows: { Args: { "": string }; Returns: number };
      unsubscribe_contact_from_comedian_emails: {
        Args: { p_comedian_id: string; p_contact_id: string };
        Returns: undefined;
      };
      unsubscribe_contact_from_venue_page_emails: {
        Args: { p_contact_id: string; p_venue_page_id: string };
        Returns: undefined;
      };
      unsubscribe_user_from_comedian_emails: {
        Args: { comedian_id: string; p_user_id: string };
        Returns: undefined;
      };
      unsubscribe_user_from_venue_page_emails: {
        Args: { p_user_id: string; p_venue_page_id: string };
        Returns: undefined;
      };
      update_comedian_clicked_play_agg: { Args: never; Returns: undefined };
      update_comedian_clicked_play_daily: { Args: never; Returns: undefined };
      update_comedian_clicked_play_summary: { Args: never; Returns: undefined };
      update_comedian_contacts_near_venues: { Args: never; Returns: undefined };
      update_comedian_contacts_near_venues_v2: {
        Args: never;
        Returns: undefined;
      };
      update_comedian_dashboard_benchmarks: { Args: never; Returns: undefined };
      update_comedian_dashboard_benchmarks_v2: {
        Args: never;
        Returns: undefined;
      };
      update_comedian_fans_near_venues: { Args: never; Returns: undefined };
      update_comedian_fans_near_venues_summary: {
        Args: never;
        Returns: undefined;
      };
      update_comedian_fans_near_venues_test: {
        Args: never;
        Returns: undefined;
      };
      update_comedian_fans_near_venues_v2: { Args: never; Returns: undefined };
      update_comedian_followed_agg: { Args: never; Returns: undefined };
      update_comedian_followers_agg: { Args: never; Returns: undefined };
      update_comedian_followers_daily: { Args: never; Returns: undefined };
      update_comedian_followers_details: { Args: never; Returns: undefined };
      update_comedian_followers_from_snowflake: {
        Args: never;
        Returns: undefined;
      };
      update_comedian_followers_summary: { Args: never; Returns: undefined };
      update_comedian_momentum_scores_from_snowflake: {
        Args: never;
        Returns: undefined;
      };
      update_comedian_ticket_clicks_agg: { Args: never; Returns: undefined };
      update_comedian_ticket_clicks_daily: { Args: never; Returns: undefined };
      update_comedian_ticket_clicks_summary: {
        Args: never;
        Returns: undefined;
      };
      update_comedian_tickets_agg: { Args: never; Returns: undefined };
      update_event_sales_daily: { Args: never; Returns: undefined };
      update_event_sales_summary: { Args: never; Returns: undefined };
      update_show_badges_from_snowflake: { Args: never; Returns: undefined };
      update_trending_specials_from_snowflake: {
        Args: never;
        Returns: undefined;
      };
      update_unsubtokens: {
        Args: { comedian_id: string; updates: Json[] };
        Returns: undefined;
      };
      update_venue_analytics_from_snowflake: {
        Args: never;
        Returns: undefined;
      };
      update_venue_followers_daily: {
        Args: { from_date?: string; to_date?: string };
        Returns: undefined;
      };
      update_venue_followers_summary: { Args: never; Returns: undefined };
      update_venue_sales_daily: { Args: never; Returns: undefined };
      update_venue_sales_summary: { Args: never; Returns: undefined };
      update_venue_ticket_clicks_daily: {
        Args: { from_date?: string; to_date?: string };
        Returns: undefined;
      };
      update_venue_ticket_clicks_summary: { Args: never; Returns: undefined };
      update_weekly_audience_table: {
        Args: never;
        Returns: {
          batchname: string;
          cnt: number;
        }[];
      };
      update_weekly_email_audience_from_snowflake: {
        Args: never;
        Returns: undefined;
      };
      updategeometrysrid: {
        Args: {
          catalogn_name: string;
          column_name: string;
          new_srid_in: number;
          schema_name: string;
          table_name: string;
        };
        Returns: string;
      };
      upsert_contacts_and_lists_batch: {
        Args: { contact_data: Json; contact_list_id: string };
        Returns: Json;
      };
      upsert_push_job: {
        Args: {
          in_comedian_id: string;
          in_message_id: string;
          in_show_date: string;
          in_show_id: string;
          in_venue_id: string;
        };
        Returns: {
          comedian_id: string | null;
          created_at: string | null;
          error_message: string | null;
          id: string;
          job_type: string | null;
          metadata: Json | null;
          processed_at: string | null;
          show_ids: string[];
          status: Database["public"]["Enums"]["push_notification_job"] | null;
          updated_at: string | null;
          venue_id: string | null;
        };
        SetofOptions: {
          from: "*";
          to: "push_notification_jobs";
          isOneToOne: true;
          isSetofReturn: false;
        };
      };
      venue_name_search: {
        Args: {
          input_comedian_id: string;
          venue_cities: string[];
          venue_names: string[];
        };
        Returns: {
          city: string;
          datetime: string;
          id: string;
          location: string;
          matched_venue_name: string;
          venue: string;
          venue_id: string;
        }[];
      };
      venues_near_coordinates: {
        Args: { latitude: number; longitude: number; range_km: number };
        Returns: {
          address: string | null;
          capacity: number | null;
          city: string | null;
          country: string | null;
          country_code: string | null;
          created_at: string;
          created_by: string | null;
          id: string;
          is_deleted: boolean;
          is_pending: boolean;
          location: Json | null;
          location_geo: unknown;
          name: string | null;
          postal_code: string | null;
          state: string | null;
        }[];
        SetofOptions: {
          from: "*";
          to: "venues";
          isOneToOne: false;
          isSetofReturn: true;
        };
      };
      venues_near_postal_code: {
        Args: { country_code: string; postal_code: string; range_km: number };
        Returns: {
          address: string | null;
          capacity: number | null;
          city: string | null;
          country: string | null;
          country_code: string | null;
          created_at: string;
          created_by: string | null;
          id: string;
          is_deleted: boolean;
          is_pending: boolean;
          location: Json | null;
          location_geo: unknown;
          name: string | null;
          postal_code: string | null;
          state: string | null;
        }[];
        SetofOptions: {
          from: "*";
          to: "venues";
          isOneToOne: false;
          isSetofReturn: true;
        };
      };
      venues_near_zipcode:
        | {
            Args: never;
            Returns: {
              address: string | null;
              capacity: number | null;
              city: string | null;
              country: string | null;
              country_code: string | null;
              created_at: string;
              created_by: string | null;
              id: string;
              is_deleted: boolean;
              is_pending: boolean;
              location: Json | null;
              location_geo: unknown;
              name: string | null;
              postal_code: string | null;
              state: string | null;
            }[];
            SetofOptions: {
              from: "*";
              to: "venues";
              isOneToOne: false;
              isSetofReturn: true;
            };
          }
        | {
            Args: { zipcode: number };
            Returns: {
              address: string | null;
              capacity: number | null;
              city: string | null;
              country: string | null;
              country_code: string | null;
              created_at: string;
              created_by: string | null;
              id: string;
              is_deleted: boolean;
              is_pending: boolean;
              location: Json | null;
              location_geo: unknown;
              name: string | null;
              postal_code: string | null;
              state: string | null;
            }[];
            SetofOptions: {
              from: "*";
              to: "venues";
              isOneToOne: false;
              isSetofReturn: true;
            };
          }
        | {
            Args: { range_km: number; zipcode: number };
            Returns: {
              address: string | null;
              capacity: number | null;
              city: string | null;
              country: string | null;
              country_code: string | null;
              created_at: string;
              created_by: string | null;
              id: string;
              is_deleted: boolean;
              is_pending: boolean;
              location: Json | null;
              location_geo: unknown;
              name: string | null;
              postal_code: string | null;
              state: string | null;
            }[];
            SetofOptions: {
              from: "*";
              to: "venues";
              isOneToOne: false;
              isSetofReturn: true;
            };
          };
    };
    Enums: {
      ad_campaign_type: "feed";
      batchname:
        | "7am"
        | "11am"
        | "8am"
        | "9am"
        | "10am"
        | "12pm"
        | "1pm"
        | "2pm";
      collection_kind: "collection" | "podcast";
      contact_lists_status: "created" | "uploading" | "processing" | "failed";
      content_tag_status: "pending" | "accepted" | "rejected";
      credit_pool_type: "free" | "purchased" | "automations";
      credit_reference_type: "Southpaw" | "Stripe" | "Cron";
      credit_transaction_type:
        | "free_monthly_deposit"
        | "one_time_purchase"
        | "subscription_renewal"
        | "message_send_debit"
        | "cancellation_refund"
        | "admin_adjustment"
        | "subscription_canceled"
        | "subscription_upgrade"
        | "subscription_downgrade";
      email_frequency: "weekly" | "biweekly" | "monthly" | "disabled";
      gift_card_status: "active" | "redeemed" | "expired" | "disabled";
      gift_card_transaction_type:
        | "purchase"
        | "redemption"
        | "refund"
        | "adjustment";
      livestream_state: "not_started" | "waiting" | "live" | "ended";
      livestream_vendor: "mux";
      moderation_level: "off" | "low" | "medium" | "high";
      newsletter_issue_status:
        | "draft"
        | "scheduled"
        | "dispatching"
        | "dispatched"
        | "sent"
        | "failed"
        | "canceled";
      notification_preference: "sms-only" | "email-only" | "none" | "all";
      notification_type:
        | "weekly_email"
        | "new_special"
        | "new_comedian"
        | "new_tour_date";
      page_types:
        | "comedian"
        | "venue_page"
        | "musician"
        | "podcast"
        | "film"
        | "production_company"
        | "journalist"
        | "magician"
        | "medium";
      payment_provider: "stripe" | "apple-iap";
      payment_status:
        | "incomplete"
        | "success"
        | "processing"
        | "requires_action"
        | "refunded"
        | "failed";
      product_category: "video" | "subscription" | "collection";
      push_notification_job: "pending" | "processing" | "processed" | "failed";
      show_creator_entity_type: "comedian" | "venue_page" | "internal";
      social_media_type:
        | "Twitter"
        | "Instagram"
        | "Facebook"
        | "Youtube"
        | "TikTok"
        | "LinkedIn"
        | "Podcast"
        | "Custom";
      subscription_status:
        | "incomplete"
        | "active"
        | "canceled"
        | "past_due"
        | "unpaid";
      ticket_flags: "LOW_TICKETS";
      timezone:
        | "America/New_York"
        | "America/Los_Angeles"
        | "America/Chicago"
        | "America/Denver"
        | "Europe/Andorra"
        | "Europe/Vienna"
        | "Europe/Brussels"
        | "Europe/Sofia"
        | "Europe/Minsk"
        | "Europe/Zurich"
        | "Europe/Prague"
        | "Europe/Berlin"
        | "Europe/Copenhagen"
        | "Europe/Tallinn"
        | "Europe/Madrid"
        | "Europe/Helsinki"
        | "Europe/Paris"
        | "Europe/London"
        | "Europe/Gibraltar"
        | "Europe/Athens"
        | "Europe/Budapest"
        | "Europe/Dublin"
        | "Europe/Rome"
        | "Europe/Vilnius"
        | "Europe/Luxembourg"
        | "Europe/Riga"
        | "Europe/Monaco"
        | "Europe/Chisinau"
        | "Europe/Malta"
        | "Europe/Amsterdam"
        | "Europe/Oslo"
        | "Europe/Warsaw"
        | "Europe/Lisbon"
        | "Europe/Bucharest"
        | "Europe/Belgrade"
        | "Europe/Kaliningrad"
        | "Europe/Moscow"
        | "Europe/Simferopol"
        | "Europe/Kirov"
        | "Europe/Volgograd"
        | "Europe/Astrakhan"
        | "Europe/Saratov"
        | "Europe/Ulyanovsk"
        | "Europe/Samara"
        | "Europe/Stockholm"
        | "Europe/Istanbul"
        | "Europe/Kiev"
        | "Europe/Uzhgorod"
        | "Europe/Zaporozhye"
        | "Europe/Tirane"
        | "Asia/Dubai"
        | "Asia/Kabul"
        | "Asia/Yerevan"
        | "Asia/Baku"
        | "Asia/Dhaka"
        | "Asia/Brunei"
        | "Asia/Thimphu"
        | "Asia/Shanghai"
        | "Asia/Urumqi"
        | "Asia/Nicosia"
        | "Asia/Famagusta"
        | "Asia/Tbilisi"
        | "Asia/Hong_Kong"
        | "Asia/Jakarta"
        | "Asia/Pontianak"
        | "Asia/Makassar"
        | "Asia/Jayapura"
        | "Asia/Jerusalem"
        | "Asia/Kolkata"
        | "Asia/Baghdad"
        | "Asia/Tehran"
        | "Asia/Amman"
        | "Asia/Tokyo"
        | "Asia/Bishkek"
        | "Asia/Pyongyang"
        | "Asia/Seoul"
        | "Asia/Almaty"
        | "Asia/Qyzylorda"
        | "Asia/Qostanay"
        | "Asia/Aqtobe"
        | "Asia/Aqtau"
        | "Asia/Atyrau"
        | "Asia/Oral"
        | "Asia/Beirut"
        | "Asia/Colombo"
        | "Asia/Yangon"
        | "Asia/Ulaanbaatar"
        | "Asia/Hovd"
        | "Asia/Choibalsan"
        | "Asia/Macau"
        | "Asia/Kuala_Lumpur"
        | "Asia/Kuching"
        | "Asia/Kathmandu"
        | "Asia/Manila"
        | "Asia/Karachi"
        | "Asia/Gaza"
        | "Asia/Hebron"
        | "Asia/Qatar"
        | "Asia/Yekaterinburg"
        | "Asia/Omsk"
        | "Asia/Novosibirsk"
        | "Asia/Barnaul"
        | "Asia/Tomsk"
        | "Asia/Novokuznetsk"
        | "Asia/Krasnoyarsk"
        | "Asia/Irkutsk"
        | "Asia/Chita"
        | "Asia/Yakutsk"
        | "Asia/Khandyga"
        | "Asia/Vladivostok"
        | "Asia/Ust-Nera"
        | "Asia/Magadan"
        | "Asia/Sakhalin"
        | "Asia/Srednekolymsk"
        | "Asia/Kamchatka"
        | "Asia/Anadyr"
        | "Asia/Riyadh"
        | "Asia/Singapore"
        | "Asia/Bangkok"
        | "Asia/Dushanbe"
        | "Asia/Dili"
        | "Asia/Ashgabat"
        | "Asia/Taipei"
        | "Asia/Samarkand"
        | "Asia/Tashkent"
        | "Asia/Ho_Chi_Minh"
        | "America/Detroit"
        | "America/Kentucky/Louisville"
        | "America/Kentucky/Monticello"
        | "America/Indiana/Indianapolis"
        | "America/Indiana/Vincennes"
        | "America/Indiana/Winamac"
        | "America/Indiana/Marengo"
        | "America/Indiana/Petersburg"
        | "America/Indiana/Vevay"
        | "America/Indiana/Tell_City"
        | "America/Indiana/Knox"
        | "America/Menominee"
        | "America/North_Dakota/Center"
        | "America/North_Dakota/New_Salem"
        | "America/North_Dakota/Beulah"
        | "America/Boise"
        | "America/Phoenix"
        | "America/Anchorage"
        | "America/Juneau"
        | "America/Sitka"
        | "America/Metlakatla"
        | "America/Yakutat"
        | "America/Nome"
        | "America/Adak"
        | "America/St_Johns"
        | "America/Halifax"
        | "America/Glace_Bay"
        | "America/Moncton"
        | "America/Goose_Bay"
        | "America/Blanc-Sablon"
        | "America/Toronto"
        | "America/Nipigon"
        | "America/Thunder_Bay"
        | "America/Iqaluit"
        | "America/Pangnirtung"
        | "America/Atikokan"
        | "America/Winnipeg"
        | "America/Rainy_River"
        | "America/Resolute"
        | "America/Rankin_Inlet"
        | "America/Regina"
        | "America/Swift_Current"
        | "America/Edmonton"
        | "America/Cambridge_Bay"
        | "America/Yellowknife"
        | "America/Inuvik"
        | "America/Creston"
        | "America/Dawson_Creek"
        | "America/Fort_Nelson"
        | "America/Whitehorse"
        | "America/Dawson"
        | "America/Vancouver"
        | "America/Mexico_City"
        | "America/Cancun"
        | "America/Merida"
        | "America/Monterrey"
        | "America/Matamoros"
        | "America/Mazatlan"
        | "America/Chihuahua"
        | "America/Ojinaga"
        | "America/Hermosillo"
        | "America/Tijuana"
        | "America/Bahia_Banderas"
        | "America/Belize"
        | "America/Costa_Rica"
        | "America/Guatemala"
        | "America/Tegucigalpa"
        | "America/Managua"
        | "America/Panama"
        | "America/El_Salvador"
        | "America/Barbados"
        | "America/Nassau"
        | "America/Havana"
        | "America/Curacao"
        | "America/Santo_Domingo"
        | "America/Port-au-Prince"
        | "America/Jamaica"
        | "America/Martinique"
        | "America/Puerto_Rico"
        | "America/Grand_Turk"
        | "America/Port_of_Spain"
        | "America/Miquelon"
        | "America/Argentina/Buenos_Aires"
        | "America/Argentina/Cordoba"
        | "America/Argentina/Salta"
        | "America/Argentina/Jujuy"
        | "America/Argentina/Tucuman"
        | "America/Argentina/Catamarca"
        | "America/Argentina/La_Rioja"
        | "America/Argentina/San_Juan"
        | "America/Argentina/Mendoza"
        | "America/Argentina/San_Luis"
        | "America/Argentina/Rio_Gallegos"
        | "America/Argentina/Ushuaia"
        | "America/La_Paz"
        | "America/Noronha"
        | "America/Belem"
        | "America/Fortaleza"
        | "America/Recife"
        | "America/Araguaina"
        | "America/Maceio"
        | "America/Bahia"
        | "America/Sao_Paulo"
        | "America/Campo_Grande"
        | "America/Cuiaba"
        | "America/Santarem"
        | "America/Porto_Velho"
        | "America/Boa_Vista"
        | "America/Manaus"
        | "America/Eirunepe"
        | "America/Rio_Branco"
        | "America/Santiago"
        | "America/Punta_Arenas"
        | "America/Bogota"
        | "America/Guayaquil"
        | "America/Cayenne"
        | "America/Guyana"
        | "America/Lima"
        | "America/Asuncion"
        | "America/Paramaribo"
        | "America/Montevideo"
        | "America/Caracas"
        | "Africa/Abidjan"
        | "Africa/Algiers"
        | "Africa/Cairo"
        | "Africa/El_Aaiun"
        | "Africa/Ceuta"
        | "Africa/Accra"
        | "Africa/Bissau"
        | "Africa/Nairobi"
        | "Africa/Monrovia"
        | "Africa/Tripoli"
        | "Africa/Casablanca"
        | "Africa/Maputo"
        | "Africa/Windhoek"
        | "Africa/Lagos"
        | "Africa/Khartoum"
        | "Africa/Juba"
        | "Africa/Sao_Tome"
        | "Africa/Ndjamena"
        | "Africa/Tunis"
        | "Africa/Johannesburg"
        | "Pacific/Pago_Pago"
        | "Pacific/Rarotonga"
        | "Pacific/Easter"
        | "Pacific/Galapagos"
        | "Pacific/Fiji"
        | "Pacific/Chuuk"
        | "Pacific/Pohnpei"
        | "Pacific/Kosrae"
        | "Pacific/Guam"
        | "Pacific/Tarawa"
        | "Pacific/Enderbury"
        | "Pacific/Kiritimati"
        | "Pacific/Majuro"
        | "Pacific/Kwajalein"
        | "Pacific/Noumea"
        | "Pacific/Norfolk"
        | "Pacific/Nauru"
        | "Pacific/Niue"
        | "Pacific/Auckland"
        | "Pacific/Chatham"
        | "Pacific/Tahiti"
        | "Pacific/Marquesas"
        | "Pacific/Gambier"
        | "Pacific/Port_Moresby"
        | "Pacific/Bougainville"
        | "Pacific/Pitcairn"
        | "Pacific/Palau"
        | "Pacific/Guadalcanal"
        | "Pacific/Fakaofo"
        | "Pacific/Tongatapu"
        | "Pacific/Funafuti"
        | "Pacific/Wake"
        | "Pacific/Honolulu"
        | "Pacific/Efate"
        | "Pacific/Wallis"
        | "Pacific/Apia"
        | "Australia/Lord_Howe"
        | "Australia/Hobart"
        | "Australia/Melbourne"
        | "Australia/Sydney"
        | "Australia/Broken_Hill"
        | "Australia/Brisbane"
        | "Australia/Lindeman"
        | "Australia/Adelaide"
        | "Australia/Darwin"
        | "Australia/Perth"
        | "Australia/Eucla"
        | "Antarctica/Casey"
        | "Antarctica/Davis"
        | "Antarctica/DumontDUrville"
        | "Antarctica/Mawson"
        | "Antarctica/Palmer"
        | "Antarctica/Rothera"
        | "Antarctica/Syowa"
        | "Antarctica/Troll"
        | "Antarctica/Vostok"
        | "Antarctica/Macquarie"
        | "Atlantic/Bermuda"
        | "Atlantic/Cape_Verde"
        | "Atlantic/Canary"
        | "Atlantic/Faroe"
        | "Atlantic/Stanley"
        | "Atlantic/South_Georgia"
        | "Atlantic/Reykjavik"
        | "Atlantic/Madeira"
        | "Atlantic/Azores"
        | "Indian/Cocos"
        | "Indian/Christmas"
        | "Indian/Chagos"
        | "Indian/Mauritius"
        | "Indian/Maldives"
        | "Indian/Mahe"
        | "Indian/Kerguelen"
        | "Indian/Reunion"
        | "America/Nuuk"
        | "America/Danmarkshavn"
        | "America/Scoresbysund"
        | "America/Thule";
      user_specials_reactions_type: "clap";
      venue_page_show_view_type: "list" | "grid";
      venue_pages_carousel_item_type: "show" | "image" | "video";
      venue_pages_carousel_type: "default" | "custom";
      venue_pages_nav_item_type: "base" | "custom" | "external" | "group";
      venue_pages_shows_grouping_type: "individual" | "grouped";
    };
    CompositeTypes: {
      contact_import_stats: {
        contacts_inserted: number | null;
        contacts_updated: number | null;
        contacts_linked: number | null;
        contacts_skipped: number | null;
        processing_time_ms: number | null;
      };
      geometry_dump: {
        path: number[] | null;
        geom: unknown;
      };
      valid_detail: {
        valid: boolean | null;
        reason: string | null;
        location: unknown;
      };
    };
  };
};

type DatabaseWithoutInternals = Omit<Database, "__InternalSupabase">;

type DefaultSchema = DatabaseWithoutInternals[Extract<
  keyof Database,
  "public"
>];

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema["Tables"] & DefaultSchema["Views"])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends (DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])
    : never) = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Views"])[TableName] extends {
      Row: infer R;
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema["Tables"] &
        DefaultSchema["Views"])
    ? (DefaultSchema["Tables"] &
        DefaultSchema["Views"])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R;
      }
      ? R
      : never
    : never;

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends (DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never) = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Insert: infer I;
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I;
      }
      ? I
      : never
    : never;

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema["Tables"]
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends (DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"]
    : never) = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions["schema"]]["Tables"][TableName] extends {
      Update: infer U;
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema["Tables"]
    ? DefaultSchema["Tables"][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U;
      }
      ? U
      : never
    : never;

export type Enums<
  DefaultSchemaEnumNameOrOptions extends
    | keyof DefaultSchema["Enums"]
    | { schema: keyof DatabaseWithoutInternals },
  EnumName extends (DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"]
    : never) = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions["schema"]]["Enums"][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema["Enums"]
    ? DefaultSchema["Enums"][DefaultSchemaEnumNameOrOptions]
    : never;

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema["CompositeTypes"]
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends (PublicCompositeTypeNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals;
  }
    ? keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"]
    : never) = never,
> = PublicCompositeTypeNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals;
}
  ? DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions["schema"]]["CompositeTypes"][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema["CompositeTypes"]
    ? DefaultSchema["CompositeTypes"][PublicCompositeTypeNameOrOptions]
    : never;

export const Constants = {
  public: {
    Enums: {
      ad_campaign_type: ["feed"],
      batchname: ["7am", "11am", "8am", "9am", "10am", "12pm", "1pm", "2pm"],
      collection_kind: ["collection", "podcast"],
      contact_lists_status: ["created", "uploading", "processing", "failed"],
      content_tag_status: ["pending", "accepted", "rejected"],
      credit_pool_type: ["free", "purchased", "automations"],
      credit_reference_type: ["Southpaw", "Stripe", "Cron"],
      credit_transaction_type: [
        "free_monthly_deposit",
        "one_time_purchase",
        "subscription_renewal",
        "message_send_debit",
        "cancellation_refund",
        "admin_adjustment",
        "subscription_canceled",
        "subscription_upgrade",
        "subscription_downgrade",
      ],
      email_frequency: ["weekly", "biweekly", "monthly", "disabled"],
      gift_card_status: ["active", "redeemed", "expired", "disabled"],
      gift_card_transaction_type: [
        "purchase",
        "redemption",
        "refund",
        "adjustment",
      ],
      livestream_state: ["not_started", "waiting", "live", "ended"],
      livestream_vendor: ["mux"],
      moderation_level: ["off", "low", "medium", "high"],
      newsletter_issue_status: [
        "draft",
        "scheduled",
        "dispatching",
        "dispatched",
        "sent",
        "failed",
        "canceled",
      ],
      notification_preference: ["sms-only", "email-only", "none", "all"],
      notification_type: [
        "weekly_email",
        "new_special",
        "new_comedian",
        "new_tour_date",
      ],
      page_types: [
        "comedian",
        "venue_page",
        "musician",
        "podcast",
        "film",
        "production_company",
        "journalist",
        "magician",
        "medium",
      ],
      payment_provider: ["stripe", "apple-iap"],
      payment_status: [
        "incomplete",
        "success",
        "processing",
        "requires_action",
        "refunded",
        "failed",
      ],
      product_category: ["video", "subscription", "collection"],
      push_notification_job: ["pending", "processing", "processed", "failed"],
      show_creator_entity_type: ["comedian", "venue_page", "internal"],
      social_media_type: [
        "Twitter",
        "Instagram",
        "Facebook",
        "Youtube",
        "TikTok",
        "LinkedIn",
        "Podcast",
        "Custom",
      ],
      subscription_status: [
        "incomplete",
        "active",
        "canceled",
        "past_due",
        "unpaid",
      ],
      ticket_flags: ["LOW_TICKETS"],
      timezone: [
        "America/New_York",
        "America/Los_Angeles",
        "America/Chicago",
        "America/Denver",
        "Europe/Andorra",
        "Europe/Vienna",
        "Europe/Brussels",
        "Europe/Sofia",
        "Europe/Minsk",
        "Europe/Zurich",
        "Europe/Prague",
        "Europe/Berlin",
        "Europe/Copenhagen",
        "Europe/Tallinn",
        "Europe/Madrid",
        "Europe/Helsinki",
        "Europe/Paris",
        "Europe/London",
        "Europe/Gibraltar",
        "Europe/Athens",
        "Europe/Budapest",
        "Europe/Dublin",
        "Europe/Rome",
        "Europe/Vilnius",
        "Europe/Luxembourg",
        "Europe/Riga",
        "Europe/Monaco",
        "Europe/Chisinau",
        "Europe/Malta",
        "Europe/Amsterdam",
        "Europe/Oslo",
        "Europe/Warsaw",
        "Europe/Lisbon",
        "Europe/Bucharest",
        "Europe/Belgrade",
        "Europe/Kaliningrad",
        "Europe/Moscow",
        "Europe/Simferopol",
        "Europe/Kirov",
        "Europe/Volgograd",
        "Europe/Astrakhan",
        "Europe/Saratov",
        "Europe/Ulyanovsk",
        "Europe/Samara",
        "Europe/Stockholm",
        "Europe/Istanbul",
        "Europe/Kiev",
        "Europe/Uzhgorod",
        "Europe/Zaporozhye",
        "Europe/Tirane",
        "Asia/Dubai",
        "Asia/Kabul",
        "Asia/Yerevan",
        "Asia/Baku",
        "Asia/Dhaka",
        "Asia/Brunei",
        "Asia/Thimphu",
        "Asia/Shanghai",
        "Asia/Urumqi",
        "Asia/Nicosia",
        "Asia/Famagusta",
        "Asia/Tbilisi",
        "Asia/Hong_Kong",
        "Asia/Jakarta",
        "Asia/Pontianak",
        "Asia/Makassar",
        "Asia/Jayapura",
        "Asia/Jerusalem",
        "Asia/Kolkata",
        "Asia/Baghdad",
        "Asia/Tehran",
        "Asia/Amman",
        "Asia/Tokyo",
        "Asia/Bishkek",
        "Asia/Pyongyang",
        "Asia/Seoul",
        "Asia/Almaty",
        "Asia/Qyzylorda",
        "Asia/Qostanay",
        "Asia/Aqtobe",
        "Asia/Aqtau",
        "Asia/Atyrau",
        "Asia/Oral",
        "Asia/Beirut",
        "Asia/Colombo",
        "Asia/Yangon",
        "Asia/Ulaanbaatar",
        "Asia/Hovd",
        "Asia/Choibalsan",
        "Asia/Macau",
        "Asia/Kuala_Lumpur",
        "Asia/Kuching",
        "Asia/Kathmandu",
        "Asia/Manila",
        "Asia/Karachi",
        "Asia/Gaza",
        "Asia/Hebron",
        "Asia/Qatar",
        "Asia/Yekaterinburg",
        "Asia/Omsk",
        "Asia/Novosibirsk",
        "Asia/Barnaul",
        "Asia/Tomsk",
        "Asia/Novokuznetsk",
        "Asia/Krasnoyarsk",
        "Asia/Irkutsk",
        "Asia/Chita",
        "Asia/Yakutsk",
        "Asia/Khandyga",
        "Asia/Vladivostok",
        "Asia/Ust-Nera",
        "Asia/Magadan",
        "Asia/Sakhalin",
        "Asia/Srednekolymsk",
        "Asia/Kamchatka",
        "Asia/Anadyr",
        "Asia/Riyadh",
        "Asia/Singapore",
        "Asia/Bangkok",
        "Asia/Dushanbe",
        "Asia/Dili",
        "Asia/Ashgabat",
        "Asia/Taipei",
        "Asia/Samarkand",
        "Asia/Tashkent",
        "Asia/Ho_Chi_Minh",
        "America/Detroit",
        "America/Kentucky/Louisville",
        "America/Kentucky/Monticello",
        "America/Indiana/Indianapolis",
        "America/Indiana/Vincennes",
        "America/Indiana/Winamac",
        "America/Indiana/Marengo",
        "America/Indiana/Petersburg",
        "America/Indiana/Vevay",
        "America/Indiana/Tell_City",
        "America/Indiana/Knox",
        "America/Menominee",
        "America/North_Dakota/Center",
        "America/North_Dakota/New_Salem",
        "America/North_Dakota/Beulah",
        "America/Boise",
        "America/Phoenix",
        "America/Anchorage",
        "America/Juneau",
        "America/Sitka",
        "America/Metlakatla",
        "America/Yakutat",
        "America/Nome",
        "America/Adak",
        "America/St_Johns",
        "America/Halifax",
        "America/Glace_Bay",
        "America/Moncton",
        "America/Goose_Bay",
        "America/Blanc-Sablon",
        "America/Toronto",
        "America/Nipigon",
        "America/Thunder_Bay",
        "America/Iqaluit",
        "America/Pangnirtung",
        "America/Atikokan",
        "America/Winnipeg",
        "America/Rainy_River",
        "America/Resolute",
        "America/Rankin_Inlet",
        "America/Regina",
        "America/Swift_Current",
        "America/Edmonton",
        "America/Cambridge_Bay",
        "America/Yellowknife",
        "America/Inuvik",
        "America/Creston",
        "America/Dawson_Creek",
        "America/Fort_Nelson",
        "America/Whitehorse",
        "America/Dawson",
        "America/Vancouver",
        "America/Mexico_City",
        "America/Cancun",
        "America/Merida",
        "America/Monterrey",
        "America/Matamoros",
        "America/Mazatlan",
        "America/Chihuahua",
        "America/Ojinaga",
        "America/Hermosillo",
        "America/Tijuana",
        "America/Bahia_Banderas",
        "America/Belize",
        "America/Costa_Rica",
        "America/Guatemala",
        "America/Tegucigalpa",
        "America/Managua",
        "America/Panama",
        "America/El_Salvador",
        "America/Barbados",
        "America/Nassau",
        "America/Havana",
        "America/Curacao",
        "America/Santo_Domingo",
        "America/Port-au-Prince",
        "America/Jamaica",
        "America/Martinique",
        "America/Puerto_Rico",
        "America/Grand_Turk",
        "America/Port_of_Spain",
        "America/Miquelon",
        "America/Argentina/Buenos_Aires",
        "America/Argentina/Cordoba",
        "America/Argentina/Salta",
        "America/Argentina/Jujuy",
        "America/Argentina/Tucuman",
        "America/Argentina/Catamarca",
        "America/Argentina/La_Rioja",
        "America/Argentina/San_Juan",
        "America/Argentina/Mendoza",
        "America/Argentina/San_Luis",
        "America/Argentina/Rio_Gallegos",
        "America/Argentina/Ushuaia",
        "America/La_Paz",
        "America/Noronha",
        "America/Belem",
        "America/Fortaleza",
        "America/Recife",
        "America/Araguaina",
        "America/Maceio",
        "America/Bahia",
        "America/Sao_Paulo",
        "America/Campo_Grande",
        "America/Cuiaba",
        "America/Santarem",
        "America/Porto_Velho",
        "America/Boa_Vista",
        "America/Manaus",
        "America/Eirunepe",
        "America/Rio_Branco",
        "America/Santiago",
        "America/Punta_Arenas",
        "America/Bogota",
        "America/Guayaquil",
        "America/Cayenne",
        "America/Guyana",
        "America/Lima",
        "America/Asuncion",
        "America/Paramaribo",
        "America/Montevideo",
        "America/Caracas",
        "Africa/Abidjan",
        "Africa/Algiers",
        "Africa/Cairo",
        "Africa/El_Aaiun",
        "Africa/Ceuta",
        "Africa/Accra",
        "Africa/Bissau",
        "Africa/Nairobi",
        "Africa/Monrovia",
        "Africa/Tripoli",
        "Africa/Casablanca",
        "Africa/Maputo",
        "Africa/Windhoek",
        "Africa/Lagos",
        "Africa/Khartoum",
        "Africa/Juba",
        "Africa/Sao_Tome",
        "Africa/Ndjamena",
        "Africa/Tunis",
        "Africa/Johannesburg",
        "Pacific/Pago_Pago",
        "Pacific/Rarotonga",
        "Pacific/Easter",
        "Pacific/Galapagos",
        "Pacific/Fiji",
        "Pacific/Chuuk",
        "Pacific/Pohnpei",
        "Pacific/Kosrae",
        "Pacific/Guam",
        "Pacific/Tarawa",
        "Pacific/Enderbury",
        "Pacific/Kiritimati",
        "Pacific/Majuro",
        "Pacific/Kwajalein",
        "Pacific/Noumea",
        "Pacific/Norfolk",
        "Pacific/Nauru",
        "Pacific/Niue",
        "Pacific/Auckland",
        "Pacific/Chatham",
        "Pacific/Tahiti",
        "Pacific/Marquesas",
        "Pacific/Gambier",
        "Pacific/Port_Moresby",
        "Pacific/Bougainville",
        "Pacific/Pitcairn",
        "Pacific/Palau",
        "Pacific/Guadalcanal",
        "Pacific/Fakaofo",
        "Pacific/Tongatapu",
        "Pacific/Funafuti",
        "Pacific/Wake",
        "Pacific/Honolulu",
        "Pacific/Efate",
        "Pacific/Wallis",
        "Pacific/Apia",
        "Australia/Lord_Howe",
        "Australia/Hobart",
        "Australia/Melbourne",
        "Australia/Sydney",
        "Australia/Broken_Hill",
        "Australia/Brisbane",
        "Australia/Lindeman",
        "Australia/Adelaide",
        "Australia/Darwin",
        "Australia/Perth",
        "Australia/Eucla",
        "Antarctica/Casey",
        "Antarctica/Davis",
        "Antarctica/DumontDUrville",
        "Antarctica/Mawson",
        "Antarctica/Palmer",
        "Antarctica/Rothera",
        "Antarctica/Syowa",
        "Antarctica/Troll",
        "Antarctica/Vostok",
        "Antarctica/Macquarie",
        "Atlantic/Bermuda",
        "Atlantic/Cape_Verde",
        "Atlantic/Canary",
        "Atlantic/Faroe",
        "Atlantic/Stanley",
        "Atlantic/South_Georgia",
        "Atlantic/Reykjavik",
        "Atlantic/Madeira",
        "Atlantic/Azores",
        "Indian/Cocos",
        "Indian/Christmas",
        "Indian/Chagos",
        "Indian/Mauritius",
        "Indian/Maldives",
        "Indian/Mahe",
        "Indian/Kerguelen",
        "Indian/Reunion",
        "America/Nuuk",
        "America/Danmarkshavn",
        "America/Scoresbysund",
        "America/Thule",
      ],
      user_specials_reactions_type: ["clap"],
      venue_page_show_view_type: ["list", "grid"],
      venue_pages_carousel_item_type: ["show", "image", "video"],
      venue_pages_carousel_type: ["default", "custom"],
      venue_pages_nav_item_type: ["base", "custom", "external", "group"],
      venue_pages_shows_grouping_type: ["individual", "grouped"],
    },
  },
} as const;
